import dayjs from 'dayjs';
import autoTable from 'jspdf-autotable';
import ReportCoreMixin from '@shared/mixin/report/report.core.mixin.js';
import {benchmark_score, business_site_category, code_level_thresholds, code_localized, code_advice, audit_categories, answer_localized, answers_by_question, question_subjects, core_risk_indicators, core_indicator_campaign_exclusions, code_descriptions, summary_composition_desc} from '@shared/mixin/report/raw-data/hyundai_v2_2026';

const HYUNDAILogoPNG = require('@shared/assets/img/hyundai_logo.png');
const HYUNDAILogoBluePNG = require('@shared/assets/img/hyundai_logo_blue.png');
const QESGLogoWhitePNG = require('@shared/assets/img/QuantifiedESG_Logo_v3_White.png');

// 벌크 처리 성능 진단용 [PERF] 로그 — prd 에서는 숨김.
function perfLog(...args) {
    if (process.env.VUE_APP_BUILD_ENV !== 'production') console.log(...args);
}

// ────────────────────────────────────────────────────────────────
// 전년도(2025) 비교용 매핑.
//   - 2026 hyundai v2 family (550-558) ↔ 2025 hyundai 사업장 family (522-530)
//   - 매핑이 없는 캠페인(예: 550 사업본부 종합) 은 전년도 비교를 표시하지 않음.
//   - 신규 회사 (2026 부터 추가) 는 매핑이 있더라도 전년도 데이터가 없으므로 fetch skip.
// 분류 기준이 2026 에서 바뀌어 전년도 충족률은 단순 참고용.
// ────────────────────────────────────────────────────────────────
const PRIOR_YEAR_CAMPAIGN_MAP = {
    551: 523, 552: 524, 553: 525, 554: 526,
    555: 527, 556: 528, 557: 529, 558: 530
};
const HYUNDAI_V2_NEW_COMPANY_IDS = new Set([32805, 32806, 32807, 32808]);
const PRIOR_YEAR_LABEL = 2025;
const PRIOR_YEAR_BAR_COLOR_HEX = '#c49163';
const PRIOR_YEAR_BAR_COLOR_RGB = [196, 145, 99];
// Summary 페이지(표지) 전년도 텍스트/마커 — 차분한 진회색 (radar/코드 카드의 #c49163 와 별개).
const PRIOR_YEAR_SUMMARY_TEXT_HEX = '#595959';
const PRIOR_YEAR_SUMMARY_TEXT_RGB = [89, 89, 89];

// 2026↔2025 분류 체계 변경 — prefix 기반 매핑.
//   2025 응답의 category{1,2,3}_question_code 는 'qXXXXXX' 내부 id 라 cross-year 매칭 불가.
//   API 응답 category{1,2,3}_question_name 의 prefix("A-1-1." 등 ". " 앞 부분) 로 정렬.
//   매핑 없는 항목 (2026 신규: A-1-5 사내도급 / B-2-1) → prior 미표시.
const PRIOR_YEAR_CODE_MAP = {
    category1: {
        'A': '1',  // 인권 (& 노동)
        'B': '2'   // 윤리
    },
    category2: {
        'A-1': '1.2',  // 인권경영 시스템
        'A-2': '1.3',  // 인권 리스크 관리
        'A-3': '1.4',  // 근로환경 개선
        'A-4': '1.5',  // 공급망 인권/노동 관리
        'A-5': '1.6',  // 생활환경 인권 관리
        'B-1': '2.1'   // 윤리 경영 시스템
    },
    category3: {
        'A-1-1': '1.2.1', 'A-1-2': '1.2.2', 'A-1-3': '1.2.3', 'A-1-4': '1.2.4',
        // A-1-5 (사내도급) 2026 신규 — 2025 매핑 없음
        'A-2-1': '1.3.1', 'A-2-2': '1.3.2', 'A-2-3': '1.3.3', 'A-2-4': '1.3.4',
        'A-3-1': '1.4.1', 'A-3-2': '1.4.2', 'A-3-3': '1.4.3', 'A-3-4': '1.4.4',
        'A-3-5': '1.4.6', // 1.4.5(결사) 가 2026 에서 제거되어 인덱스 shift
        'A-4-1': '1.5.1',
        'A-5-1': '1.6.1', 'A-5-2': '1.6.2',
        'B-1-1': '2.1.1', 'B-1-2': '2.1.2'
    }
};

// category name (예: "A-1-1. xxx" 또는 "1.2.1. xxx") 에서 prefix 추출.
//   ". " (dot + space) 앞 부분을 반환. 양식 안 맞으면 원본 그대로.
function _extractCategoryPrefix(name) {
    if (!name) return null;
    const _str = String(name);
    const _idx = _str.indexOf('. ');
    return _idx > 0 ? _str.slice(0, _idx) : _str;
}

// ── B-2 (그린워싱 리스크 관리) 영역 — 2026 hyundai-v2 결과보고서에서 전면 제외 ──
//   충족률 산정 + 모든 표시(레이더/코드카드/문항별 답변/desc 등)에서 제거.
//   식별: sub_code H2.11v2·H2.12v2, 또는 category2 "B-2." / category3 "B-2-".
const HYUNDAI_V2_EXCLUDED_SUB_CODES = new Set(['H2.11v2', 'H2.12v2']);
function _isExcludedB2Row(row) {
    if (!row) return false;
    if (row.sub_code && HYUNDAI_V2_EXCLUDED_SUB_CODES.has(row.sub_code)) return true;
    const _c2 = String(row.category2_question_name ?? '');
    const _c3 = String(row.category3_question_name ?? '');
    return /^B-2(?:[.\-\s]|$)/.test(_c2) || /^B-2-/.test(_c3);
}

export default {
    mixins: [ReportCoreMixin],
    data() {
        return {
            category2_score_standard: 50,
            hyundai_colors: {
                blue: '#002c5f',
                sand: '#e4dcd3',
                light_sand: '#f6f3f2',
                gold: '#a36b4f',
                teal: '#31859c',
                teal_light: '#9ec6d2',
                gray: '#b1b1b1',
                muted: '#969696',
            },
            hyundai_colors_rgb: {
                blue: [0, 44, 95],
                sand: [228, 220, 221],
                light_sand: [246, 243, 242],
                gold: [163, 107, 79],
                teal: [49, 133, 156],
                teal_light: [158, 198, 210],
                muted: [150, 150, 150]
            },
            hyundai_logo_white: {
                width: 235 / 4, height: 30 / 4
            },
            hyundai_logo_blue: {
                width: 33.8 * 2, height: 23.9 * 2
            },
            mapSubCodeToScoringPd: {},
            mapSubCodeToSubScoringPd: {},

            // 전년도(2025) 비교용 — generatePDF_hyundai_v2 진입 시 1회 로드.
            //   매핑 없거나 신규 회사거나 API 실패 → null 유지 (모든 prior year 마커 숨김).
            priorYearReportData: null,

            // hyundai-v2: sub_code → 답변 result(예/아니오/모름). loadAnswerResults() 가 채움.
            //   결과보고서 scoring 행엔 result 가 없어, 음성답변/증빙누락 시 선택 분기 정확 타겟용.
            answerResultBySubCode: {},

            workerPieChartInstance: null,
            subsCompositionPieChartInstance: null,
            imporanceCompositionPieChartInstance: null,
            category1AreaChartInstance: null,
            category1PieChartInstance: null,
            category1ContraryAreaChartInstance: null,
            category2ContraryAreaChartInstance: null,
            importance_composition_colors: [[69, 90, 100, 1], [96, 125, 139, 1], [144, 164, 174, 1],]
            // category1AreaChartInstance: null
        };
    }, computed: {
        // ── this.scoring 오버라이드 (core mixin 의 원본 대체) ──
        //   reportData.scoring 을 deep-clone 후 B-2(그린워싱) 영역을 전면 제거.
        //   행 단위 _pd 에서 B-2 행 제거 + 집계 _pd(category1 윤리/total/category0)에서 B-2 만큼 차감.
        //   이 한 곳만 거르면 하위(충족률/scoringInfo/코드페이지/문항별 답변/desc)가 전부 B-2 free.
        scoring() {
            const _src = this.reportData?.scoring;
            if (!_src) return _src;
            const _raw = JSON.parse(JSON.stringify(_src));

            // 차감용: 제거 대상 B-2 category2 집계 행 (행 필터 전에 확보)
            const _excludedC2 = Array.isArray(_raw.category2_scoring_pd)
                ? _raw.category2_scoring_pd.filter(_isExcludedB2Row)
                : [];

            // 행 단위 _pd: B-2 행 제거
            ['scoring_pd', 'sub_scoring_pd', 'category2_scoring_pd', 'category3_scoring_pd', 'category1_importance_pd']
                .forEach(_k => {
                    if (Array.isArray(_raw[_k])) _raw[_k] = _raw[_k].filter(_r => !_isExcludedB2Row(_r));
                });

            // 집계 _pd: 윤리 category1 / total / category0 에서 B-2 분 차감
            const _aggFields = ['문항 수', 'not_applicable_num', 'score', '배점', 'cap_score'];
            const _subAgg = (_target, _ex) => _aggFields.forEach(_f => {
                if (_target[_f] != null && _ex[_f] != null) _target[_f] = Number(_target[_f]) - Number(_ex[_f]);
            });
            _excludedC2.forEach(_ex => {
                (_raw.category1_scoring_pd ?? []).forEach(_r => {
                    if (_r.category1_question_code === _ex.category1_question_code) _subAgg(_r, _ex);
                });
                (_raw.total_scoring_pd ?? []).forEach(_r => _subAgg(_r, _ex));
                (_raw.category0_scoring_pd ?? []).forEach(_r => {
                    if (_r.category0_question_name === _ex.category0_question_name) _subAgg(_r, _ex);
                });
            });

            return _raw;
        },
        category1_composition_colors() {
            // 인권 — hyundai navy / 윤리 — teal (#31859c)
            return [this.hyundai_colors_rgb.blue, [49, 133, 156]];
        },
        hyundai_logo_png() {
            return HYUNDAILogoPNG;
        },
        hyundai_logo_blue_png() {
            return HYUNDAILogoBluePNG;
        },
        benchMarkScore() {
            // 0720: category3(Code) benchmark 가 캠페인 종료 후 실측값으로 교체됨
            //   (raw-data/hyundai_v2_2026.js ← 2026_CODE별_평균충족률.xlsx).
            //   0629 의 초안(is_draft) 0 표기는 '시드 난수를 확정 수치처럼 보이게 하지 않기'
            //   위한 임시 조치였고, 실측값이 들어온 지금은 근거가 사라져 제거했다.
            //   category2(레이더)는 대분류 평균 데이터 미수령이라 raw-data 에서 null 유지 →
            //   해당 라인만 값 없이 그려진다.
            return benchmark_score;
        },
        businessSiteCategory() {
            return business_site_category;
        },
        _jsPDF_headerStyles() {
            return {
                fontSize: 8.5, fillColor: [152, 74, 89], textColor: [255, 255, 255]
            };
        },
        _jsPDF_font_config_fontSize() {
            if (this.report_lang === 'en') {
                return {
                    cover_name_lg: 35,
                    cover_name: 30,
                    cover_company_name: 25,
                    title: 13.5, // 15,
                    title_sm: 12, // 13,
                    title_xs: 10.5, // 11,
                    table_th: 9.5, // 9,
                    table_td: 8.5, // 8,
                    footnote: 8, // 9,
                    default: 9, // 7,
                };
            } else {
                return {
                    cover_name_lg: 50,
                    cover_name: 30,
                    cover_company_name: 25,
                    title: 15,
                    title_sm: 13,
                    title_xs: 11,
                    table_th: 9,
                    table_td: 8,
                    footnote: 9,
                    default: 7
                };
            }
        },
        tableStyles() {
            return {
                global: {
                    valign: 'middle',
                    overflow: 'linebreak',
                    lineColor: [222, 226, 230],
                    lineWidth: 0.1,
                    minCellHeight: 16,
                }, th: {
                    // fontSize: this.report_lang === "ko" ? 11 : 10,
                    // fontSize: this.report_lang === "ko" ? 11 : 10.5,
                    fontSize: this.report_lang === 'ko' ? 9.5 : 9,
                    font: this.report_lang !== 'ca' ? 'NotoSansKR' : 'NotoSansHK',
                    cellPadding: {top: 4, bottom: 3, left: 3, right: 4},
                    fontStyle: 'medium',
                    fillColor: [246, 243, 242],
                    lineColor: [228, 220, 211],
                    textColor: '#292828', // "#424242"
                    valign: 'middle'
                }, td: {
                    font: this.report_lang !== 'ca' ? 'NotoSansKR' : 'NotoSansHK', // fontSize: this.report_lang === "ko" ? 9.5 : 9.0,
                    // fontSize: this.report_lang === "ko" ? 9.5 : 9,
                    fontSize: this.report_lang === 'ko' ? 8.5 : 8,
                    cellPadding: {top: 4, bottom: 3, left: 3, right: 4},
                    fontStyle: 'normal',
                    fillColor: '#FFFFFF', // [256, 256, 256],
                    // textColor: "#424242", // "#616161",
                    valign: 'middle'
                }
            };
        }, _jsPDF_card_config() {
            return {
                fillColor: [246, 243, 242], // '#F8F8F8',
                borderColor: '#e4dcd3', // '#E0E0E0',
                lineWidth: 0.5
            };
        },
        headerInfo() {
            return {
                request_id: this.reportData?.info?.id,
                company_id: this.reportData?.info?.company.id,
                company_name: this.reportData?.info?.company?.company_name,
                vendor_code: this.reportData?.info?.vendor_code,
                published_date: this.reportData?.info?.completed_ts
            };
        },
        project_code(){
            return this.reportData?.info?.project_code;
        },
        // ── hyundai-v2 전용: count 기반 충족률(%) ───────────────────────────────
        //   현대차 KPI 요구 (슬랙 C0ARKBXJ65R thread): 문항에서 점수 획득(score>0) 시 '충족',
        //   해당없음(NA) 문항은 모수에서 제외. 충족률 = 충족 문항 수 / 적용(NA 제외) 문항 수 × 100.
        //   영역(인권/윤리)별로 산정하고, 전체는 영역 합산(전체 분자/전체 분모).
        //   BE 의 환산_점수(획득점수/총점, '점수 기반')와 별개로, 결과보고서 출력에서만 이 값으로 대체.
        //
        //   [분모 = 적용 문항 수] BE 권위값 category{1,2,3}_scoring_pd['문항 수'] 사용.
        //     - 서버가 전체 템플릿 문항(고중요도 아닌 지표 포함) 기준 집계, NA 는 이미 제외.
        //     - category1_importance_pd 는 고중요도(HIGH EV) 서브셋이라 비고중요도 평가지표 1~N개가 빠짐
        //       (실측 HD_OV_PROD_26: importance 인권 44 vs 권위값 45). → 분모로 부적합.
        //     - sub_scoring_pd 직접 카운트도 미답변(cap_score 0)·NA 누락 위험 → 분모로 안 씀.
        //     - 기본(Basic) 영역은 cap_score=0 → 전체 분모에서 제외(평가지표 아님).
        //   [분자 = 점수 획득 문항 수] sub_scoring_pd 에서 score>0 문항 수 (전체 평가지표 포함, 신뢰 가능).
        //     - 미답변/NA 문항은 score 0 → 분자에 안 잡힘(정상).
        //   등급(codeLevelByCode/codeLevelCounts)은 raw score 기반이라 이 값과 무관하게 유지됨.
        //
        //   0721: total(전체) 을 hmc-theme "종합 점수" 패턴처럼 인권/윤리 단순평균((A+B)/2) 으로
        //   바꿀지 검토했으나, 평가팀 결정으로 **현행 pooled(전체 분자/분모 합산) 방식 유지** 확정.
        hyundaiComplianceRates() {
            const _subs = this.scoring?.sub_scoring_pd ?? [];
            const _c1pd = this.scoring?.category1_scoring_pd ?? [];
            const _c2pd = this.scoring?.category2_scoring_pd ?? [];
            const _c3pd = this.scoring?.category3_scoring_pd ?? [];

            // 분자: 점수 획득(score>0) 문항 수 — question_code 별 카운트 (sub_scoring_pd = 문항 단위).
            const _satC1 = {}, _satC2 = {}, _satC3 = {};
            const _bumpSat = (map, code) => { if (code) map[code] = (map[code] || 0) + 1; };
            for (const _q of _subs) {
                if (!((Number(_q.score) || 0) > 0)) continue;              // 점수 획득 문항만
                _bumpSat(_satC1, _q.category1_question_code);
                _bumpSat(_satC2, _q.category2_question_code);
                _bumpSat(_satC3, _q.category3_question_code);
            }

            // 분모: '문항 수' (적용 문항 수, NA 제외). category{1,2,3}_scoring_pd 권위값.
            const _denomOf = (row) => Number(row?.['문항 수']) || 0;
            const _isBasic = (row) => (Number(row?.cap_score) || 0) <= 0;  // 기본(Basic) 영역 식별
            const _mkDen = (pd, codeKey) => pd.reduce((m, r) => {
                const c = r?.[codeKey];
                if (c) m[c] = _denomOf(r);
                return m;
            }, {});
            const _denC2 = _mkDen(_c2pd, 'category2_question_code');
            const _denC3 = _mkDen(_c3pd, 'category3_question_code');

            // category1 + 전체(기본 제외 합산)
            const _denC1 = {};
            let _totSatisfied = 0, _totApplicable = 0;
            for (const r of _c1pd) {
                const c = r?.category1_question_code;
                if (!c) continue;
                _denC1[c] = _denomOf(r);
                if (_isBasic(r)) continue;                                 // 전체 분모/분자에서 기본 제외
                _totApplicable += _denomOf(r);
                _totSatisfied += (_satC1[c] || 0);
            }

            const _rate = (num, den) => den > 0
                ? this.roundToOneDecimalPlace((num / den) * 100)
                : null;                                                    // 적용 문항 0 → null → 호출부 fallback
            const _mapRates = (satMap, denMap) => Object.keys(denMap).reduce((acc, code) => {
                acc[code] = _rate(satMap[code] || 0, denMap[code]);
                return acc;
            }, {});

            return {
                total: _rate(_totSatisfied, _totApplicable),
                category1: _mapRates(_satC1, _denC1),
                category2: _mapRates(_satC2, _denC2),
                category3: _mapRates(_satC3, _denC3)
            };
        },
        category1_description() {
            return {
                'A. 인권': this.$t('인권 및 노동 영역 평가는 각 법인이 인권 및 노동 관련 리스크를 적절히 관리하고, 인권경영 시스템을 체계적으로 구축하였는지, 근로환경 개선을 위한 실질적인 활동을 수행하며, 공급망과 생활환경에 대한 관리가 적정하게 이루어지고 있는지를 종합적으로 확인합니다.', this.report_lang),
                'B. 윤리': this.$t('윤리 영역 평가는 각 법인이 윤리적 경영체계를 구축하여 부패, 뇌물 수수 등 비윤리적 행위에 대한 관리·감독을 적절히 수행하고 있는지, 아울러 그린워싱 리스크의 발생을 방지하기 위한 예방적 조치를 이행하고 있는지를 확인합니다.', this.report_lang),
            };
        },
        scoringInfo() {
            // hyundai-v2 KPI: 표시 충족률(%)을 count 기반(hyundaiComplianceRates)으로 주입.
            //   total/category1/category2/category3 의 '환산_점수' + category3 element score 를 대체.
            //   rate 가 null(적용 문항 0)인 경우만 기존 점수 기반 값으로 fallback.
            const _h2cr = this.hyundaiComplianceRates;
            let _total_score = this.scoring.total_scoring_pd[0];
            _total_score['환산_점수'] = _h2cr.total ?? this.roundToOneDecimalPlace(_total_score['환산_점수']);

            let _category1_score = [];
            if (this.scoring?.category1_scoring_pd) {
                _category1_score = this.orderCategory1(this.scoring?.category1_scoring_pd, 'category1_question_name').reduce((_category1_score, _category1) => {
                    const _question_name = this.report_lang === 'ko' ? _category1['category1_question_name'] : this.report_lang === 'en' ? _category1['category1_question_name_ea'] : _category1['category1_question_name_ca'];

                    _category1_score.push({
                        question_name: _question_name,
                        question_name_ko: _category1['category1_question_name'],
                        '문항_수': _category1['문항 수'] || 0,
                        '배점': _category1['배점'] || 0,
                        '배점_대비_점수_백분율': _category1['배점_대비_점수_백분율'] || 0,
                        '배점_합': _category1['배점_합'] || 0,
                        '가중치': this.roundToOneDecimalPlace(_category1['가중치'] || 0),
                        '접수_합': this.roundToOneDecimalPlace(_category1['score'] || 0),
                        '환산_점수': _h2cr.category1[_category1['category1_question_code']] ?? this.roundToOneDecimalPlace(_category1['환산_점수'] || 0)
                    });
                    return _category1_score;
                }, []);
            }

            let _category2_score = [];
            if (this.scoring?.category2_scoring_pd) {
                // v2 prefix "A-X." / v1 prefix "1.X." 양쪽 사전순 정렬
                this.scoring?.category2_scoring_pd.sort((a, b) => {
                    const _rank = (name) => {
                        const m = String(name || '').match(/^([AB])-(\d+)/);
                        if (m) return m[1].charCodeAt(0) * 100 + Number(m[2]);
                        const num = Number(String(name).substring(0, 3).split('.').join(''));
                        return Number.isFinite(num) ? num : Infinity;
                    };
                    return _rank(a.category2_question_name) - _rank(b.category2_question_name);
                });
                _category2_score = this.scoring?.category2_scoring_pd.reduce((_category2_score, _category2) => {
                    const _question_name = this.report_lang === 'ko' ? _category2['category2_question_name'] : this.report_lang === 'en' ? _category2['category2_question_name_ea'] : _category2['category2_question_name_ca'];

                    _category2_score.push({
                        question_name: _question_name,
                        question_name_ko: _category2['category2_question_name'],
                        '문항_수': _category2['문항 수'] || 0,
                        '배점': _category2['배점'] || 0,
                        '배점_대비_점수_백분율': _category2['배점_대비_점수_백분율'] || 0,
                        '배점_합': _category2['배점_합'] || 0,
                        '가중치': this.roundToOneDecimalPlace(_category2['가중치'] || 0),
                        '점수_합': this.roundToOneDecimalPlace(_category2['score'] || 0),
                        '환산_점수': _h2cr.category2[_category2['category2_question_code']] ?? this.roundToOneDecimalPlace(_category2['환산_점수'] || 0)
                    });
                    return _category2_score;
                }, []);
            }

            let _category3_score = [];
            if (this.scoring?.category3_scoring_pd) {
                _category3_score = this.scoring?.category3_scoring_pd.reduce((_category3_score, _category3) => {
                    const _category3_sub_scoring = this.scoring.sub_scoring_pd.filter((_category3_sub) => _category3_sub.category3_question_code === _category3.category3_question_code);

                    let _is_category3_na = false;
                    if (_category3_sub_scoring.length) {
                        _is_category3_na = _category3_sub_scoring.every(({not_applicable}) => not_applicable === true);
                    }

                    if (!_category3_score[_category3.category1_question_name]) {
                        const _category3_category1_score = this.scoring?.category1_scoring_pd.find(({category1_question_name}) => category1_question_name === _category3.category1_question_name);
                        // let { category1_question_code, category1_question_name, category1_question_name_ca, category1_question_name_ea, not_applicable_num, 환산_점수 } = _category3
                        let {
                            category1_question_code,
                            category1_question_name,
                            category1_question_name_ca,
                            category1_question_name_ea
                        } = _category3;
                        환산_점수 = _is_category3_na ? null : this.roundToOneDecimalPlace(환산_점수 || 0);
                        
                        // category1 영역 충족률도 count 기반으로 (도넛/세부 영역별 페이지 일치).
                        const _c1_rate_kpi = _h2cr.category1[category1_question_code] ?? this.roundToOneDecimalPlace(_category3_category1_score['환산_점수']);
                        _category3_score[_category3.category1_question_name] = {
                            category1_question_code,
                            category1_question_name_ko: category1_question_name,
                            category1_question_name: this.report_lang === 'ko' ? category1_question_name : this.report_lang === 'ca' ? category1_question_name_ca : category1_question_name_ea,
                            category1_score: _c1_rate_kpi,
                            min: _c1_rate_kpi,
                            max: null,
                            category3: []
                        };
                    }
                    
                    let {
                        category3_question_code,
                        category3_question_name,
                        category3_question_name_ca,
                        category3_question_name_ea,
                        환산_점수
                    } = _category3;
                    
                    if (!_is_category3_na) {
                        환산_점수 = this.roundToOneDecimalPlace(_h2cr.category3[category3_question_code] ?? 환산_점수);
                        if (환산_점수 > 0) {
                            _category3_score[_category3.category1_question_name]['max'] = Math.max(_category3_score[_category3.category1_question_name]['max'], 환산_점수);
                        }
                        // _category3_score[_category3.category1_question_name]["max"] = Math.max(_category3_score[_category3.category1_question_name]["max"], 환산_점수)
                        _category3_score[_category3.category1_question_name]['min'] = Math.min(_category3_score[_category3.category1_question_name]['min'], 환산_점수);
                    }
                    
                    _category3_score[_category3.category1_question_name].category3.push({
                        category3_question_code,
                        category3_question_name_kr: category3_question_name,
                        category3_question_name: this.report_lang === 'ko' ? category3_question_name : this.report_lang === 'ca' ? category3_question_name_ca : category3_question_name_ea,
                        score: _is_category3_na ? null : this.roundToOneDecimalPlace(환산_점수 || 0),
                        not_applicable: _is_category3_na
                    });
                    return _category3_score;
                }, {});
                
                // v2 format "A-1-1. xxx" / v1 format "1.2.1. xxx" 둘 다 지원
                const parseKey = str => {
                    const m = String(str).match(/^([A-Z])-(\d+)-(\d+)/);
                    if (m) return m[1].charCodeAt(0) * 1000000 + Number(m[2]) * 1000 + Number(m[3]);
                    const parts = String(str).split('.');
                    return (Number(parts[0]) || 0) * 10000 + (Number(parts[1]) || 0) * 100 + (Number(parts[2]) || 0);
                };
                
                Object.values(_category3_score).forEach((_category3_group) => {
                    _category3_group.min = _category3_group.max === _category3_group.min ? null : _category3_group.min;
                    _category3_group.category3.forEach(category3_ele => {
                        category3_ele.max = category3_ele.score === _category3_group.max;
                        category3_ele.min = category3_ele.score === _category3_group.min;
                        category3_ele.below = category3_ele.score < this.category2_score_standard;
                    });
                    _category3_group.category3 = _category3_group.category3.sort((a, b) => parseKey(a.category3_question_name) - parseKey(b.category3_question_name));
                    
                    _category3_group.min = _category3_group.min === null ? ' - ' : _category3_group.min;
                });
            }
            
            const _category3 = this.orderCategory1(Object.values(_category3_score), 'category1_question_name_ko');
            return {
                total: _total_score, category1: _category1_score, category2: _category2_score, category3: _category3
            };
        },
        // ────────────────────────────────────────────────────────────────
        // 전년도(2025) 데이터 — 분류 기준이 바뀌어 sub-level 까지는 비교 불가.
        //   total / category1 / category2 / category3 단위 충족률만 변환해서
        //   표지 진행바, 영역별 도넛, radar, 코드 카드 4-bar stack 에 단순 참고용 표시.
        //   priorYearReportData 가 null 이면 (신규 회사 / 매핑 없음 / API 실패) 비교 안 함.
        // ────────────────────────────────────────────────────────────────
        hasPriorYear() {
            return !!this.priorYearReportData && !!this.priorYearReportData?.scoring;
        },
        priorYearScoringInfo() {
            if (!this.hasPriorYear) return null;
            const _prior = this.priorYearReportData.scoring;

            const _total_score = _prior?.total_scoring_pd?.[0] || null;
            const _total = _total_score
                ? { ..._total_score, '환산_점수': this.roundToOneDecimalPlace(_total_score['환산_점수']) }
                : null;

            // category{1,2,3} 모두 question_name 의 prefix (예: "1.2.1") 로 인덱싱.
            //   2025 응답의 question_code(qXXXXX 내부 id) 는 cross-year 매칭 불가능.
            //   2026 caller 는 PRIOR_YEAR_CODE_MAP 으로 변환된 2025 prefix 를 쓴다.
            const _buildPrefixIndex = (rows, nameKey) => {
                const _idx = {};
                if (!rows) return _idx;
                rows.forEach(r => {
                    const _prefix = _extractCategoryPrefix(r?.[nameKey]);
                    if (_prefix) {
                        _idx[_prefix] = this.roundToOneDecimalPlace(r['환산_점수'] || 0);
                    }
                });
                return _idx;
            };

            return {
                total: _total,
                category1ScoreByPrefix: _buildPrefixIndex(_prior?.category1_scoring_pd, 'category1_question_name'),
                category2ScoreByPrefix: _buildPrefixIndex(_prior?.category2_scoring_pd, 'category2_question_name'),
                category3ScoreByPrefix: _buildPrefixIndex(_prior?.category3_scoring_pd, 'category3_question_name')
            };
        },
        evaluation_score_description() {
            return {
                'ko': `${this.companyInfo['회사명']}의 ${this.cover_title.join(' ')} 충족률은 ${this.roundToOneDecimalPlace(this.scoringInfo.total['환산_점수'])} % 입니다.`,
                'en': `The overall compliance of the ${this.cover_title.join(' ')} for ${this.companyInfo['회사명']} is ${this.roundToOneDecimalPlace(this.scoringInfo.total['환산_점수'])} %.`,
                'ca': `${this.companyInfo['회사명']}在 ${this.cover_title.join(' ')}满意率 ${this.roundToOneDecimalPlace(this.scoringInfo.total['환산_점수'])} %。`
            };
        },
        companyInfo() {
            // 콤마 포함 문자열 → 숫자 (e.g. "4,345" → 4345). 실패 시 0.
            const _toNum = (v) => {
                if (v == null || v === ' - ') return 0;
                const n = Number(String(v).replace(/,/g, ''));
                return Number.isFinite(n) ? n : 0;
            };

            const info_company = this.reportData?.info?.company ?? {};
            const _result = {
                '회사명': info_company.company_name ?? ' - ',
                '사업장 유형': info_company.hd_corporate ?? ' - ',
                '근로자_정규직': ' - ',
                '근로자_계약직': ' - ',
                '근로자_계약직_파견': ' - ',
                '근로자수': ' - '
            };

            const _values = this.reportData?.parser?.values ?? [];
            if (!_values.length) return _result;

            const _worker_fields = ['근로자_정규직', '근로자_계약직', '근로자_계약직_파견'];
            _values.forEach(_v => {
                if (_worker_fields.includes(_v.criteria_name)) {
                    _result[_v.criteria_name] = (!_v.value || !_v.value.length) ? '(없음)' : _v.value[0];
                }
            });

            // 근로자수 합산 (콤마 strip 후 숫자 변환) + 콤마 포맷
            const _total = _toNum(_result['근로자_정규직'])
                + _toNum(_result['근로자_계약직'])
                + _toNum(_result['근로자_계약직_파견']);
            _result['근로자수'] = _total > 0 ? _total.toLocaleString() : ' - ';

            return _result;
        },
        assessmentInfo() {
            let {
                service_type, requested_ts, in_progress_ts, in_assessment_ts, duedate_ts, completed_ts
            } = this.reportData.info;
            service_type = service_type === '일반' ? this.$t('일반 평가', this.report_lang) : this.$t('심화 평가', this.report_lang);
            
            let _assessmentInfo = {
                service_type: null, requested_date: requested_ts, duedate_date: duedate_ts, completed_date: completed_ts
            };
            
            if (service_type) _assessmentInfo.service_type = this.$t(service_type, this.report_lang);
            
            // 평가기간 실 평가기간으로 교체
            if (this.set_actual_duration) {
                _assessmentInfo.requested_date = this.report_lang === 'ko' ? dayjs(in_progress_ts).format('YYYY년 MM월 DD일') : dayjs(in_progress_ts).format('MM/DD/YYYY');
                
                _assessmentInfo.duedate_date = this.report_lang === 'ko' ? dayjs(in_assessment_ts).format('YYYY년 MM월 DD일') : dayjs(in_assessment_ts).format('MM/DD/YYYY');
            } else {
                if (_assessmentInfo.requested_date) {
                    _assessmentInfo.requested_date = this.report_lang === 'ko' ? dayjs(_assessmentInfo.requested_date).format('YYYY년 MM월 DD일') : dayjs(_assessmentInfo.requested_date).format('MM/DD/YYYY');
                } else {
                    _assessmentInfo.requested_date = ' - ';
                }
                
                if (_assessmentInfo.duedate_date) {
                    _assessmentInfo.duedate_date = this.report_lang === 'ko' ? dayjs(_assessmentInfo.duedate_date).format('YYYY년 MM월 DD일') : dayjs(_assessmentInfo.duedate_date).format('MM/DD/YYYY');
                } else {
                    _assessmentInfo.duedate_date = ' - ';
                }
                
                if (_assessmentInfo.completed_date) {
                    _assessmentInfo.completed_date = this.report_lang === 'ko' ? dayjs(_assessmentInfo.completed_date).format('YYYY년 MM월 DD일') : dayjs(_assessmentInfo.completed_date).format('MM/DD/YYYY');
                } else {
                    _assessmentInfo.completed_date = dayjs().format(this.report_lang === 'ko' ? 'YYYY년 MM월 DD일' : 'MM/DD/YYYY');
                }
            }

            return this.applyCustomDuration(_assessmentInfo);
        },
        ordered_cataory1() {
            return ['A. 인권', 'B. 윤리'];
        },
        category1_composition() {
            if (!this.scoring?.scoring_pd || !this.scoring.scoring_pd.length) {
                return [{label: '인권', value: 0}, {label: '윤리', value: 0}];
            } else {
                const _category1_composition = this.scoring.scoring_pd.reduce((_cat1_composition, _score) => {
                    const {
                        satisfied,
                        category1_question_code,
                        category1_question_name,
                        category1_question_name_ca,
                        category1_question_name_ea
                    } = _score;
                    if (!_cat1_composition[category1_question_code]) {
                        _cat1_composition[category1_question_code] = {
                            category1_question_name, category1_question_name_ca, category1_question_name_ea, count: 0
                        };
                    }
                    
                    if (satisfied) {
                        ++_cat1_composition[category1_question_code]['count'];
                    }
                    
                    return _cat1_composition;
                }, {});
                
                return this.ordered_cataory1.reduce((_ordered, _category1_name) => {
                    const _category1 = Object.values(_category1_composition).find(({category1_question_name}) => category1_question_name === _category1_name);
                    if (_category1) _ordered.push(_category1);
                    return _ordered;
                }, []);
            }
        },
        sub_imporatance_composition_description() {
            // 지표 개수(인권/윤리/총합)는 sub_scoring_pd(문항 단위) 를 category1 이름으로 그룹해 카운트.
            //   '인권'/'윤리' 포함 행 수 = 해당없음(NA) 포함 전체 적용 지표 수 (예: HD_DOM_PROD_26 인권 46/윤리 10).
            //   category1_scoring_pd 인덱스([0]/[1]) 방식은 순서가 [인권, 기본, 윤리] 라 [1]=기본 으로 잘못 잡힘 → 폐기.
            //   '문항 수'(권위값)는 NA 제외(적용)라 44 가 되어 desc 의 '총 56' 과 안 맞음 → 여기선 총 지표 수(=행 수) 사용.
            const _subs = this.scoring?.sub_scoring_pd ?? [];
            const _cntCat1 = (kw) => _subs.filter(_q => String(_q.category1_question_name || '').includes(kw)).length;
            const human_count = _cntCat1('인권');
            const ethics_count = _cntCat1('윤리');
            const total_count = human_count + ethics_count;
            const company_name = this.headerInfo?.company_name ?? '';

            // 문구 템플릿(ko/en/ca)은 raw-data/summary_composition_desc_hyundai_v2.json 에서 관리.
            //   placeholder 치환: {company_name} {human_count} {ethics_count} {total_count}.
            const _lang = ['ko', 'en', 'ca'].includes(this.report_lang) ? this.report_lang : 'ko';
            const _template = summary_composition_desc?.[_lang] ?? summary_composition_desc?.ko ?? '';

            const _vars = {company_name, human_count, ethics_count, total_count};
            return _template.replace(
                /\{(company_name|human_count|ethics_count|total_count)\}/g,
                (_match, key) => String(_vars[key] ?? '')
            );
        },
        // v2 — raw-data 노출 (management-level-config.json 임계값 + core 11문항)
        //   code_level_thresholds / code_advice 는 campaign(사업장)별로 다른 구조 → campaign_id 로 lookup.
        //   호출 컨텍스트 별 campaign_id source 우선순위:
        //     1) this.campaign_id (Questions.vue, CompanyAnswer.vue 등 prop 전달)
        //     2) this.reportData.info.campaign.id (ReportViewer*HYUNDAI_V2 / 결과보고서)
        //   매핑 없는 캠페인이면 빈 객체 반환 → 모든 lookup 이 안전하게 falsy.
        currentCampaignId () {
            return String(this.campaign_id ?? this.reportData?.info?.campaign?.id ?? '');
        },
        codeLevelThresholds () {
            const _cfg = (code_level_thresholds && code_level_thresholds[this.currentCampaignId]) || {};
            // B-2(그린워싱) 코드 전면 제외 → 코드별 관리수준/준수결과/4-card 카운트에서 B-2 미표시
            return Object.keys(_cfg).reduce((_acc, _k) => {
                if (!/^B-2/.test(_k)) _acc[_k] = _cfg[_k];
                return _acc;
            }, {});
        },
        codeLocalized () { return code_localized; },
        codeAdvice () {
            return (code_advice && code_advice[this.currentCampaignId]) || {};
        },
        // Core 11문항. 단, 질문지(캠페인)에 문항 자체가 없는 Core 지표는 제외한다.
        //   (제외 안 하면 채점 유령 row 로 인해 Core 표에 '미충족' 으로 표출됨 —
        //    core_indicator_campaign_exclusions 주석 참고. 예: A-4-1/H1.49v2 @ 553·556·557·558)
        //   제외 후 No. 컬럼 연속성 유지를 위해 1..N 재부여.
        coreRiskIndicators () {
            const _cid = Number(this.currentCampaignId);
            return core_risk_indicators
                .filter((ind) => {
                    const _ex = core_indicator_campaign_exclusions[ind.db_sub_code];
                    return !(_ex && _ex.includes(_cid));
                })
                .map((ind, _i) => ({ ...ind, no: _i + 1 }));
        },
        codeDescriptions () { return code_descriptions; },
        auditCategoriesMap () { return audit_categories; },
        answerLocalizedMap () { return answer_localized?.answers || {}; },
        answersByQuestion () { return answers_by_question?.by_question || {}; },
        questionSubjectsMap () { return question_subjects?.subjects || {}; },
        // Q-no → 'subject' (주제) lookup. 'Q1.9: 인권 정책 제정 또는 번역 보유' 의 ': 뒷부분'.
        getLocalizedQuestionSubject () {
            const lang = this.report_lang;
            const map = this.questionSubjectsMap;
            return (qno) => {
                const entry = map[qno];
                if (entry && entry[lang]) return entry[lang];
                if (entry && entry.ko) return entry.ko;
                return '';
            };
        },
        // scoring_pd 의 한 question(label=Q1.9) 의 모든 ev row 를 종합해 '답변 최종' 카테고리/텍스트 결정.
        //   not_applicable=true  → na
        //   획득 점수 합 > 0     → satisfied
        //   획득 점수 0          → 평가자가 충족 처리한 '부정 근거(0점) row' 의 formula 로 세분
        //                          ('모름' → unsatisfied_unknown / 그 외 → unsatisfied_no)
        //
        // 충족 판정은 score 기준이지 satisfied 플래그가 아니다.
        //   0점 부정 근거 row (formula: result=='아니오' / result=='모름') 도 평가자 수동 판정
        //   (manual_satisfied) 으로 satisfied=true 를 달고 오기 때문에, 플래그로 판정하면
        //   0점(미충족) 문항이 '충족' 으로 오표기된다. 예: 32807(558) A-1-5/Q1.22 H1.22_n
        //   = score 0/cap 3 인데 result=='모름' row 가 satisfied=true → '충족' 표기.
        //   coreIndicatorStatusByDbCode(:941) / hyundaiComplianceRates(:306) 와 동일 기준(score>0=충족).
        //
        // 미충족 문구는 '평가자 판정이 최종' 기준.
        //   평가자가 고른 부정 근거 row 의 formula (result=='모름' / result=='아니오') 로 판정하고,
        //   평가자 판정이 아예 없을 때만 기존 is_compliance=1 row 의 ev_name 키워드로 fallback.
        //   (is_compliance=1 은 Core 문항에만 부여되어 비Core 문항은 이 fallback 이 동작하지 않는다)
        //   결정된 ko 텍스트 → answer_localized 매핑으로 i18n.
        getQuestionAnswerFromRows () {
            const lang = this.report_lang;
            const byQ = this.answersByQuestion;
            const ansMap = this.answerLocalizedMap;
            const _norm = (s) => String(s || '').replace(/[○●]/g, '').replace(/\s+/g, ' ').trim();

            return (rowsForQ, qno) => {
                if (!rowsForQ || !rowsForQ.length || !qno) return { category: 'unknown', text: '' };

                let category;
                const _score = rowsForQ.reduce(
                    (s, r) => s + (r.not_applicable ? 0 : (Number(r.score) || 0)), 0
                );
                if (rowsForQ.some(r => r.not_applicable === true)) {
                    category = 'na';
                } else if (_score > 0) {
                    category = 'satisfied';
                } else {
                    // 평가자가 충족 처리한 0점(부정) 근거 row — 있으면 이것이 최종 판정
                    const auditRow = rowsForQ.find(
                        r => r.satisfied === true && !(Number(r.ev_score) > 0)
                    );
                    if (auditRow) {
                        category = /모름/.test(String(auditRow.formula || ''))
                            ? 'unsatisfied_unknown'
                            : 'unsatisfied_no';
                    } else {
                        // 평가자 판정 없음 → 기존 규칙 유지 (is_compliance=1 row 의 ev_name 키워드)
                        const negRow = rowsForQ.find(r => r.is_compliance === 1);
                        const negName = negRow ? String(negRow.ev_name || '') : '';
                        category = /확인되지 않음|확인할 수 없음|확인되지/.test(negName)
                            ? 'unsatisfied_unknown'
                            : 'unsatisfied_no';
                    }
                }

                const ko = byQ[qno]?.[category] || '';
                if (!ko) return { category, text: '' };

                if (lang === 'ko') return { category, text: ko };
                const i18nEntry = ansMap[_norm(ko)];
                const localized = i18nEntry?.[lang];
                return { category, text: localized || ko };
            };
        },
        // 한 question 의 ev row 중 audit_category_code 가 의미 있는 값을 가진 row 의 코드를 반환.
        //   EV_AUTO / null / 빈 문자열은 skip.
        //   우선순위는 getQuestionAnswerFromRows 와 동일하게 '평가자 판정이 최종' 기준:
        //     1) 평가자가 충족 처리한 0점 부정 근거 row (그 문항의 미충족 사유를 확정한 row)
        //     2) 점수를 못 받은 row
        //     3) 아무 유효 row
        //   1) 을 satisfied !== true 로 걸러내면 안 된다 — 평가자가 고른 부정 근거 row 는
        //   satisfied=true 를 달고 오기 때문에 정작 판정 주체인 row 가 빠진다.
        pickAuditCategoryFromRows () {
            return (rowsForQ) => {
                if (!rowsForQ || !rowsForQ.length) return '';
                const _valid = (c) => c && c !== 'EV_AUTO';
                const audit = rowsForQ.find(
                    r => _valid(r.audit_category_code)
                        && r.satisfied === true && !(Number(r.ev_score) > 0)
                );
                if (audit) return audit.audit_category_code;
                const fail = rowsForQ.find(
                    r => _valid(r.audit_category_code) && !(Number(r.score) > 0)
                );
                if (fail) return fail.audit_category_code;
                const any = rowsForQ.find(r => _valid(r.audit_category_code));
                return any ? any.audit_category_code : '';
            };
        },
        // P10/P11 답변 최종 lookup 헬퍼 — ko 텍스트(○/● marker 제거, 공백 정리) → en/ca 매핑.
        //   백엔드 ev_name.ko 와 raw-data ko 가 미묘하게 다를 수 있어 normalize 후 매칭.
        //   매칭 fail 시 backend ev_name[lang] fallback, 그것도 없으면 ko 원문.
        getLocalizedAnswer () {
            const lang = this.report_lang;
            const map = this.answerLocalizedMap;
            const _norm = (s) => String(s || '').replace(/[○●]/g, '').replace(/\s+/g, ' ').trim();
            return (ko, fallbacks = {}) => {
                if (!ko) return '';
                const key = _norm(ko);
                const entry = map[key];
                if (entry && entry[lang]) return entry[lang];
                if (lang === 'ko') return ko;
                if (lang === 'en' && fallbacks.ev_name_e) return fallbacks.ev_name_e;
                if (lang === 'ca' && fallbacks.ev_name_c) return fallbacks.ev_name_c;
                return ko;
            };
        },
        // P10/P11 평가 근거 lookup 헬퍼 — audit_category_code (EV_*) → 축약 텍스트.
        //   백엔드가 code 만 보내면 raw-data 매핑으로 i18n. 그 외 fallback 은 빈 문자열.
        getLocalizedAuditCategory () {
            const lang = this.report_lang;
            const map = this.auditCategoriesMap;
            return (code) => {
                if (!code || code === 'EV_AUTO') return '';
                const entry = map[code];
                if (entry && entry[lang]) return entry[lang];
                if (entry && entry.ko) return entry.ko;
                return '';
            };
        },
        // v2 — codeLocalized 와 codeLevelThresholds 를 함께 조회해 lang 별 텍스트 반환.
        //   - getLocalizedCodeName(codeId)      → name (lang fallback ko)
        //   - getLocalizedLevelMessage(id, lv)  → levels[lv].defaultMessage (lang fallback ko)
        // codeLocalized 에 누락된 케이스는 code_level_config_hyundai_v2.json 의 ko 원문으로 자동 fallback.
        getLocalizedCodeName () {
            const lang = this.report_lang;
            return (codeId) => {
                const loc = this.codeLocalized[codeId]?.name;
                const fallback = this.codeLevelThresholds[codeId]?.name || '';
                return (loc && loc[lang]) || (loc && loc.ko) || fallback;
            };
        },
        getLocalizedLevelMessage () {
            const lang = this.report_lang;
            return (codeId, level) => {
                const loc = this.codeLocalized[codeId]?.levels?.[level];
                const fallback = this.codeLevelThresholds[codeId]?.levels?.[level]?.defaultMessage || '';
                return (loc && loc[lang]) || (loc && loc.ko) || fallback;
            };
        },
        // v2 — 코드별 '개선 권고' 텍스트 (major / minor 등급만 적용). 출처: code_advice_hyundai_v2.json.
        // lang fallback: 요청 lang 키가 없으면 ko 사용. 빈 문자열이면 '' 반환 → 호출부에서 카드 미렌더 처리.
        getLocalizedLevelAdvice () {
            const lang = this.report_lang;
            return (codeId, level) => {
                if (level !== 'major' && level !== 'minor') return '';
                const entry = this.codeAdvice?.[codeId]?.[level];
                if (!entry) return '';
                return (entry[lang] || entry.ko || '').trim();
            };
        },
        // v2 — sub_code (A-X-Y) → level (major/minor/conformance/excellent/na) 매핑.
        // ManagementLevelTable.vue 의 currentLevel 로직을 그대로 따른다:
        //   1. scoring_pd + risk_pd 를 합쳐 category3 (= A-X-Y) 별로 그룹화
        //   2. 각 category3 안에서 sub_code 별로 묶어 {score, cap, not_applicable, is_core} 계산
        //   3. 전체 N/A → 'na', CORE 미충족 → 'major', 점수 합산 후 임계값 매칭
        // 충족 판정은 전부 score 기준이다. row 의 satisfied 플래그는 쓰지 않는다 —
        //   0점 부정 근거 row 도 평가자 수동 판정으로 satisfied=true 가 되기 때문.
        //   (getQuestionAnswerFromRows / coreIndicatorStatusByDbCode 주석 참고)
        codeLevelByCode () {
            const map = {};
            const config = this.codeLevelThresholds;
            const rawRows = [
                ...(this.scoring?.scoring_pd ?? []),
                ...(this.reportData?.risk?.risk_pd ?? [])
            ];
            if (!rawRows.length || !config) return map;

            // category3 (= A-X-Y) 별 그룹화
            const c3Groups = {};
            for (const row of rawRows) {
                const c3name = row.category3_question_name;
                if (!c3name) continue;
                const m = String(c3name).match(/^([A-Z]-\d+-\d+)/);
                if (!m) continue;
                const codeId = m[1];
                if (!config[codeId]) continue;
                if (!c3Groups[codeId]) c3Groups[codeId] = [];
                c3Groups[codeId].push(row);
            }

            for (const codeId of Object.keys(config)) {
                const rows = c3Groups[codeId];
                if (!rows || !rows.length) { map[codeId] = 'na'; continue; }

                const subGroups = {};
                for (const r of rows) {
                    const sc = r.sub_code;
                    if (!sc) continue;
                    if (!subGroups[sc]) subGroups[sc] = { rows: [], risk: r.risk === true };
                    subGroups[sc].rows.push(r);
                    if (r.risk === true) subGroups[sc].risk = true;
                }
                const items = Object.values(subGroups).map(g => {
                    const allNA = g.rows.every(r => r.not_applicable === true);
                    const score = g.rows.reduce((s, r) => s + (r.not_applicable ? 0 : (Number(r.score) || 0)), 0);
                    // 0618: 해당없음(NA) sub_code 의 만점(cap_score) — totalScore 산정 시 만점으로 가산
                    const cap = g.rows.reduce((m, r) => Math.max(m, Number(r.cap_score) || 0), 0);
                    return { score, cap, not_applicable: allNA, is_core: g.risk };
                });
                if (!items.length) { map[codeId] = 'na'; continue; }

                let level;
                if (items.every(i => i.not_applicable)) {
                    level = 'na';
                } else if (items.some(i => i.is_core && !i.not_applicable && (Number(i.score) || 0) === 0)) {
                    // CORE 미충족(0점) → 즉시 major. score 기준(satisfied 플래그 아님) — P8 Core 표와 동일 규칙.
                    level = 'major';
                } else {
                    // 0618: NA sub_code 는 만점(cap), 그 외는 획득 점수로 합산 (전 문항 NA Code 는 위에서 'na' 처리)
                    const totalScore = items.reduce((s, i) => s + (i.not_applicable ? (i.cap || 0) : (i.score || 0)), 0);
                    const lv = config[codeId].levels || {};
                    if (lv.excellent && lv.excellent.minScore != null && totalScore >= lv.excellent.minScore) level = 'excellent';
                    else if (lv.conformance && lv.conformance.minScore != null && totalScore >= lv.conformance.minScore) level = 'conformance';
                    else if (lv.minor && lv.minor.minScore != null && totalScore >= lv.minor.minScore) level = 'minor';
                    else level = 'major';
                }
                map[codeId] = level;
            }
            return map;
        },
        // v2 — P8 Core 표용. core_risk_indicators[].db_sub_code (H1.Xv2) 별 충족 여부 매핑.
        //   - codeLevelByCode 와 동일하게 scoring_pd + risk_pd 를 sub_code 기준 그룹화
        //   - 각 sub_code 의 판정 규칙:
        //       not_applicable: 모든 row 가 not_applicable === true
        //       satisfied:      획득 점수 합 !== 0  (row 의 satisfied 플래그가 아니다 — 아래 주석 참고)
        //   - scoring 응답에 해당 sub_code 가 없는 경우 satisfied=null (데이터 없음)
        coreIndicatorStatusByDbCode () {
            const status = {};
            const rawRows = [
                ...(this.scoring?.scoring_pd ?? []),
                ...(this.reportData?.risk?.risk_pd ?? [])
            ];
            const subGroups = {};
            for (const r of rawRows) {
                const sc = r.sub_code;
                if (!sc) continue;
                if (!subGroups[sc]) subGroups[sc] = [];
                subGroups[sc].push(r);
            }
            for (const ind of this.coreRiskIndicators) {
                const code = ind.db_sub_code;
                const rows = subGroups[code];
                if (!rows || !rows.length) {
                    // 0619: scoring/risk 데이터에 row 가 없는 Core 지표 = 해당 사업장에 미적용 → 해당없음(N/A) 처리.
                    //   (기존엔 satisfied:null → 표에 '-' 로 표출되던 문제. 예: 본사 A-1-5/Q1.22 사내도급)
                    status[code] = { satisfied: null, not_applicable: true };
                    continue;
                }
                const allNA = rows.every(r => r.not_applicable === true);
                // 충족 판정 = 점수 획득(score≠0) 기준. satisfied 플래그가 아니다.
                //   미답변·0점인데 satisfied=true 인 value-drift 케이스(옛 답변 문구에 formula 매칭)를
                //   '충족' 으로 오표기하던 버그 수정. 예: 남양연구소(553) A-1-5/Q1.22 H1.22_n = 0점 → 미충족.
                //   count 기반 충족률(hyundaiComplianceRates)·코어지표 엑셀과 동일 기준(score>0=충족).
                const totalScore = rows.reduce((s, r) => s + (r.not_applicable ? 0 : (Number(r.score) || 0)), 0);
                const satisfied = totalScore !== 0;
                status[code] = { satisfied, not_applicable: allNA };
            }
            return status;
        },
        // v2 — 코드별 경영 수준 결과 카운트 (P2 4-card / P5 TOTAL 행 공통 데이터)
        codeLevelCounts () {
            const counts = { major: 0, minor: 0, conformance: 0, excellent: 0, na: 0 };
            const config = this.codeLevelThresholds;
            const rawRows = [
                ...(this.scoring?.scoring_pd ?? []),
                ...(this.reportData?.risk?.risk_pd ?? [])
            ];
            if (!rawRows.length || !config) return counts;

            // 1) category3 (= A-X-Y) 별 그룹화 — question_name 의 "A-X-Y" prefix 로 매칭
            const c3Groups = {};
            for (const row of rawRows) {
                const c3name = row.category3_question_name;
                if (!c3name) continue;
                const m = String(c3name).match(/^([A-Z]-\d+-\d+)/);
                if (!m) continue;
                const codeId = m[1];
                if (!config[codeId]) continue;
                if (!c3Groups[codeId]) c3Groups[codeId] = [];
                c3Groups[codeId].push(row);
            }

            // 2) 각 A-X-Y 그룹에 대해 sub_code 별 집계 후 level 판정 (MLT currentLevel 미러)
            for (const codeId of Object.keys(config)) {
                const rows = c3Groups[codeId];
                if (!rows || !rows.length) continue;

                // sub_code 별 묶기
                const subGroups = {};
                for (const r of rows) {
                    const sc = r.sub_code;
                    if (!sc) continue;
                    if (!subGroups[sc]) subGroups[sc] = { rows: [], risk: r.risk === true };
                    subGroups[sc].rows.push(r);
                    if (r.risk === true) subGroups[sc].risk = true;
                }

                const items = Object.values(subGroups).map(g => {
                    const allNA = g.rows.every(r => r.not_applicable === true);
                    const score = g.rows.reduce((s, r) => s + (r.not_applicable ? 0 : (Number(r.score) || 0)), 0);
                    // 0618: 해당없음(NA) sub_code 의 만점(cap_score) — totalScore 산정 시 만점으로 가산
                    const cap = g.rows.reduce((m, r) => Math.max(m, Number(r.cap_score) || 0), 0);
                    return { score, cap, not_applicable: allNA, is_core: g.risk };
                });

                if (!items.length) continue;

                let level;
                if (items.every(i => i.not_applicable)) {
                    level = 'na';
                } else if (items.some(i => i.is_core && !i.not_applicable && (Number(i.score) || 0) === 0)) {
                    // CORE 미충족(0점) → 즉시 major. score 기준(satisfied 플래그 아님) — P8 Core 표와 동일 규칙.
                    level = 'major';
                } else {
                    // 0618: NA sub_code 는 만점(cap), 그 외는 획득 점수로 합산 (전 문항 NA Code 는 위에서 'na' 처리)
                    const totalScore = items
                        .reduce((s, i) => s + (i.not_applicable ? (i.cap || 0) : (i.score || 0)), 0);
                    const lv = config[codeId].levels || {};
                    if (lv.excellent && lv.excellent.minScore != null && totalScore >= lv.excellent.minScore) {
                        level = 'excellent';
                    } else if (lv.conformance && lv.conformance.minScore != null && totalScore >= lv.conformance.minScore) {
                        level = 'conformance';
                    } else if (lv.minor && lv.minor.minScore != null && totalScore >= lv.minor.minScore) {
                        level = 'minor';
                    } else {
                        level = 'major';
                    }
                }

                counts[level] = (counts[level] || 0) + 1;
            }

            return counts;
        },
        section_score_description() {
            const top2category2_question_names = JSON.parse(JSON.stringify(this.scoringInfo?.category2));
            top2category2_question_names.sort((a, b) => {
                return b['환산_점수'] - a['환산_점수'];
            });
            const lowest2category2_question_names = JSON.parse(JSON.stringify(this.scoringInfo?.category2));
            lowest2category2_question_names.sort((a, b) => {
                return a['환산_점수'] - b['환산_점수'];
            });
            
            
            // 0618: 중분류/Code 수 — 그린워싱(B-2) 제외가 row-level(sub_code) 로만 정확하므로
            //   row-level scoring_pd(+risk) 에서 distinct category2/category3 prefix 로 산정.
            // B-2(그린워싱)는 this.scoring override 에서 이미 제거됨(scoring_pd B-2-free).
            //   risk_pd 는 raw reportData 소스라 여기서 별도 제외.
            const _countRows = [
                ...(this.scoring?.scoring_pd ?? []),
                ...((this.reportData?.risk?.risk_pd ?? []).filter(r => !_isExcludedB2Row(r)))
            ];
            const _c2set = new Set();
            const _c3set = new Set();
            // 0618: 지표 수 = 배점 있는(cap_score>0) 지표만 distinct sub_code.
            //   기본(Basic, cap_score=0)·그린워싱(B-2, 배점 없음)은 점수 측정 대상이 아니라 자동 제외됨.
            // 0721: canonical A/B-x-y 코드 소속 지표(_m3)만 집계. category3 매핑 없는(nan) 유령 문항
            //   (예: 캠페인 미포함 A-4-1/H1.49v2 등, cap>0 인데 미표시 → Q1.9 이전 고용구조와 함께 제외).
            const _qset = new Set();
            for (const _r of _countRows) {
                const _m3 = String(_r.category3_question_name || '').match(/^([A-Z]-\d+-\d+)/);
                if (_m3) {
                    _c3set.add(_m3[1]);
                    const _m2 = _m3[1].match(/^([A-Z]-\d+)/);
                    if (_m2) _c2set.add(_m2[1]);
                }
                if (_m3 && _r.sub_code && (Number(_r.cap_score) || 0) > 0) _qset.add(_r.sub_code);
            }
            const category3_count = _c3set.size;
            const category2_count = _c2set.size;
            const total_count = _qset.size;

            let upCnt = 0;
            // 0618: "##개 Code 는 해당없음 처리됨" 개수 = 경영수준이 'na'(코드 내 전 문항 해당없음)인 Code 수.
            //   본문 "Code별 관리 수준" 표(codeLevelByCode)와 동일 소스(codeLevelCounts)로 일치시킨다.
            const naCnt = this.codeLevelCounts?.na || 0;

            // 0720: 초안(is_draft) 전용 'n' 리터럴 분기 제거.
            //   0629 당시엔 benchmark 가 시드 난수라 '평균 대비 우수/미달' 수치를 확정할 수
            //   없어 'n' 으로 표기했으나, category3 실측 평균이 들어와 초안에서도 실제 값을 쓴다.
            const _levelMap = this.codeLevelByCode || {};
            // 0721: '우수' 판정 기준값 = 점수(환산_점수)가 아니라 충족률(count 기반, 카드 표시값과 동일).
            //   점수 기반은 부분점수 때문에 카드 충족률과 어긋난 수치가 나옴 → 충족률로 통일.
            const _crMap = this.hyundaiComplianceRates?.category3 || {};
            this.scoring.category3_scoring_pd.forEach((category3) => {
                // B-2(그린워싱)는 this.scoring override 에서 이미 제거됨 → 별도 가드 불필요
                const _cid = String(category3.category3_question_name || '').match(/^([A-Z]-\d+-\d+)/)?.[1];
                // 충족률(count 기반). null = 적용 문항 0(해당없음) → 비교 제외.
                const cr = _crMap[category3.category3_question_code];
                // benchmark(전체 평균 충족률) 없는 경우(해당 Code 미시드) → 비교 skip
                const avg = this.benchMarkScore?.category3?.[category3.category3_question_name]?.avg;
                // 해당없음(na) Code / 벤치마크 없음 / 충족률 null → 제외. cr >= avg (동점=우수 포함).
                if (_cid && _levelMap[_cid] === 'na') {
                    return;
                }
                else if (avg == null || cr == null) {
                    return;
                }
                else if (cr >= avg) {
                    upCnt++;
                }
                // 0721: 평균 미달(belowCnt) 집계·문구 제거 — desc 에서 '개선 필요' 문장 삭제됨
            });
            
            
            
            let _section_score_description = '';
            const _name = this.reportData?.info?.company?.company_name;
            if (this.report_lang === 'ko') {
                _section_score_description += `${_name}은/는 총 ${category2_count}개의 중분류, ${category3_count}개의 Code, ${total_count}개 지표에 대해 사업장의 운영 체계 및 관리 실태 전반을 진단받았고, `;
                _section_score_description += `그 중 ${upCnt}개 Code에서는 전체 사업장 평균 대비 우수한 수준을 보였습니다.`;
                if(naCnt >0){
                    _section_score_description += ` (${naCnt}개 Code는 해당없음 처리됨)`;
                }
            }
            else if (this.report_lang === 'ca') {
                _section_score_description += `${_name} 在 ${category2_count} 个中分类、${category3_count} 个 Code、${total_count} 个指标的范围内接受了对运营体系与管理实态的全面诊断，`;
                _section_score_description += `其中 ${upCnt} 个 Code 表现优于全体法人平均水平。`;
                if(naCnt > 0){
                    _section_score_description += `（${naCnt} 个 Code 被认定为不适用）`;
                }
            }
            else {
                _section_score_description += `${_name} underwent a comprehensive assessment of its operational systems and management practices across ${category2_count} subcategories, ${category3_count} Codes, and ${total_count} indicators. `;
                _section_score_description += `Among these, ${upCnt} Codes showed performance above the overall site average.`;
                if(naCnt === 1){
                    _section_score_description += ` (${naCnt} Code was marked as not applicable)`;
                }
                if(naCnt > 1){
                    _section_score_description += ` (${naCnt} Codes were marked as not applicable)`;
                }
            }
            
            return _section_score_description;
        }
    },
    methods: {
        async createSummaryPage(doc, position_y) {
            position_y = this.firstElementSpacing;
            position_y = this.addTitle(doc, position_y, 1, '사업장 및 평가 정보');
            
            /**
             * 기업 기본정보: 사업장명, 소재국가, 매출액, 사업장 유형, 근로자수 (막대 차트)
             */
            
            let _column_width = this.pageContentWidth / 4;
            let [_assessment_info_table_styles_th, _assessment_info_table_styles_td] = [{
                ...this.tableStyles.th,
                cellWidth: _column_width
            }, {...this.tableStyles.td, cellWidth: _column_width}];
            
            
            let _assessment_info_table_body = [[{
                content: this.$t('평가기간', this.report_lang),
                styles: _assessment_info_table_styles_th
            }, {
                content: `${this.assessmentInfo.requested_date} ~ ${this.assessmentInfo.duedate_date}`,
                colSpan: 3,
                styles: _assessment_info_table_styles_td
            }]];
            
            if ((this.assessmentType && this.assessmentType === 'OSIS') || (!this.assessmentType && this.reportData.info.service_type === '심화')) {
                _assessment_info_table_body.push([{
                    content: this.$t('평가유형', this.report_lang),
                    styles: _assessment_info_table_styles_th
                }, {
                    content: this.$t('실사평가', this.report_lang), colSpan: 3, styles: _assessment_info_table_styles_td
                }]);
            }
            
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: this.jsPDFOpt.margin,
                styles: {...this.tableStyles.global},
                body: [[{
                    content: this.$t('사업장명', this.report_lang),
                    styles: _assessment_info_table_styles_th
                }, {
                    content: this.companyInfo['회사명'],
                    styles: _assessment_info_table_styles_td
                }, {
                    content: this.$t('사업장 코드', this.report_lang),
                    styles: _assessment_info_table_styles_th
                }, {
                    content: this.headerInfo.vendor_code,
                    styles: _assessment_info_table_styles_td
                }], [{
                    content: this.$t('소재국가', this.report_lang),
                    styles: _assessment_info_table_styles_th
                }, {
                    content: this.businessSiteCategory[this.headerInfo.vendor_code]?.[`country_${this.report_lang}`] ?? this.businessSiteCategory[this.headerInfo.vendor_code]?.['country_en'],
                    styles: _assessment_info_table_styles_td
                }, {content: this.$t('사업장 유형', this.report_lang), styles: _assessment_info_table_styles_th}, {
                    content: this.businessSiteCategory[this.headerInfo.vendor_code]?.[`business_type_${this.report_lang}`] ?? this.businessSiteCategory[this.headerInfo.vendor_code]?.['business_type_en'],
                    styles: _assessment_info_table_styles_td
                }], [{
                    content: this.$t('전체 근로자 수', this.report_lang),
                    styles: _assessment_info_table_styles_th
                }, {
                    content: this.companyInfo['근로자수'],
                    styles: _assessment_info_table_styles_td,
                    colSpan: 3
                },], ..._assessment_info_table_body],
                didDrawCell: (data) => this.jspdf_common_cell_font_convert(data)
            });
            
            
            this.setFont(doc, 'default', null, 'normal');
            doc.setTextColor(this.hyundai_colors.muted);
            const worker_guide_text = doc.splitTextToSize(`(*${this.$t('근로자 수 기준년도: {year}', this.report_lang, {year: 2025})})`, {fontSize: 7});
            const worker_guide_text_dim = doc.getTextDimensions(worker_guide_text, {fontSize: 7});
            
            const worker_guide_text_position_x = this.jsPDFOpt.margin.left + 64 + Number(worker_guide_text_dim.w);
            const worker_guide_text_position_y = doc.lastAutoTable.finalY - 22;
            
            doc.text(worker_guide_text, worker_guide_text_position_x, worker_guide_text_position_y);
            position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            
            
            // 지표 구성 및 중요도 구성 진단
            const _section_subs_risk_composition_chartBox_padding = 5;
            const _subs_composition_box_position_x = this.jsPDFOpt.margin.left;
            const _subs_composition_padding = _section_subs_risk_composition_chartBox_padding * 3;
            const _subs_composition_area_width = this.pageContentWidth - _subs_composition_padding;
            
            this.setFont(doc, 'table_td', null, 'normal');
            doc.setFontSize(this.report_lang === 'ko' ? 9.5 : 8.5);
            doc.setLineHeightFactor(1.55);

            const _sub_composition_description = doc.splitTextToSize(this.sub_imporatance_composition_description, _subs_composition_area_width);
            const _sub_composition_text_dim = doc.getTextDimensions(_sub_composition_description, {fontSize: this.report_lang === 'ko' ? 10.5 : 9.3});
            
            doc.setLineWidth(0.5);
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.setDrawColor(224, 224, 224);
            const _subs_composition_box_height = _sub_composition_text_dim.h + _subs_composition_padding;
            const _subs_composition_box_width = this.pageContentWidth;
            doc.rect(this.jsPDFOpt.margin.left, position_y, _subs_composition_box_width, _subs_composition_box_height, 'FD');
            
            position_y += 11 - 1.55;
            const _subs_composition_area_position_x = _subs_composition_box_position_x + ((_subs_composition_box_width - _sub_composition_text_dim.w) / 2) + _subs_composition_padding;
            const _subs_composition_area_position_y = position_y + ((_subs_composition_box_height - _sub_composition_text_dim.h) / 2);
            doc.text(_sub_composition_description, _subs_composition_area_position_x, _subs_composition_area_position_y);
            doc.setLineHeightFactor(1.15);
            
            position_y += _sub_composition_text_dim.h + this.jsPDFOpt.spacing.section_sm;
            
            
            position_y = this.addTitle(doc, position_y, 2, this.$t('평가결과', this.report_lang));
            position_y += this.jsPDFOpt.spacing.title_xs;

            // 평가결과 바로 아래 충족률 문구 desc — 폰트 축소 (en 10 / 그 외 11)
            if (this.report_lang === 'en') {
                doc.setFontSize(10);
            } else {
                doc.setFontSize(11);
            }

            const _total_score_guide_box_padding = 10;
            const _total_score_guide_box_width = this.pageContentWidth;
            const _total_score_guide_area_width = _total_score_guide_box_width - (_total_score_guide_box_padding * 2);
            let _total_score_guide_text = doc.splitTextToSize(this.evaluation_score_description[this.report_lang], _total_score_guide_area_width);
            
            const _total_score_guide_dim = doc.getTextDimensions(_total_score_guide_text);
            const _total_score_guide_box_height = _total_score_guide_dim.h + (_total_score_guide_box_padding * 2);
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.setDrawColor(this.jsPDFOpt.card.borderColor);
            doc.rect(this.jsPDFOpt.margin.left, position_y, _total_score_guide_box_width, _total_score_guide_box_height, 'F');
            
            const _total_score_guide_text_position_x = this.jsPDFOpt.margin.left + _total_score_guide_box_padding;
            const _total_score_guide_text_position_y = position_y + ((_total_score_guide_box_height - _total_score_guide_dim.h) / 2) + (_total_score_guide_box_padding / 2);
            doc.text(_total_score_guide_text, _total_score_guide_text_position_x, _total_score_guide_text_position_y, {baseline: 'middle'});
            
            
            position_y += _total_score_guide_box_height + this.jsPDFOpt.spacing.title_xs;
            
            
            if (this.report_lang === 'en') {
                doc.setFontSize(12);
            } else {
                doc.setFontSize(13);
            }
            
            
            // 스코어박스 섹션 — gauge + 코드별 경영 수준 통합 (단일 rect, 카드는 rounded)
            const scorebox_height = this.report_lang === 'ko' ? 70 : 55;
            const scorebox_label_h = 22;
            const scorebox_cards_h = 60;
            const scorebox_total_h = scorebox_height + scorebox_label_h + scorebox_cards_h;
            const scorebox_top_y = position_y;
            doc.setLineWidth(0);
            doc.setDrawColor(this.jsPDFOpt.card.borderColor);
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.rect(this.jsPDFOpt.margin.left, scorebox_top_y, this.pageContentWidth, scorebox_total_h, 'D');
            position_y += scorebox_height;
            
            // 스코어박스 - 스코어 라벨(Total Score)
            if (this.report_lang === 'ca') {
                doc.setFont(this.jsPDFOpt.fontFamily_ca, 'normal');
            } else {
                doc.setFont(this.jsPDFOpt.fontFamily, 'normal');
            }
            
            doc.setFontSize(12);
            const _score_title_position_x = this.jsPDFOpt.margin.left + 10;
            const _score_title_position_y = (position_y - scorebox_height + 0.75) + this.jsPDFOpt.margin.top * 0.9;
            doc.text(this.report_lang === 'ca' ? '总分' : 'Total Compliance Rate', _score_title_position_x, _score_title_position_y);
            
            
            // 스코어박스 - 스코어(점수)
            const total_score_width = this.pageContentWidth * 0.4;
            this.setFont(doc, 'cover_company_name');
            doc.setFontSize(30);
            
            const _score = this.scoringInfo.total['환산_점수'];
            const _score_text = `${_score}%`;
            const _score_score = doc.splitTextToSize(_score_text, total_score_width);
            const _score_score_dim = doc.getTextDimensions(_score_score);
            const _score_position_x = _score_title_position_x + (((total_score_width - _score_title_position_x) - _score_score_dim.w) / 2);
            const _score_text_baseline_y = position_y - (scorebox_height / 2) + (_score_score_dim.h / 2);
            doc.text(_score_score, _score_position_x, _score_text_baseline_y, {halign: 'center'});

            // 전년도(2025) 충족률 — 현년도 큰 % 숫자 아래에 작게 표시 (진회색)
            //   영역별 도넛/코드 카드와 문구 통일: "전년도 충족률 XX%"
            if (this.hasPriorYear && this.priorYearScoringInfo?.total) {
                const _priorScore = Number(this.priorYearScoringInfo.total['환산_점수'] || 0);
                this.setFont(doc, 'footnote', null, 'normal');
                doc.setFontSize(10);
                doc.setTextColor(PRIOR_YEAR_SUMMARY_TEXT_HEX);
                doc.text(
                    `${this.$t('전년도 충족률', this.report_lang)} ${_priorScore}%`,
                    _score_position_x + (_score_score_dim.w / 2),
                    _score_text_baseline_y + 12,
                    {align: 'center'}
                );
                doc.setTextColor('#000000');
            }


            // 스코어박스 - 스코어바
            const scorebar_width = this.pageContentWidth * 0.6;
            const scorebar_height = 12;
            const scorebar_radius = 6;
            const scorebar_position_x = this.pageContentWidth * 0.425;
            const scorebar_position_y = position_y - (scorebox_height / 2) - (scorebar_height / 2);
            
            doc.setLineWidth(0);
            doc.setFillColor(228, 220, 211);
            doc.setDrawColor('#d6cdc4');
            doc.roundedRect(scorebar_position_x, scorebar_position_y, scorebar_width, scorebar_height, scorebar_radius, scorebar_radius, 'FD');
            
            // Draw the marker triangle
            const markerX = scorebar_position_x + ((_score / 100) * scorebar_width);
            doc.setFillColor(0, 0, 0);
            doc.triangle(markerX - 3, scorebar_position_y - 8, markerX + 3, scorebar_position_y - 8, markerX, scorebar_position_y - 3, 'F');

            // Draw the progress bar
            doc.setFillColor(...this.hyundai_colors_rgb.blue);
            doc.roundedRect(scorebar_position_x, scorebar_position_y, (_score / 100) * scorebar_width, scorebar_height, 5, 5, 'F');

            // 전년도(2025) 충족률 마커 — bar 상단 ▼ triangle (현 년도와 동일 모양/위치, 색상만 진회색)
            //   text 라벨은 위쪽(큰 % 숫자 아래) 에 따로 표시됨.
            if (this.hasPriorYear && this.priorYearScoringInfo?.total) {
                const _priorScore = Number(this.priorYearScoringInfo.total['환산_점수'] || 0);
                const _priorMarkerX = scorebar_position_x + ((_priorScore / 100) * scorebar_width);
                doc.setFillColor(...PRIOR_YEAR_SUMMARY_TEXT_RGB);
                doc.triangle(
                    _priorMarkerX - 3, scorebar_position_y - 8,
                    _priorMarkerX + 3, scorebar_position_y - 8,
                    _priorMarkerX,     scorebar_position_y - 3,
                    'F'
                );
            }
            
            // Draw the Step Number
            doc.setFontSize(10);
            doc.setTextColor(this.hyundai_colors.blue);
            doc.text('0%', scorebar_position_x, scorebar_position_y + scorebar_height + 11);
            const _score_100_width = doc.getStringUnitWidth('100') * doc.getFontSize();
            doc.text('100%', scorebar_position_x + scorebar_width - _score_100_width / 2, scorebar_position_y + scorebar_height + 11);
            
            doc.setFontSize(9);
            doc.setTextColor('#999999');
            for (let i = 10; i < 100; i += 10) {
                doc.text(`${i}`, scorebar_position_x + (i / 100) * scorebar_width, scorebar_position_y + scorebar_height + 11, null, null, 'center');
            }
            // ── 코드별 경영 수준 결과 — scorebox 안, gauge 아래 (단일 rect) ──
            {
                // 라벨 영역 (gauge 아래)
                const label_y = scorebox_top_y + scorebox_height + 14;
                doc.setFont(this.jsPDFOpt.fontFamily, 'normal');
                doc.setFontSize(12);
                doc.setTextColor('#212121');
                doc.text(
                    this.$t('코드별 경영 수준 결과', this.report_lang),
                    this.jsPDFOpt.margin.left + 10,
                    label_y
                );

                // 카드 영역 (라벨 아래, 4 컬럼)
                const cards_inner_pad = 10;
                const cards_top = scorebox_top_y + scorebox_height + scorebox_label_h + 2;
                const cards_h = scorebox_cards_h - 10;
                const cardsAreaX = this.jsPDFOpt.margin.left + cards_inner_pad;
                const cardsAreaW = this.pageContentWidth - cards_inner_pad * 2;
                const cardGap = 6;
                // 0618: 해당없음(na) 카드 추가 → 4 → 5 컬럼
                const cardW = (cardsAreaW - cardGap * 4) / 5;

                const levelKeys = ['na', 'major', 'minor', 'conformance', 'excellent'];
                const levelAccentRGB = {
                    major: [183, 65, 56],         // 매우미흡 — 더스티 테라코타
                    minor: [196, 145, 100],       // 미흡 — 웜 탄
                    conformance: [108, 130, 150], // 보통 — 뮤티드 스틸 블루
                    excellent: [110, 140, 95],    // 우수 — 뮤티드 세이지
                    na: [160, 160, 160]           // 해당 없음 — 뉴트럴 그레이
                };
                const levelLabels = {
                    ko: { major: '매우 미흡', minor: '미흡', conformance: '보통', excellent: '우수', na: '해당 없음' },
                    en: { major: 'Major', minor: 'Minor', conformance: 'Conformance', excellent: 'Excellent', na: 'N/A' },
                    ca: { major: '严重不足', minor: '不足', conformance: '一般', excellent: '优秀', na: '不适用' }
                };
                const labels = levelLabels[this.report_lang] || levelLabels.ko;
                const counts = this.codeLevelCounts;

                levelKeys.forEach((key, idx) => {
                    const cx = cardsAreaX + idx * (cardW + cardGap);
                    const cy = cards_top;
                    const accent = levelAccentRGB[key];

                    // 카드 — 흰 배경 + 얇은 sand 보더 + 라운드 코너 (sand 컨테이너 위 lift)
                    doc.setFillColor(255, 255, 255);
                    doc.setDrawColor(...this.hyundai_colors_rgb.sand);
                    doc.setLineWidth(0.3);
                    doc.roundedRect(cx, cy, cardW, cards_h, 3, 3, 'FD');

                    // 좌상단 컬러 dot
                    const dot_r = 3;
                    const dot_x = cx + 10;
                    const dot_y = cy + 13;
                    doc.setFillColor(accent[0], accent[1], accent[2]);
                    doc.circle(dot_x, dot_y, dot_r, 'F');

                    // 라벨 — dot 우측, 진한 회색, 사이즈 +1
                    const tx = dot_x + dot_r + 6;
                    this.setFont(doc, 'footnote', this.jsPDFOpt.fontFamily, 'normal');
                    doc.setFontSize(this.report_lang === 'ko' ? 10.5 : 10);
                    doc.setTextColor('#424242');
                    doc.text(labels[key], tx, cy + 16);

                    // 카운트 — 카드 좌패딩 기준 정렬, navy bold, 사이즈 18
                    const count_x = cx + 10;
                    this.setFont(doc, 'cover_company_name', this.jsPDFOpt.fontFamily, 'bold');
                    doc.setFontSize(18);
                    doc.setTextColor(...this.hyundai_colors_rgb.blue);
                    const cnt = counts[key] ?? 0;
                    const cntText = `${cnt}`;
                    doc.text(cntText, count_x, cy + 36);

                    // "개" suffix — 한글일 때만, 진한 회색
                    if (this.report_lang === 'ko') {
                        const cntW = doc.getTextWidth(cntText);
                        this.setFont(doc, 'footnote', this.jsPDFOpt.fontFamily, 'normal');
                        doc.setFontSize(10);
                        doc.setTextColor('#595959');
                        doc.text('개', count_x + cntW + 3, cy + 36);
                    }
                });

                position_y = scorebox_top_y + scorebox_total_h + this.jsPDFOpt.spacing.section_xs;
            }

            // ESG 평가 영역별 결과
            position_y = this.addTitle(doc, position_y, 3, this.$t('평가 영역별 결과', this.report_lang));
            
            // ESG 평가 영역별 테이블
            let _category1_score_table_name_head = this.scoringInfo.category1.reduce((_added_category1_name, _category1) => {
                _added_category1_name.push({
                    content: _category1.question_name, styles: {...this.tableStyles.th, fontSize: 10}
                });
                return _added_category1_name;
            }, []);
            
            let _category1_score_table_rows = [[{
                content: this.$t('영역별 충족률', this.report_lang),
                colSpan: 2,
                styles: {...this.tableStyles.th, fontSize: 12},
                minCellHeight: 25
            }], [..._category1_score_table_name_head]];
            
            
            const _total_score_cell_width = 0;
            const _category1_score_cell_width = (this.pageContentWidth - _total_score_cell_width) / 2;
            const _category1_score_cell_height = this.pageContentHeight - position_y - this.jsPDFOpt.footerHeight + 8;
            

            // chart 는 작게 — cell 높이의 절반 이하, 최대 60 cap
            const _category1_score_chart_size = Math.min(70, _category1_score_cell_height * 0.6);

            let _category1_score_index = 0;
            let _category1_score_table_body = [];
            for (const _category1 of this.scoringInfo.category1) {
                if (this.category1PieChartInstance) {
                    this.category1PieChartInstance.destroy();
                    this.category1PieChartInstance = null;
                }

                _category1_score_table_body.push({
                    content: '', // chart + % 텍스트는 didDrawCell 에서 직접 렌더
                    category1_cell: true,
                    category1_score: this.roundToOneDecimalPlace(_category1['환산_점수']),
                    category1_color_idx: _category1_score_index,
                    category1_img_url: await this.generateCategory1PieChartInstance(`rgba(${this.category1_composition_colors[_category1_score_index].join(',')})`, [this.roundToOneDecimalPlace(_category1['환산_점수']), 100 - this.roundToOneDecimalPlace(_category1['환산_점수'])]),
                    category1_question_name: _category1.question_name,
                    category1_question_name_ko: _category1.question_name_ko,
                    // _category1.question_name_ko 형식: "A. 인권" → prefix "A"
                    category1_prior_score: this.getPriorYearScore('category1', _extractCategoryPrefix(_category1.question_name_ko)),
                    styles: {
                        ...this.tableStyles.td,
                        minCellHeight: _category1_score_cell_height,
                        cellWidth: _category1_score_cell_width,
                        valign: 'middle',
                        cellPadding: { top: 0, bottom: 0, left: 0, right: 0 }
                    }
                });
                ++_category1_score_index;
            }

            _category1_score_table_rows.push(_category1_score_table_body);

            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: {...this.jsPDFOpt.margin, top: 0, left: this.jsPDFOpt.margin.left},
                styles: {...this.tableStyles.global, halign: 'center'},
                body: _category1_score_table_rows,
                didDrawCell: async (data) => {
                    if (data.cell.raw?.category1_cell !== true) return;

                    const score = data.cell.raw.category1_score;
                    const colorIdx = data.cell.raw.category1_color_idx;
                    const accent = this.category1_composition_colors[colorIdx];

                    // 컨텐츠를 셀의 좌/우 두 영역으로 나눠 [chart] [number]
                    const inner_pad = 36;
                    const content_y_mid = data.cell.y + _category1_score_cell_height / 2;
                    const chart_size = _category1_score_chart_size;
                    const chart_x = data.cell.x + inner_pad;
                    const chart_y = content_y_mid - chart_size / 2;

                    doc.addImage(
                        data.cell.raw.category1_img_url, 'png',
                        chart_x, chart_y, chart_size, chart_size
                    );

                    // 큰 % 숫자 — chart 우측에 배치 (현년도 충족률). 영문은 캡션이 길어 % 를 더 작게.
                    this.setFont(doc, 'cover_company_name', this.jsPDFOpt.fontFamily, 'bold');
                    doc.setFontSize(this.report_lang === 'en' ? 22 : 25);
                    // doc.setTextColor(accent[0], accent[1], accent[2]);
                    const pctText = `${score}%`;
                    const pctH = doc.getTextDimensions(pctText).h;
                    const text_x = chart_x + chart_size + 16;
                    // pctH/2 로 % 박스를 셀 중앙에 자동 정렬(폰트 크기 바뀌면 같이 따라옴) + 위로 살짝 올림(-7):
                    //   글자를 줄이면 % 박스가 작아져 baseline 이 올라가므로, 전년도 줄 띄움용 lift 는 -9→-7 로 축소.
                    const text_y = content_y_mid + pctH / 2 - 7;
                    doc.text(pctText, text_x, text_y);

                    // 전년도 충족률 — 큰 % 아래에 작게 표시 (summary 페이지 진회색 톤)
                    const priorScore = data.cell.raw.category1_prior_score;
                    if (priorScore != null) {
                        this.setFont(doc, 'footnote', this.jsPDFOpt.fontFamily, 'normal');
                        doc.setFontSize(8.5);
                        doc.setTextColor(PRIOR_YEAR_SUMMARY_TEXT_HEX);
                        doc.text(
                            `${this.$t('전년도 충족률', this.report_lang)} ${priorScore}%`,
                            text_x,
                            text_y + 12
                        );
                        doc.setTextColor('#000000');
                    }

                    if (this.category1PieChartInstance) {
                        this.category1PieChartInstance.destroy();
                        this.category1PieChartInstance = null;
                    }
                }
            });
            
            position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            
            return position_y;
        },
        generatecategory1AreaChartInstance(_data = this.scoringInfo.category1, opt = {}) {
            return new Promise((resolve) => {
                let _resolved = false;
                const _resolveOnce = (_img) => {
                    if (_resolved) return;
                    _resolved = true;
                    resolve(_img);
                };
                if (!_data) {
                    _data = this.scoringInfo.category1;
                }
                
                let _category1_data = _data.map(({환산_점수}) => 환산_점수);
                let _category1_label = _data.map(({question_name}) => this.$t(question_name, this.report_lang));
                if (this.report_lang === 'en') {
                    _category1_label = this.splitByManagement(_category1_label);
                }
                
                
                if (!opt.emphasis || typeof opt.emphasis === 'undefined') {
                    opt.emphasis = false;
                }
                
                let emphasis_max_label = null;
                if (!opt.emphasis_max || typeof opt.emphasis_max === 'undefined') {
                    opt.emphasis_max = false;
                } else {
                    let _max_score = Math.max(..._category1_data);
                    emphasis_max_label = _data.find(({환산_점수}) => 환산_점수 === _max_score);
                    emphasis_max_label = this.splitByManagement(this.$t(emphasis_max_label.question_name, this.report_lang));
                }
                
                let emphasis_min_label = null;
                if (!opt.emphasis_min || typeof opt.emphasis_min === 'undefined') {
                    opt.emphasis_min = false;
                } else {
                    let _min_score = Math.min(..._category1_data);
                    emphasis_min_label = _data.find(({환산_점수}) => 환산_점수 === _min_score);
                    emphasis_min_label = this.splitByManagement(this.$t(emphasis_min_label.question_name, this.report_lang));
                }
                
                const _scope1_data = {
                    labels: _category1_label, datasets: [{
                        data: _category1_data,
                        fill: true,
                        backgroundColor: 'rgba(194, 24, 91, 0.4)',
                        borderColor: 'rgb(194, 24, 91)',
                        pointBorderColor: (context) => {
                            if (opt.emphasis_max) {
                                if (_category1_label[context.index] === emphasis_max_label) {
                                    return 'rgba(194, 24, 91, 1)';
                                }
                            }
                            
                            if (opt.emphasis_min) {
                                if (_category1_label[context.index] === emphasis_min_label) {
                                    return 'rgba(229, 57, 53, 1)';
                                }
                            }
                            
                            if (opt.emphasis) {
                                if (_category1_label[context.index] === opt.emphasis_label) {
                                    return 'rgba(194, 24, 91, 1)';
                                }
                            }
                            
                            return 'rgb(206, 66, 122)';
                        },
                        pointBackgroundColor: (context) => {
                            if (opt.emphasis_max) {
                                if (_category1_label[context.index] === emphasis_max_label) {
                                    return 'rgba(194, 24, 91, 0.6)';
                                }
                            }
                            
                            if (opt.emphasis_min) {
                                if (_category1_label[context.index] === emphasis_min_label) {
                                    return 'rgba(229, 57, 5, 0.6)';
                                }
                            }
                            
                            if (opt.emphasis) {
                                if (_category1_label[context.index] === opt.emphasis_label) {
                                    return 'rgba(194, 24, 91, 0.6)';
                                }
                            }
                            
                            return 'rgba(181, 69, 114, 0.5)';
                        }
                    }]
                };
                
                const _common_option = {
                    global: {
                        aspectRatio: this.report_lang === 'en' ? null : 4.7 / 3,
                        responsive: false,
                        maintainAspectRatio: false,
                        devicePixelRatio: 1.5,
                        responsiveAnimationDuration: 0,
                        animation: {
                            duration: 0, onComplete: function () {
                                _resolveOnce(this.toBase64Image('image/jpg', 1));
                            }
                        }
                    }, plugins: {
                        legend: {display: false}, datalabels: {
                            display: false
                        },
                    }, scales: {
                        max: 100, beginAtZero: true, ticks: {
                            display: false
                        }
                    }, layout: {}
                };
                
                let _font_size = {
                    default: this.report_lang === 'en' ? 12.5 : 14.5,
                    enphasis: this.report_lang === 'en' ? 13.5 : this.report_lang === 'ca' ? 15.5 : 16
                };
                
                const _scope1_area_option = {
                    ..._common_option.global,
                    layout: _common_option.layout,
                    elements: {line: {borderWidth: 1.5}},
                    scales: {
                        r: {
                            ..._common_option.scales, ticks: {
                                ..._common_option.scales.ticks, stepSize: 25, color: '#E0E0E0', autoSkip: false, // 레이블 건너뛰기 해제
                                overflow: 'hidden' // 레이블이 차트 영역 밖으로 넘치지 않도록 설정
                            }, grid: {
                                color: '#E0E0E0'
                            }, pointLabels: {
                                display: true, padding: 7, font: {
                                    size: (context) => {
                                        if (opt.emphasis_max) {
                                            if (context.label === emphasis_max_label) {
                                                return _font_size.enphasis;
                                            }
                                        }
                                        
                                        if (opt.emphasis_min) {
                                            if (context.label === emphasis_min_label) {
                                                return _font_size.enphasis;
                                            }
                                        }
                                        
                                        if (opt.emphasis) {
                                            if (context.label === opt.emphasis_label) {
                                                return _font_size.enphasis;
                                            }
                                        }
                                        
                                        return _font_size.default;
                                    }, weight: (context) => {
                                        if (opt.emphasis_max) {
                                            if (context.label === emphasis_max_label) {
                                                return 700;
                                            }
                                        }
                                        
                                        if (opt.emphasis_min) {
                                            if (context.label === emphasis_min_label) {
                                                return 700;
                                            }
                                        }
                                        
                                        if (opt.emphasis) {
                                            if (context.label === opt.emphasis_label) {
                                                return 700;
                                            }
                                        }
                                        
                                        return 500;
                                    },
                                }, color: (context) => {
                                    if (opt.emphasis_max) {
                                        if (context.label === emphasis_max_label) {
                                            return 'rgba(194, 24, 91, 1)';
                                        }
                                    }
                                    
                                    if (opt.emphasis_min) {
                                        if (context.label === emphasis_min_label) {
                                            return 'rgba(229, 57, 5, 1)';
                                        }
                                    }
                                    
                                    if (opt.emphasis) {
                                        if (context.label === opt.emphasis_label) {
                                            return 'rgba(194, 24, 91, 1)';
                                        }
                                    }
                                    
                                    return '#616161';
                                }
                            }
                        }
                    },
                    plugins: _common_option.plugins
                };
                
                if (!this.category1AreaChartInstance) {
                    this.category1AreaChartInstance = this.$chartjs.createChart(document.getElementById('category1-chart-area'), {
                        type: 'radar', data: _scope1_data, options: _scope1_area_option
                    });
                } else {
                    this.$chartjs.updateChart(this.category1AreaChartInstance, _scope1_data, _scope1_area_option);
                }

                // animation.onComplete 는 Chart.js 가 requestAnimationFrame 으로 스케줄링 —
                //   브라우저 탭이 백그라운드면 rAF 가 안 돌아 영원히 resolve 안 될 수 있음(벌크 처리 hang 원인).
                //   setTimeout 은 백그라운드에서도 동작하므로 안전망으로 사용.
                setTimeout(() => {
                    if (_resolved || !this.category1AreaChartInstance) return;
                    _resolveOnce(this.category1AreaChartInstance.toBase64Image('image/jpg', 1));
                }, 1000);
            });
        },
        generateCategory2ContraryAreaChartInstance(doc) {
            return new Promise((resolve) => {
                let _resolved = false;
                const _resolveOnce = (_img) => {
                    if (_resolved) return;
                    _resolved = true;
                    resolve(_img);
                };
                const _isEn = this.report_lang === 'en';   // 영문은 라벨/값 폰트를 ko/ca 보다 더 축소
                // ── canonical 순서 (12시 = A-1, clockwise) — A-/B- prefix 없는 항목
                //    ('고용 구조 및 리스크' / 미매핑 nan)은 inCanonical 에서 자동 제외.
                //    B-2(그린워싱)는 this.scoring override 에서 이미 빠져 scoringInfo.category2 에 없음.
                const _canonical_order = ['A-1', 'A-2', 'A-3', 'A-4', 'A-5', 'B-1', 'B-2'];
                const _prefixOf = (q) => {
                    const m = String(q || '').match(/^([AB]-\d+)/);
                    return m ? m[1] : null;
                };
                const _orderRank = (q) => _canonical_order.indexOf(_prefixOf(q));
                // 0721: 해당없음(NA) 중분류(배점 0)도 축으로 노출 — 라벨 + 'N/A' 표시.
                //   기존 hasAllocation(배점>0) 필터가 A-5 같은 NA 중분류를 통째로 숨기던 것을 제거.
                const _sorted_category2 = [...this.scoringInfo.category2]
                    .filter(c2 => _orderRank(c2.question_name_ko || c2.question_name) !== -1)
                    .sort((a, b) => _orderRank(a.question_name_ko || a.question_name) - _orderRank(b.question_name_ko || b.question_name));

                const naIdx = [];
                const _current_data = _sorted_category2.map(({환산_점수, 배점}, idx) => {
                    // 배점 0 && 환산 0 = 전 문항 해당없음 중분류 → 0%점(중심 함몰) 대신 null(축만 표시),
                    //   라벨/값 렌더 시 naIdx 로 'N/A' 표기 (아래 customPointLabels).
                    if (환산_점수 === 0 && 배점 === 0) { naIdx.push(idx); return null; }
                    return 환산_점수;
                });

                const labels = _sorted_category2.map(({question_name}) => doc.splitTextToSize(question_name, 95));

                // benchmark 는 Korean question_name_ko 키로 직접 조회 (prefix 매칭 X)
                const avgBenchmark = [];
                const tierAvgBenchmark = [];
                _sorted_category2.forEach(({question_name_ko}) => {
                    const bench = this.benchMarkScore?.category2?.[question_name_ko];
                    avgBenchmark.push(bench?.avg ?? null);
                    tierAvgBenchmark.push(bench?.tier_avg?.[this.project_code] ?? null);
                });

                // 전년도(2025) 데이터 — 2026 prefix (A-1 등) → 2025 prefix (1.2 등) 변환 후 lookup.
                //   매칭 안 되는 항목 (A-1-5 사내도급 / B-2-1 신규 등) 은 null (radar 에서 라인 단절).
                const priorYearData = _sorted_category2.map(({question_name_ko, question_name}) => {
                    return this.getPriorYearScore('category2', _prefixOf(question_name_ko || question_name));
                });
                const _hasAnyPriorPoint = priorYearData.some(v => v != null);

                const _scope2_data = {
                    labels,
                    datasets: [
                        {
                            label: this.headerInfo.company_name,
                            data: _current_data,
                            borderColor: 'rgba(0, 44, 95, 0.85)',
                            backgroundColor: 'rgba(0, 44, 95, 0.12)',
                            pointBackgroundColor: 'rgba(0, 44, 95, 1)',
                            pointBorderColor: '#FFFFFF',
                            pointBorderWidth: 1.5,
                            pointRadius: 4.5,
                            pointHoverRadius: 4.5,
                            borderWidth: 2,
                            tension: 0
                        },
                        {
                            label: this.$t('전체 평균', this.report_lang),
                            data: avgBenchmark,
                            borderColor: this.hyundai_colors.muted,
                            backgroundColor: 'transparent',
                            borderDash: [4, 4],
                            pointBackgroundColor: this.hyundai_colors.muted,
                            pointBorderColor: '#FFFFFF',
                            pointBorderWidth: 1,
                            pointRadius: 3,
                            borderWidth: 1,
                            tension: 0
                        },
                        {
                            label: this.$t('Tier 평균', this.report_lang),
                            data: tierAvgBenchmark,
                            borderColor: this.hyundai_colors.teal,
                            backgroundColor: 'transparent',
                            borderDash: [4, 4],
                            pointBackgroundColor: this.hyundai_colors.teal,
                            pointBorderColor: '#FFFFFF',
                            pointBorderWidth: 1,
                            pointRadius: 3.2,
                            borderWidth: 1.2,
                            tension: 0
                        },
                        // 전년도(2025) — 매칭되는 데이터가 하나라도 있을 때만 추가. spanGaps 로 null 사이 연결 시도.
                        //   점선 처리 (현 년도와 시각적 구분).
                        ...(this.hasPriorYear && _hasAnyPriorPoint ? [{
                            label: `${this.headerInfo.company_name}(${PRIOR_YEAR_LABEL})`,
                            data: priorYearData,
                            borderColor: PRIOR_YEAR_BAR_COLOR_HEX,
                            backgroundColor: 'transparent',
                            borderDash: [4, 4],
                            pointBackgroundColor: PRIOR_YEAR_BAR_COLOR_HEX,
                            pointBorderColor: '#FFFFFF',
                            pointBorderWidth: 1,
                            pointRadius: 3.5,
                            borderWidth: 1.6,
                            spanGaps: true,
                            tension: 0
                        }] : [])
                    ]
                };


                const _common_option = {
                    global: {
                        responsive: false,
                        maintainAspectRatio: false,
                        devicePixelRatio: 1.5,
                        responsiveAnimationDuration: 0,
                        animation: {
                            duration: 0, onComplete: function () {
                                _resolveOnce(this.toBase64Image('image/jpg', 1));
                            }
                        }
                    }
                };

                const _scope2_area_option = {
                    ..._common_option.global,
                    // 라벨이 잘리지 않도록 좌우 padding 을 크게 — 차트는 그만큼 작아짐
                    layout: {
                        padding: { top: 110, bottom: 110, left: 200, right: 200 }
                    },
                    plugins: {
                        legend: {display: false},
                        datalabels: {display: false},
                    },
                    scales: {
                        r: {
                            // Chart.js 3.9.x radar scale — 20단위 동심 grid + axis 라인
                            min: 0, max: 100,
                            beginAtZero: true,
                            ticks: { stepSize: 20, display: false, showLabelBackdrop: false },
                            grid: { display: true, color: '#d8d8d8', lineWidth: 0.5, circular: false },
                            angleLines: { display: true, color: '#d8d8d8', lineWidth: 0.5 },
                            pointLabels: { display: false }
                        }
                    }
                };

                // dynamic point label offsets (radial outward) — 좌우 push 는 줄여서 캔버스 안쪽으로 유지
                // 12시/6시 zone 은 label 전체 묶음을 차트에 더 가깝게 (push 약하게)
                const _label_count = labels.length;
                const pointLabelOffsets = [];
                for (let i = 0; i < _label_count; i++) {
                    const a = (i * 2 * Math.PI) / _label_count; // 0 = 12시, clockwise
                    const cos_a = Math.cos(a);
                    let y_mag = 35;             // 기본 magnitude (측면 label)
                    if (cos_a > 0.7) y_mag = 10;     // 12시 zone (6/7축 i=0)
                    else if (cos_a < -0.7) y_mag = 6; // 6시 zone (6축 i=3 / 7축 i=3,4)
                    pointLabelOffsets.push({
                        x: Math.sin(a) * 55,
                        y: -cos_a * y_mag
                    });
                }

                if (!this.category2ContraryAreaChartInstance) {
                    this.category2ContraryAreaChartInstance = this.$chartjs.createChart(document.getElementById('category2-contrary-chart-area'), {
                        type: 'radar', data: _scope2_data, options: _scope2_area_option,
                        plugins: [{
                            id: 'customPointLabels',
                            afterDraw(chart) {
                                const {ctx, scales} = chart;
                                const rScale = scales.r;
                                const labels = rScale._pointLabels;
                                const data = chart.data.datasets[0].data;
                                const centerX = rScale.xCenter;
                                const centerY = rScale.yCenter;
                                const radius = rScale.drawingArea + 24;

                                labels.forEach((label, i) => {
                                    const angle = rScale.getIndexAngle(i) - Math.PI / 2;
                                    let x = centerX + Math.cos(angle) * radius;
                                    let y = centerY + Math.sin(angle) * radius;

                                    if (pointLabelOffsets[i]) {
                                        x += pointLabelOffsets[i].x;
                                        y += pointLabelOffsets[i].y;
                                    }

                                    ctx.save();
                                    ctx.textAlign = 'center';
                                    ctx.textBaseline = 'middle';

                                    // 값 (위, 큰 폰트)
                                    let valText = '';
                                    if (naIdx.includes(i)) {
                                        valText = 'N/A';
                                        ctx.fillStyle = '#999';
                                    } else {
                                        valText = `${data[i]}%`;
                                        ctx.fillStyle = '#002c5f';
                                    }
                                    ctx.font = `bold ${_isEn ? 11 : 12}px sans-serif`;
                                    ctx.fillText(valText, x, y - (_isEn ? 14 : 15));

                                    // 라벨 (아래)
                                    const resizedLabel = doc.splitTextToSize(label, _isEn ? 120 : 110);
                                    let labelY = y + 4;
                                    ctx.font = `bold ${_isEn ? 9.5 : 10.5}px sans-serif`;
                                    ctx.fillStyle = '#444';
                                    resizedLabel.forEach(elem => {
                                        ctx.fillText(`${elem}`, x, labelY);
                                        labelY += _isEn ? 14 : 15.5;
                                    });

                                    ctx.restore();
                                });
                            }
                        }]
                    });
                } else {
                    this.$chartjs.updateChart(this.category2ContraryAreaChartInstance, _scope2_data, _scope2_area_option);
                }

                // rAF 기반 onComplete 안전망 — 백그라운드 탭에서도 resolve 보장 (아래 chart1 설명과 동일 이유).
                setTimeout(() => {
                    if (_resolved || !this.category2ContraryAreaChartInstance) return;
                    _resolveOnce(this.category2ContraryAreaChartInstance.toBase64Image('image/jpg', 1));
                }, 1000);
            });
        },

        generateCategory1PieChartInstance(keyColor, category1_score) {
            return new Promise((resolve) => {
                let _resolved = false;
                const _resolveOnce = (_img) => {
                    if (_resolved) return;
                    _resolved = true;
                    resolve(_img);
                };
                const _scope1_data = {
                    datasets: [{
                        data: category1_score, fill: true, backgroundColor: [// 'rgba(96, 125, 139, 1)',
                            keyColor, 'rgba(236, 239, 241, 0.7)'], borderColor: [// 'rgba(96, 125, 139, 1)',
                            keyColor, 'rgba(144, 164, 174, 0.6)'],
                    }]
                };
                
                const _common_option = {
                    global: {
                        aspectRatio: 5 / 5,
                        responsive: false,
                        maintainAspectRatio: false,
                        responsiveAnimationDuration: 0,
                        animation: {
                            duration: 0, onComplete: function () {
                                _resolveOnce(this.toBase64Image('image/jpg', 1));
                            }
                        }
                    }, plugins: {
                        datalabels: {display: false},
                    }
                };
                
                const _scope1_area_option = {
                    ..._common_option.global,
                    elements: {line: {borderWidth: 1}},
                    scales: {},
                    plugins: _common_option.plugins,
                    cutout: '76%'
                };
                
                if (!this.category1PieChartInstance) {
                    this.category1PieChartInstance = this.$chartjs.createChart(document.getElementById('category1-chart-pie'), {
                        type: 'doughnut', data: _scope1_data, options: _scope1_area_option
                    });
                } else {
                    this.$chartjs.updateChart(this.category1PieChartInstance, _scope1_data, _scope1_area_option);
                }

                // rAF 기반 onComplete 안전망 — 백그라운드 탭에서도 resolve 보장 (chart1 설명과 동일 이유).
                setTimeout(() => {
                    if (_resolved || !this.category1PieChartInstance) return;
                    _resolveOnce(this.category1PieChartInstance.toBase64Image('image/jpg', 1));
                }, 1000);
            });
        }, drawCategory1PieChartImage() {
            return this.category1PieChartInstance.toBase64Image('image/jpg', 1);
        },
        async createCategory2ScorePage(doc, position_y) {
            doc.addPage();
            this.addHeader(doc, 2);
            position_y = this.firstElementSpacing;
            
            
            position_y = this.addTitle(doc, position_y, 1, this.$t('Code별 관리 수준', this.report_lang));
            
            /**
             * guide text 생성
             */
            const _category1_section_score_desc_padding = 5;
            const _category1_section_score_desc_box_position_x = this.jsPDFOpt.margin.left;
            
            const _category1_section_score_desc_area_width = this.pageContentWidth - (_category1_section_score_desc_padding * 2);
            
            this.setFont(doc, 'table_td', null, 'normal');
            doc.setFontSize(11);
            doc.setLineHeightFactor(1.55);
            const _category1_score_description = doc.splitTextToSize(this.section_score_description, _category1_section_score_desc_area_width);
            const _category1_score_desc_dim = doc.getTextDimensions(_category1_score_description);
            
            doc.setLineWidth(0.5);
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.setDrawColor(224, 224, 224);
            const _category1_score_desc_box_height = _category1_score_desc_dim.h + (_category1_section_score_desc_padding * 3);
            const _category1_score_desc_box_width = this.pageContentWidth;
            doc.rect(_category1_section_score_desc_box_position_x, position_y, _category1_score_desc_box_width, _category1_score_desc_box_height, 'FD');

            position_y += 11 - 1.55;
            const _subs_composition_area_position_x = _category1_section_score_desc_box_position_x + _category1_section_score_desc_padding; // _category1_section_score_desc_box_position_x + ((_category1_score_desc_box_width - _category1_score_desc_dim.w) / 2) + _category1_section_score_desc_padding
            const _subs_composition_area_position_y = position_y + ((_category1_score_desc_box_height - _category1_score_desc_dim.h) / 3);
            doc.text(_category1_score_description, _subs_composition_area_position_x, _subs_composition_area_position_y);
            doc.setLineHeightFactor(1.15);

            position_y += _category1_score_desc_box_height;

            /**
             * radar 차트 생성 — 차트 영역 최대화를 위해 범례는 좌상단 세로 배치 유지
             */
            const _category2_area_chart = await this.generateCategory2ContraryAreaChartInstance(doc);
            const _category2_score_box_size = this.pageContentWidth;
            const _category2_score_box_height = _category2_score_box_size * 0.5;
            const _category2_score_box_total_h = _category2_score_box_height * 1.25 + 60; // +40 (box 높이 확장)
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.rect(this.jsPDFOpt.margin.left, position_y, _category2_score_box_size, _category2_score_box_total_h, 'F');

            // 차트(radar) 전체 크기 — addImage 크기를 줄이면 radar+라벨+값이 함께 축소. 영문은 더 작게.
            const _category2_score_chart_height = (_category2_score_box_height * 1.6 + 188) * (this.report_lang === 'en' ? 0.88 : 0.94);
            const _category2_score_chart_position_x = this.jsPDFOpt.margin.left + (_category2_score_box_size - _category2_score_chart_height) / 2;
            const _category2_score_chart_position_y = position_y + ((_category2_score_box_height - _category2_score_chart_height) / 2) + 62; // +6 (y 아래로)
            doc.addImage(_category2_area_chart, 'png', _category2_score_chart_position_x, _category2_score_chart_position_y, _category2_score_chart_height, _category2_score_chart_height);

            // 범례: 좌상단 세로 (이전 버전)
            let startX = this.jsPDFOpt.margin.left + 10;
            let startY = position_y + 10;
            let lineLen = 16;
            let circleR = 2.5;
            let gapY = 9;

            // 분류 기준 변경 주석 — radar dataset 가 추가된 경우에만 legend 상단에 한 줄로 표시 (개행 없음).
            //   범례 라벨(긴 기업명)은 좌측 구간으로 wrap + 영문은 폰트도 축소해 차트 상단 라벨(100% / A-1...) 겹침 방지.
            const _legendLabelMaxW = (this.pageContentWidth * (this.report_lang === 'en' ? 0.40 : 0.42)) - (lineLen + 6);
            if (this.hasPriorYear) {
                this.setFont(doc, 'footnote', null, 'normal');
                doc.setFontSize(8);
                doc.setTextColor(PRIOR_YEAR_SUMMARY_TEXT_HEX);
                const _noteText = this.$t('분류 기준 변경으로 전년도 충족률은 단순 참고용으로만 활용 바랍니다.', this.report_lang);
                doc.text(_noteText, startX, startY);   // 한 줄 — 개행하지 않음
                doc.setTextColor('#000000');
                startY += gapY + 2;
            }

            // legend 순서: 당 사업장(solid) → 전년도(dashed) → Tier 평균(dashed) → 전체 평균(dashed)
            //   4-bar stack 순서와 동일. 당 사업장만 solid, 나머지는 점선. 긴 기업명은 자동 개행.
            const iter_data = [
                {label: this.headerInfo.company_name, color: this.hyundai_colors_rgb.blue, solid: true},
                // 전년도(2025) — radar dataset 이 추가된 경우에만 범례 행 추가.
                ...(this.hasPriorYear ? [{
                    label: `${this.headerInfo.company_name}(${PRIOR_YEAR_LABEL})`,
                    color: PRIOR_YEAR_BAR_COLOR_RGB
                }] : []),
                {label: this.$t('Tier 평균', this.report_lang), color: this.hyundai_colors_rgb.teal},
                {label: this.$t('전체 평균', this.report_lang), color: this.hyundai_colors_rgb.muted}
            ];
            for (let i = 0; i < iter_data.length; i++) {
                const {color, label, solid} = iter_data[i];
                doc.setDrawColor(...color);
                doc.setLineWidth(1.2);
                // 당 사업장만 solid, 나머지(전년도/Tier/전체) 점선
                const _isDashed = !solid;
                if (_isDashed) {
                    doc.setLineDashPattern([1, 2], 0);
                }
                // swatch(라인+circle)는 첫 줄에 정렬
                doc.line(startX, startY, startX + lineLen, startY);
                if (_isDashed) {
                    doc.setLineDashPattern([], 0);
                }
                doc.setFillColor(...color);
                doc.circle(startX + 8, startY, circleR, 'F');
                this.setFont(doc, 'table_th', null, 'bold');
                if (this.report_lang === 'en') doc.setFontSize(8.5);   // 영문 범례 폰트 축소 (겹침 완화)
                doc.setTextColor(...color);
                const _labelLines = doc.splitTextToSize(label, _legendLabelMaxW);   // 긴 기업명 자동 개행
                doc.text(_labelLines, startX + lineLen + 6, startY + 1.5);
                startY += Math.max(gapY, doc.getTextDimensions(_labelLines).h + 2);  // 개행 줄 수만큼 내려감
            }

            position_y += _category2_score_box_total_h + this.jsPDFOpt.spacing.section_xs;
            
            // PPT v0.21 — 차트 하단 category2 line chart 블록과 중간 안내 텍스트는 제거됨.
            // 다음 섹션은 category1 그룹별 ("1. 인권 및 노동", "2. 윤리") code 카드 grid.

            if (this.checkNeedNewPage(doc, position_y, 100)) {
                position_y = this.firstElementSpacing;
            }

            // ── PPT v0.21 — 단일 컬럼 카드 (LEFT: code+title / RIGHT: 3 or 4-bar stack) ──
            //   전년도(2025) 데이터 있는 경우엔 4-bar stack 으로 확장 (카드 높이도 ↑)
            let _cat3_posistion_y = position_y;
            const _card_w = this.pageContentWidth;
            const _bar_rows_count = this.hasPriorYear ? 4 : 3;
            const _card_h = this.hasPriorYear ? 50 : 40;
            const _card_left_x = this.jsPDFOpt.margin.left;
            const _card_left_w = Math.max(95, _card_w * 0.22);   // code/title 영역
            const _card_right_x = _card_left_x + _card_left_w;
            const _card_right_w = _card_w - _card_left_w;
            const _card_inner_pad = 6;
            const _bar_label_w = this.report_lang === 'ko' ? 44 : 56;
            const _bar_value_w = 30;
            const _bar_h = 6.5;
            const _bar_row_h = (_card_h - _card_inner_pad * 2) / _bar_rows_count;
            const _bar_x = _card_right_x + _bar_label_w + 2;
            const _bar_track_w = _card_right_w - _bar_label_w - _bar_value_w - 8;

            // category3_question_name (예: "A-1-1. xxx") 에서 prefix code 추출
            const _c3CodeOf = (name) => {
                const m = String(name || '').match(/^([A-Z]-\d+-\d+)/);
                return m ? m[1] : null;
            };

            const _splitCode = (full_name_with_code) => {
                const m = String(full_name_with_code).match(/^([A-Z]-\d+-\d+\.)\s*(.*)$/);
                if (m) return { code: m[1], title: m[2] };
                return { code: '', title: String(full_name_with_code) };
            };

            this.scoringInfo.category3.forEach(({category1_question_name, category3}, _section_idx) => {
                // 윤리 영역이 2개 항목(B-1-1, B-1-2) 으로 구성된 경우, 첫 섹션이 아니면 무조건 새 페이지에서 시작.
                //   인권(A) 카드가 페이지 끝에 가득 찬 상태에서 윤리 2장만 어색하게 붙는 걸 방지.
                const _isEthicsTwoItems = _section_idx > 0
                    && Array.isArray(category3) && category3.length === 2
                    && /^B[\s.-]|윤리|Ethics/i.test(String(category1_question_name || ''));
                if (_isEthicsTwoItems) {
                    doc.addPage();
                    this.addHeader(doc);
                    _cat3_posistion_y = this.firstElementSpacing;
                } else if (this.checkNeedNewPage(doc, _cat3_posistion_y, 70)) {
                    _cat3_posistion_y = this.firstElementSpacing;
                }
                // 섹션 헤더 ("1. 인권 및 노동" / "2. 윤리")
                _cat3_posistion_y = this.addTitle(doc, _cat3_posistion_y, 3, category1_question_name) + this.jsPDFOpt.spacing.title_xs;

                // 사전순 정렬 (A-1-1 → A-1-2 → … → B-2-1)
                const _sorted = [...category3].sort((a, b) => {
                    const pa = String(a.category3_question_name_kr || a.category3_question_name).match(/^([A-Z])-(\d+)-(\d+)/);
                    const pb = String(b.category3_question_name_kr || b.category3_question_name).match(/^([A-Z])-(\d+)-(\d+)/);
                    const ka = pa ? pa[1].charCodeAt(0) * 1e6 + Number(pa[2]) * 1000 + Number(pa[3]) : Infinity;
                    const kb = pb ? pb[1].charCodeAt(0) * 1e6 + Number(pb[2]) * 1000 + Number(pb[3]) : Infinity;
                    return ka - kb;
                });

                _sorted.forEach((_cat3) => {
                    const { category3_question_name, category3_question_name_kr, not_applicable, score } = _cat3;

                    // scoring 응답에 category3 매핑이 빠진 row 가 'null' 문자열/null 로 들어오는 케이스 — 카드 자체 skip
                    const _nameStr = String(category3_question_name || '').trim().toLowerCase();
                    if (!_nameStr || _nameStr === 'null' || _nameStr === '—') return;

                    let avg = null, tier_avg = null, bm_not_applicable = false;
                    const _bench = this.benchMarkScore?.category3?.[category3_question_name_kr];
                    if (_bench) {
                        avg = _bench.avg;
                        tier_avg = _bench.tier_avg ? _bench.tier_avg[this.project_code] : null;
                    } else {
                        bm_not_applicable = true;
                    }

                    // bar 순서: 당 사업장 → 전년도(있을 때) → Tier 평균 → 전체 평균
                    const _bars = [
                        { label: this.$t('당 사업장', this.report_lang), value: score, is_na: not_applicable, fillColor: this.hyundai_colors_rgb.blue }
                    ];
                    // 전년도(2025) — 2026 prefix (A-1-1 등) → 2025 prefix (1.2.1 등) 변환 후 lookup.
                    //   매핑 없는 코드 (A-1-5 사내도급 / B-2-1) 는 N/A 표시.
                    if (this.hasPriorYear) {
                        const _priorScore = this.getPriorYearScore('category3', _c3CodeOf(category3_question_name_kr || category3_question_name));
                        _bars.push({
                            label: this.$t('전년도', this.report_lang),
                            value: _priorScore,
                            is_na: _priorScore == null,
                            fillColor: PRIOR_YEAR_BAR_COLOR_RGB
                        });
                    }
                    _bars.push(
                        { label: this.$t('Tier 평균', this.report_lang), value: tier_avg, is_na: tier_avg == null, fillColor: this.hyundai_colors_rgb.teal },
                        { label: this.$t('전체 평균', this.report_lang), value: avg, is_na: avg == null || bm_not_applicable, fillColor: this.hyundai_colors_rgb.muted }
                    );

                    if (this.checkNeedNewPage(doc, _cat3_posistion_y, _card_h + 6)) {
                        _cat3_posistion_y = this.firstElementSpacing;
                    }

                    // 카드 배경 (subtle sand fill)
                    doc.setFillColor(...this.jsPDFOpt.card.fillColor);
                    doc.rect(_card_left_x, _cat3_posistion_y, _card_w, _card_h, 'F');

                    // LEFT / RIGHT 사이 divider (얇은 세로 라인)
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.4);
                    doc.line(_card_right_x, _cat3_posistion_y + 6, _card_right_x, _cat3_posistion_y + _card_h - 6);

                    // LEFT — code + title (검은색/진회색 simple style)
                    const { code, title } = _splitCode(category3_question_name);
                    const _left_x_inner = _card_left_x + _card_inner_pad;
                    this.setFont(doc, null, null, 'bold');
                    doc.setFontSize(this.report_lang === 'ko' ? 10 : 9.5);
                    doc.setTextColor(30, 30, 30);
                    doc.text(code || '—', _left_x_inner, _cat3_posistion_y + 14);

                    this.setFont(doc, null, null, 'medium');
                    doc.setFontSize(this.report_lang === 'ko' ? 9 : 8.5);
                    doc.setTextColor(70, 70, 70);
                    const _title_lines = doc.splitTextToSize(title, _card_left_w - _card_inner_pad * 2);
                    doc.text(_title_lines.slice(0, 2), _left_x_inner, _cat3_posistion_y + 25);

                    // RIGHT — 3-bar stack
                    let _bar_row_y = _cat3_posistion_y + _card_inner_pad + (_bar_row_h - _bar_h) / 2;
                    _bars.forEach((b) => {
                        // 라벨
                        this.setFont(doc, null, null, 'normal');
                        doc.setFontSize(7.5);
                        doc.setTextColor(70, 70, 70);
                        doc.text(b.label, _card_right_x + _card_inner_pad, _bar_row_y + _bar_h - 1.5);

                        // 트랙 (옅은 회색)
                        doc.setFillColor(232, 232, 232);
                        doc.rect(_bar_x, _bar_row_y, _bar_track_w, _bar_h, 'F');

                        // 채움
                        if (!b.is_na && typeof b.value === 'number') {
                            const _fill_w = Math.max(0, Math.min(1, b.value / 100)) * _bar_track_w;
                            doc.setFillColor(...b.fillColor);
                            doc.rect(_bar_x, _bar_row_y, _fill_w, _bar_h, 'F');
                        }

                        // 값
                        this.setFont(doc, null, null, 'bold');
                        doc.setFontSize(8);
                        doc.setTextColor(50, 50, 50);
                        const _val_text = b.is_na ? 'N/A' : `${this.roundToOneDecimalPlace(b.value)}%`;
                        doc.text(_val_text, _bar_x + _bar_track_w + 4, _bar_row_y + _bar_h - 1.5);

                        _bar_row_y += _bar_row_h;
                    });

                    _cat3_posistion_y += _card_h + 4;
                });

                _cat3_posistion_y += this.jsPDFOpt.spacing.section_xs;
            });

            position_y = _cat3_posistion_y + this.jsPDFOpt.spacing.section_md;
            return position_y;
        },
        // ── P5/P6 코드별 준수결과 (대분류 | 중분류 | Code | 4단계 마킹 표) ──
        // 대분류 A / B 별로 분리된 2개 테이블, 캠페인 scoring 에 존재하는 sub_code 만 노출
        createCodeCompliancePage_v2(doc, position_y) {
            doc.addPage();
            this.addHeader(doc);
            position_y = this.firstElementSpacing;
            position_y = this.addTitle(doc, position_y, 1, this.$t('Code별 준수결과', this.report_lang));

            const config = this.codeLevelThresholds;
            const codeLevelMap = this.codeLevelByCode;

            // 1. scoring 에 존재하는 sub_code (A-X-Y) 집합 — 캠페인에 없는 항목 제외
            const _present = new Set();
            for (const row of (this.scoring?.category3_scoring_pd || [])) {
                const m = String(row.category3_question_name || '').match(/^([A-Z]-\d+-\d+)/);
                if (m) _present.add(m[1]);
            }

            const _sortFn = (a, b) => {
                const ma = a.match(/^([A-Z])-(\d+)-(\d+)/);
                const mb = b.match(/^([A-Z])-(\d+)-(\d+)/);
                if (!ma || !mb) return 0;
                return (ma[1].charCodeAt(0) * 1e6 + Number(ma[2]) * 1000 + Number(ma[3]))
                     - (mb[1].charCodeAt(0) * 1e6 + Number(mb[2]) * 1000 + Number(mb[3]));
            };
            const allSubCodes = Object.keys(config).filter(c => _present.has(c)).sort(_sortFn);

            // 2. 대분류별 그룹
            const codesByCat1 = { A: [], B: [] };
            for (const c of allSubCodes) {
                const k = c.split('-')[0];
                if (codesByCat1[k]) codesByCat1[k].push(c);
            }

            // 3. 통합 TOTAL (마지막 테이블 footer 에 1회만 노출)
            const totalCounts = { major: 0, minor: 0, conformance: 0, excellent: 0, na: 0 };
            for (const c of allSubCodes) {
                const lv = codeLevelMap[c] || 'na';
                totalCounts[lv] = (totalCounts[lv] || 0) + 1;
            }

            const levelAccentRGB = {
                major: [183, 65, 56],
                minor: [196, 145, 100],
                conformance: [108, 130, 150],
                excellent: [110, 140, 95]
            };
            const levelLabels = {
                ko: { major: '매우 미흡', minor: '미흡', conformance: '보통', excellent: '우수' },
                en: { major: 'Major', minor: 'Minor', conformance: 'Conformance', excellent: 'Excellent' },
                ca: { major: '严重不足', minor: '不足', conformance: '一般', excellent: '优秀' }
            };
            const labels = levelLabels[this.report_lang] || levelLabels.ko;

            const _header_fill = '#f5f2f2';
            const _header_text = '#2a2a2a';
            const _category_fill = '#ffffff';
            const _na_color = '#dddddd';
            // en/ca 는 'Conformance'/'Excellent' 등 단어가 길어 좁은 4-level 컬럼에서 wrap.
            // 헤더만 한 단계 작은 fontSize 적용 + 컬럼 width 도 lang 별로 키움.
            const thStyle = {
                ...this.tableStyles.th,
                fontSize: this.report_lang === 'ko' ? 10 : 8,
                fillColor: _header_fill,
                textColor: _header_text,
                lineColor: '#d8d8d8',
                fontStyle: 'bold',
                halign: 'center'
            };
            const totalStyle = {
                ...this.tableStyles.td,
                fontSize: 10,
                fontStyle: 'bold',
                halign: 'center',
                valign: 'middle',
                fillColor: _header_fill,
                textColor: _header_text,
                lineColor: '#d8d8d8'
            };

            const tableHead = [[
                { content: this.$t('대분류', this.report_lang), styles: thStyle },
                { content: this.$t('중분류', this.report_lang), styles: thStyle },
                { content: this.$t('Code', this.report_lang), colSpan: 2, styles: { ...thStyle, halign: 'center' } },
                { content: labels.major, styles: thStyle },
                { content: labels.minor, styles: thStyle },
                { content: labels.conformance, styles: thStyle },
                { content: labels.excellent, styles: thStyle }
            ]];
            // lang 분기 — en 은 description 이 길어 row height 가 커지면 cat1 rowSpan 이 한 페이지를
            // 못 채워 표 시작이 다음 페이지로 밀려나면서 빈 페이지가 생김. 따라서 en 의 description(auto)
            // 폭이 ko 와 유사하게 유지되도록 다른 컬럼을 좁힘. marker 는 약간 키워 'Conformance' 가
            // 자연스럽게 2줄 wrap 되게 유도.
            const _isKo = this.report_lang === 'ko';
            const _markerW = _isKo ? 22 : 28;
            const _columnStyles = {
                0: { cellWidth: _isKo ? 30 : 36 },   // 대분류 (Category)
                1: { cellWidth: _isKo ? 52 : 50 },   // 중분류
                2: { cellWidth: _isKo ? 76 : 64 },   // Code (좌)
                3: { cellWidth: 'auto' },             // Code 설명
                4: { cellWidth: _markerW },           // Major
                5: { cellWidth: _markerW },           // Minor
                6: { cellWidth: _markerW },           // Conformance (en 28 → 2줄 wrap)
                7: { cellWidth: _markerW }            // Excellent
            };

            const _cat1NameFromRow = (row) => {
                if (!row) return null;
                if (this.report_lang === 'en') return row.category1_question_name_ea || row.category1_question_name;
                if (this.report_lang === 'ca') return row.category1_question_name_ca || row.category1_question_name;
                return row.category1_question_name;
            };
            const _cat2NameFromRow = (row) => {
                if (!row) return null;
                if (this.report_lang === 'en') return row.category2_question_name_ea || row.category2_question_name;
                if (this.report_lang === 'ca') return row.category2_question_name_ca || row.category2_question_name;
                return row.category2_question_name;
            };

            // 단일 대분류 테이블 body 생성 헬퍼
            const _buildBody = (subCodes, includeTotal) => {
                const body = [];
                let prevCat1Code = null;
                let prevCat2Code = null;
                const levelKeys = ['major', 'minor', 'conformance', 'excellent'];

                subCodes.forEach((subCode) => {
                    const level = codeLevelMap[subCode] || 'na';
                    const parts = subCode.split('-');
                    const cat1Code = parts[0];
                    const cat2Code = `${parts[0]}-${parts[1]}`;

                    const cat1Row = (this.scoring?.category1_scoring_pd || []).find(c =>
                        String(c.category1_question_name || '').split('.')[0].trim() === cat1Code);
                    const cat2Row = (this.scoring?.category2_scoring_pd || []).find(c =>
                        String(c.category2_question_name || '').split('.')[0].trim() === cat2Code);

                    const row = [];
                    if (prevCat1Code !== cat1Code) {
                        const rowSpan = subCodes.filter(sc => sc.startsWith(cat1Code + '-')).length;
                        row.push({
                            content: _cat1NameFromRow(cat1Row) || cat1Code,
                            rowSpan,
                            styles: {
                                ...this.tableStyles.td,
                                fontSize: 10,
                                fontStyle: 'bold',
                                halign: 'center',
                                valign: 'middle',
                                fillColor: _category_fill
                            }
                        });
                        prevCat1Code = cat1Code;
                        prevCat2Code = null;
                    }
                    if (prevCat2Code !== cat2Code) {
                        const rowSpan = subCodes.filter(sc => sc.startsWith(cat2Code + '-')).length;
                        row.push({
                            content: _cat2NameFromRow(cat2Row) || cat2Code,
                            rowSpan,
                            styles: {
                                ...this.tableStyles.td,
                                fontSize: 9,
                                fontStyle: 'semibold',
                                halign: 'left',
                                valign: 'middle'
                            }
                        });
                        prevCat2Code = cat2Code;
                    }

                    // Code (좌) — lang 별 codeName (codeLocalized 우선, ko fallback)
                    const codeName = this.getLocalizedCodeName(subCode);
                    row.push({
                        content: `${subCode}. ${codeName}`,
                        styles: { ...this.tableStyles.td, fontSize: 8.5, fontStyle: 'semibold', halign: 'left', valign: 'middle', minCellHeight: 18 }
                    });

                    // Code 설명 (우)
                    const _descBundle = this.codeDescriptions[subCode] || {};
                    const _descText = _descBundle[this.report_lang] || _descBundle.ko || '';
                    row.push({
                        content: _descText,
                        styles: { ...this.tableStyles.td, fontSize: 7.5, halign: 'left', valign: 'middle', textColor: '#404040', minCellHeight: 18 }
                    });

                    // 4 단계 마킹 셀
                    levelKeys.forEach((lk) => {
                        if (level === 'na') {
                            row.push({ content: '', styles: { ...this.tableStyles.td, halign: 'center', fillColor: _na_color } });
                        } else {
                            row.push({
                                content: '',
                                styles: { ...this.tableStyles.td, halign: 'center', fillColor: level === lk ? levelAccentRGB[lk] : '#ffffff' }
                            });
                        }
                    });

                    body.push(row);
                });

                if (includeTotal) {
                    body.push([
                        { content: this.$t('TOTAL', this.report_lang), colSpan: 4, styles: totalStyle },
                        { content: String(totalCounts.major), styles: totalStyle },
                        { content: String(totalCounts.minor), styles: totalStyle },
                        { content: String(totalCounts.conformance), styles: totalStyle },
                        { content: String(totalCounts.excellent), styles: totalStyle }
                    ]);
                }
                return body;
            };

            // 4. 대분류별 테이블 렌더 — A → B, 마지막 비어있지 않은 테이블에만 TOTAL 첨부
            //    A 가 끝나면 B 는 새 페이지에서 시작
            const cat1Order = ['A', 'B'];
            // 페이지 분할 규칙 (사용자 정의):
            //   ko/ca:
            //     A cat2 == 4 → A+B 를 단일 표로 합쳐서 한 페이지
            //     A cat2 == 5 → A 표 / 별도 페이지에 B 표
            //   en:
            //     항상 A 의 첫 3 cat2 (A-1, A-2, A-3) → 페이지1
            //              나머지 cat2 + B           → 페이지2
            //     (en 은 description wrap 으로 cat1 rowSpan height 가 페이지 초과 → 빈 페이지 발생.
            //      A 를 두 그룹으로 분리해 rowSpan 을 줄여 회피.)
            const _isEn = this.report_lang === 'en';
            const _cat2Of = (subCode) => subCode.split('-').slice(0, 2).join('-');
            const _aCat2Count = codesByCat1.A
                ? new Set(codesByCat1.A.map(_cat2Of)).size
                : 0;
            const _hasA = codesByCat1.A && codesByCat1.A.length > 0;
            const _hasB = codesByCat1.B && codesByCat1.B.length > 0;

            const groups = []; // { codes:[], newPage:bool }
            if (!_isEn && _aCat2Count === 4 && _hasA && _hasB) {
                // ko/ca + cat2==4: A+B 한 표
                groups.push({ codes: [...codesByCat1.A, ...codesByCat1.B], newPage: false });
            } else if (_isEn && _hasA) {
                // en: A 첫 3 cat2 | 나머지 + B
                const cat2List = [...new Set(codesByCat1.A.map(_cat2Of))].sort();
                const firstThree = new Set(cat2List.slice(0, 3));
                const aFirst = codesByCat1.A.filter(c => firstThree.has(_cat2Of(c)));
                const aRest  = codesByCat1.A.filter(c => !firstThree.has(_cat2Of(c)));
                if (aFirst.length) groups.push({ codes: aFirst, newPage: false });
                const mergedSecond = [...aRest, ...(_hasB ? codesByCat1.B : [])];
                if (mergedSecond.length) groups.push({ codes: mergedSecond, newPage: true });
            } else {
                // ko/ca + cat2==5 또는 B 만/A 만 케이스
                if (_hasA) groups.push({ codes: codesByCat1.A, newPage: false });
                if (_hasB) groups.push({ codes: codesByCat1.B, newPage: true });
            }
            const _lastGroupIdx = groups.length - 1;
            const _gap = 14;

            for (let gIdx = 0; gIdx < groups.length; gIdx++) {
                const g = groups[gIdx];
                if (!g.codes || !g.codes.length) continue;

                if (gIdx > 0) {
                    if (g.newPage) {
                        doc.addPage();
                        this.addHeader(doc);
                        position_y = this.firstElementSpacing;
                    } else {
                        position_y += _gap;
                    }
                }

                const body = _buildBody(g.codes, gIdx === _lastGroupIdx);

                autoTable(doc, {
                    theme: 'grid',
                    startY: position_y,
                    margin: { ...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5 },
                    tableWidth: this.pageContentWidth,
                    pageBreak: 'auto',
                    // en 처럼 description 행이 길어 cat1(=Category) rowSpan cell 이 한 페이지 안에
                    // 들어가지 못하는 케이스에서 'avoid' 면 표 시작 자체를 다음 페이지로 미뤄 빈 페이지가 남음.
                    // 'auto' 로 두면 row 가 페이지 걸쳐 그려지는 대신 빈 페이지는 없어짐.
                    rowPageBreak: 'auto',
                    showHead: 'everyPage',
                    styles: { ...this.tableStyles.global, lineWidth: 0.4, lineColor: '#cccccc' },
                    columnStyles: _columnStyles,
                    head: tableHead,
                    body,
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    }
                });

                position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            }

            // N/A 범례 (우상단)
            this.setFont(doc, 'footnote', null, 'normal');
            doc.setFontSize(8.5);
            doc.setTextColor(100, 100, 100);
            const _na_label = `: ${this.$t('해당 없음', this.report_lang)}`;
            const _na_label_dim = doc.getTextDimensions(_na_label);
            const _swatch_w = 14;
            const _legend_total_w = _swatch_w + 2 + _na_label_dim.w;
            const _legend_x = this.jsPDFOpt.margin.left + this.pageContentWidth - _legend_total_w;
            const _legend_y = position_y - 3; // 기존 +5 에서 8px 위로 이동
            doc.setFillColor(_na_color);
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.3);
            doc.rect(_legend_x, _legend_y - 5, _swatch_w, 7, 'FD');
            doc.text(_na_label, _legend_x + _swatch_w + 2, _legend_y);

            return position_y + this.jsPDFOpt.spacing.section_xs + 8;
        },
        // ── P7~ Code Detail 페이지 (2 코드 / 페이지) ──
        // PPT v0.21 page 7 레이아웃: 네이비 상단(코드+설명) + 4단계 탭 + 답변 박스 + 레벨 배지+설명
        createCodeDetailPages_v2(doc, position_y) {
            const _present = new Set();
            for (const row of (this.scoring?.category3_scoring_pd || [])) {
                const m = String(row.category3_question_name || '').match(/^([A-Z]-\d+-\d+)/);
                if (m) _present.add(m[1]);
            }
            const config = this.codeLevelThresholds;
            const _sortFn = (a, b) => {
                const ma = a.match(/^([A-Z])-(\d+)-(\d+)/);
                const mb = b.match(/^([A-Z])-(\d+)-(\d+)/);
                if (!ma || !mb) return 0;
                return (ma[1].charCodeAt(0) * 1e6 + Number(ma[2]) * 1000 + Number(ma[3]))
                     - (mb[1].charCodeAt(0) * 1e6 + Number(mb[2]) * 1000 + Number(mb[3]));
            };
            const subCodes = Object.keys(config).filter(c => _present.has(c)).sort(_sortFn);
            if (!subCodes.length) return position_y;

            for (let i = 0; i < subCodes.length; i += 2) {
                doc.addPage();
                this.addHeader(doc);
                let py = this.firstElementSpacing;
                py = this.addTitle(doc, py, 1, this.$t('Code별 평가 결과', this.report_lang));

                py = this.renderCodeDetailBlock_v2(doc, py, subCodes[i]);
                py += 12;
                if (subCodes[i + 1]) {
                    py = this.renderCodeDetailBlock_v2(doc, py, subCodes[i + 1]);
                }
            }
            return position_y;
        },
        renderCodeDetailBlock_v2(doc, position_y, codeId) {
            const config = this.codeLevelThresholds;
            const codeLevelMap = this.codeLevelByCode;
            const codeName = this.getLocalizedCodeName(codeId);
            const descBundle = this.codeDescriptions[codeId] || {};
            const descText = descBundle[this.report_lang] || descBundle.ko || '';
            const level = codeLevelMap[codeId] || 'na';

            const leftX = this.jsPDFOpt.margin.left;
            const fullW = this.pageContentWidth;

            const levelKeys = ['major', 'minor', 'conformance', 'excellent'];
            const levelLabels = {
                ko: { major: '매우 미흡', minor: '미흡', conformance: '보통', excellent: '우수', na: '해당 없음' },
                en: { major: 'Major', minor: 'Minor', conformance: 'Conformance', excellent: 'Excellent', na: 'N/A' },
                ca: { major: '严重不足', minor: '不足', conformance: '一般', excellent: '优秀', na: '不适用' }
            };
            const accentRGB = {
                major: [183, 65, 56],
                minor: [196, 145, 100],
                conformance: [108, 130, 150],
                excellent: [110, 140, 95],
                na: [180, 180, 180]
            };
            const labels = levelLabels[this.report_lang] || levelLabels.ko;

            // ── Section 1: 헤더 (light sand bg / 좌 코드+이름 bold / 우 dark grey) ──
            //   carrier dimension 으로 height 계산 → locale 분기 없이 텍스트 길이에 자동 fit.
            const hdrLeftW = 220;     // 영문 코드 라벨도 한 줄에 들어가는 폭으로 통일
            const hdrRightW = fullW - hdrLeftW;
            const hdrPadV = 9;        // top + bottom 동일 padding
            const hdrPadX = 12;       // 좌우 내부 padding

            const codeLabel = `${codeId}.  ${codeName}`;

            // (1) 좌측 코드 라벨 wrap + 실측 — bold 12.5pt, 기본 lineHeightFactor 1.15
            this.setFont(doc, null, null, 'bold');
            doc.setFontSize(12.5);
            const codeLabelLines = doc.splitTextToSize(codeLabel, hdrLeftW - hdrPadX * 2).slice(0, 3);
            const codeLabelH = doc.getTextDimensions(codeLabelLines, { lineHeightFactor: 1.15 }).h;

            // (2) 우측 설명 wrap + 실측 — normal 10pt, lineHeightFactor 1.4 (doc.text 호출 시 동일)
            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(10);
            const descLines = doc.splitTextToSize(descText, hdrRightW - hdrPadX * 2).slice(0, 4);
            const descH = doc.getTextDimensions(descLines, { lineHeightFactor: 1.4 }).h;

            // (3) 카드 height = 더 큰 텍스트 block + padding × 2
            const hdrH = Math.max(codeLabelH, descH) + hdrPadV * 2;

            // (4) 배경 + 가운데 divider
            doc.setFillColor(245, 242, 242);
            doc.rect(leftX, position_y, fullW, hdrH, 'F');
            doc.setDrawColor(216, 216, 216);
            doc.setLineWidth(0.4);
            doc.line(leftX + hdrLeftW, position_y + 7, leftX + hdrLeftW, position_y + hdrH - 7);

            // (5) 좌측 텍스트 — vertical center
            this.setFont(doc, null, null, 'bold');
            doc.setFontSize(12.5);
            doc.setTextColor(40, 40, 40);
            doc.text(codeLabelLines, leftX + hdrPadX, position_y + (hdrH - codeLabelH) / 2, { baseline: 'top' });

            // (6) 우측 텍스트 — vertical center (lineHeightFactor 1.4 적용)
            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(10);
            doc.setTextColor(64, 64, 64);
            doc.setLineHeightFactor(1.4);
            doc.text(descLines, leftX + hdrLeftW + hdrPadX, position_y + (hdrH - descH) / 2, { baseline: 'top' });
            doc.setLineHeightFactor(1.15);

            position_y += hdrH + 6;

            // ── Section 2: 4단계 탭 ──
            const tabsH = 17;
            const tabW = fullW / 4;
            levelKeys.forEach((lk, idx) => {
                const tx = leftX + idx * tabW;
                const isCurrent = level === lk;
                const isNA = level === 'na';
                if (isCurrent && !isNA) {
                    doc.setFillColor(...accentRGB[lk]);
                    doc.rect(tx, position_y, tabW, tabsH, 'F');
                    this.setFont(doc, null, null, 'bold');
                    doc.setTextColor(255, 255, 255);
                } else {
                    doc.setFillColor(255, 255, 255);
                    doc.rect(tx, position_y, tabW, tabsH, 'F');
                    this.setFont(doc, null, null, 'medium');
                    doc.setTextColor(150, 150, 150);
                }
                doc.setFontSize(11);
                doc.text(labels[lk], tx + tabW / 2, position_y + 11.5, { align: 'center' });
            });
            // 탭 row 외곽 rect (좌/우/상/하)
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.4);
            doc.rect(leftX, position_y, fullW, tabsH, 'D');
            // 탭 사이 vertical hairline divider (3 boundaries)
            for (let i = 1; i < 4; i++) {
                const dx = leftX + i * tabW;
                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.4);
                doc.line(dx, position_y + 3, dx, position_y + tabsH - 3);
            }
            position_y += tabsH;

            // ── Section 3: 답변 bullets + 레벨/개선권고 (분리된 카드 구조) ──
            // 변경 (2026-05-22):
            //   - 기존: 단일 카드 (bullets + level chip + level desc)
            //   - 신규: 상단 bullets 카드 + 하단 2-card row (좌: 매우미흡/미흡 수준, 우: 개선 권고)
            //   - major/minor 가 아니거나 adviceText 가 빈 경우 하단은 단일 full-width 카드 (기존 패턴 유지)
            const evNames = this.collectEvNamesForCodeDetail_v2(codeId);
            const levelDescText = this.getLocalizedLevelMessage(codeId, level);
            const adviceText = this.getLocalizedLevelAdvice(codeId, level);

            // 카드 분리 여부: major/minor 등급이면서 개선권고 텍스트가 있을 때만 2-card
            const splitCards = (level === 'major' || level === 'minor') && !!adviceText;

            // ── Top: 답변 bullets 카드 (full-width) ──
            // ── bullet 타이포 (한 곳에서 조절) ──
            const _BF = 9.5;        // 본문/prefix 폰트
            const _BGAP = 5;        // bullet 간 간격 (실측 텍스트 높이에 더함)
            const _SF = 8;          // 내역 하위 폰트
            const _SLHF = 1.25;     // 내역 하위 줄 간격 factor
            const _SUB_GAP = 3;     // prefix ↔ 첫 하위 간격
            const _topPad = 8;
            const _bottomPad = 3;
            let bulletsAreaH = _topPad + _bottomPad;
            const _bulletLineMetrics = []; // [{lines, height}]
            if (evNames.length === 0) {
                bulletsAreaH = 28;
            } else {
                this.setFont(doc, null, null, 'normal');
                doc.setFontSize(_BF);
                for (const name of evNames) {
                    if (String(name).indexOf('\n') >= 0) {
                        // 병합 bullet: prefix(_BF) + 내역 하위리스트(_SF, 계층감). 높이 실측.
                        const _parts = String(name).split('\n');
                        doc.setFontSize(_BF);
                        const prefixLines = doc.splitTextToSize(_parts[0], fullW - 36);
                        const prefixH = doc.getTextDimensions(prefixLines).h;
                        doc.setFontSize(_SF);
                        doc.setLineHeightFactor(_SLHF);
                        let detailLines = [];
                        for (const d of _parts.slice(1)) {
                            detailLines = detailLines.concat(doc.splitTextToSize(d.trim(), fullW - 46));
                        }
                        const detailH = detailLines.length ? doc.getTextDimensions(detailLines).h : 0;
                        doc.setLineHeightFactor(1.15);
                        doc.setFontSize(_BF);
                        const h = prefixH + (detailLines.length ? _SUB_GAP + detailH : 0) + _BGAP;
                        _bulletLineMetrics.push({ merged: true, prefixLines, prefixH, detailLines, height: h });
                        bulletsAreaH += h;
                    } else {
                        const lines = doc.splitTextToSize(name, fullW - 36).slice(0, 2);
                        const h = doc.getTextDimensions(lines).h + _BGAP;   // 실측 + 간격 (wrap 과대계산 방지)
                        _bulletLineMetrics.push({ lines, height: h });
                        bulletsAreaH += h;
                    }
                }
            }

            doc.setFillColor(252, 252, 252);
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.4);
            doc.rect(leftX, position_y, fullW, bulletsAreaH, 'FD');

            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(_BF);
            doc.setTextColor(64, 64, 64);
            if (evNames.length === 0) {
                doc.setTextColor(160, 160, 160);
                doc.text('-', leftX + fullW / 2, position_y + bulletsAreaH / 2 + 1, { align: 'center' });
            } else {
                let bulletY = position_y + _topPad;   // baseline:'top' → mt = _topPad (상/하 대칭)
                for (const m of _bulletLineMetrics) {
                    doc.setFillColor(...accentRGB[level]);
                    doc.circle(leftX + 14, bulletY + 3.5, 1.3, 'F');   // 첫 줄 세로 중앙
                    if (m.merged) {
                        // prefix(_BF, 진한색) + 내역 하위리스트(_SF, 옅은색 + 더 들여쓰기)
                        doc.setFontSize(_BF);
                        doc.setTextColor(64, 64, 64);
                        doc.text(m.prefixLines, leftX + 22, bulletY, { baseline: 'top' });
                        if (m.detailLines.length) {
                            doc.setFontSize(_SF);
                            doc.setLineHeightFactor(_SLHF);
                            doc.setTextColor(120, 120, 120);
                            doc.text(m.detailLines, leftX + 28, bulletY + m.prefixH + _SUB_GAP, { baseline: 'top' });
                            doc.setLineHeightFactor(1.15);
                            doc.setFontSize(_BF);
                        }
                    } else {
                        doc.setTextColor(64, 64, 64);
                        doc.text(m.lines, leftX + 22, bulletY, { baseline: 'top' });
                    }
                    bulletY += m.height;
                }
            }
            position_y += bulletsAreaH;

            // ── Bottom: 레벨 chip + 설명 카드(들) ──
            // 단일 또는 2-card 분기. 라인 wrap 폭은 카드 폭에 맞춰 다르게 계산.
            const _cardInnerPadX = 14;
            const _cardGap = 8;
            const _cardSpacerTop = 8; // bullets 카드와 level 카드 사이 간격

            // 카드별 내부 폭
            const halfCardW = (fullW - _cardGap) / 2;
            const _wrapW = splitCards ? (halfCardW - _cardInnerPadX * 2) : (fullW - 32);

            // 레벨 설명 wrap (11pt) — 카드 분리 시 좁아진 폭에 맞춰 다시 split
            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(11);
            const lvDescLines = levelDescText
                ? doc.splitTextToSize(levelDescText, _wrapW).slice(0, 6)
                : [];
            const adviceLines = splitCards
                ? doc.splitTextToSize(adviceText, _wrapW).slice(0, 6)
                : [];

            // 어느 한쪽이라도 내용이 있을 때만 하단 row 렌더
            if (lvDescLines.length || adviceLines.length) {
                position_y += _cardSpacerTop;

                const chipH = 15;
                const chipPadX = 13;
                const _cardTopPad = 8;
                const _cardChipDescGap = 11;   // chip ↔ 설명 간격 (baseline:top 이라 실측 간격과 동일)
                const _cardBottomPad = 8;       // mt(_cardTopPad)와 동일 → 상/하 대칭

                // 카드 height 는 실제 텍스트 block dimensions(lineHeightFactor 1.3 적용)으로 측정 —
                //   lines.length × 추정 lineH 보다 정확해서 빈 공간 최소화.
                //   doc.text() 의 setLineHeightFactor(1.3) 와 동일 factor 로 getTextDimensions 호출.
                this.setFont(doc, null, null, 'medium');
                doc.setFontSize(11);
                const _measureH = (lines) => lines.length
                    ? doc.getTextDimensions(lines, { lineHeightFactor: 1.3 }).h
                    : 0;
                const lvDescTextH = _measureH(lvDescLines);
                const adviceTextH = splitCards ? _measureH(adviceLines) : 0;

                // 카드별 높이 = 패딩 + chip + gap + 실제 텍스트 block height
                const _heightFor = (textH) => textH > 0
                    ? (_cardTopPad + chipH + _cardChipDescGap + textH + _cardBottomPad)
                    : 0;
                const leftCardH = _heightFor(lvDescTextH);
                const rightCardH = _heightFor(adviceTextH);
                const cardRowH = Math.max(leftCardH, rightCardH);   // 좌/우 카드 통일 (디자인 일관성)

                // 카드 폭/위치
                const leftCardX = leftX;
                const leftCardW = splitCards ? halfCardW : fullW;
                const rightCardX = leftX + halfCardW + _cardGap;

                // ── 좌측 카드: 매우미흡/미흡 수준 (또는 단일 카드일 때 현재 등급) ──
                doc.setFillColor(252, 252, 252);
                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.4);
                doc.rect(leftCardX, position_y, leftCardW, cardRowH, 'FD');

                this.setFont(doc, null, null, 'bold');
                doc.setFontSize(11.5);
                const chipText = `${labels[level] || labels.na} ${this.$t('수준', this.report_lang)}`;
                const chipDim = doc.getTextDimensions(chipText);
                const chipW = chipDim.w + chipPadX * 2;
                const chipX = leftCardX + _cardInnerPadX;
                const chipY = position_y + _cardTopPad;
                const mutedBorder = accentRGB[level].map(c => Math.round(c + (255 - c) * 0.35));
                doc.setFillColor(...accentRGB[level]);
                doc.setDrawColor(...mutedBorder);
                doc.setLineWidth(2);
                doc.roundedRect(chipX, chipY, chipW, chipH, 3.5, 3.5, 'FD');
                doc.setTextColor(255, 255, 255);
                doc.text(chipText, chipX + chipPadX, chipY + 10);

                this.setFont(doc, null, null, 'medium');
                doc.setFontSize(11);
                doc.setTextColor(55, 55, 55);
                doc.setLineHeightFactor(1.3);
                doc.text(lvDescLines, leftCardX + _cardInnerPadX, chipY + chipH + _cardChipDescGap, { baseline: 'top' });
                doc.setLineHeightFactor(1.15);

                // ── 우측 카드: 개선 권고 (split 모드에서만) ──
                if (splitCards) {
                    doc.setFillColor(252, 252, 252);
                    doc.setDrawColor(220, 220, 220);
                    doc.setLineWidth(0.4);
                    doc.rect(rightCardX, position_y, halfCardW, cardRowH, 'FD');

                    this.setFont(doc, null, null, 'bold');
                    doc.setFontSize(11.5);
                    const adviceChipText = this.$t('개선 권고', this.report_lang) || '개선 권고';
                    const adviceChipDim = doc.getTextDimensions(adviceChipText);
                    const adviceChipW = adviceChipDim.w + chipPadX * 2;
                    const adviceChipX = rightCardX + _cardInnerPadX;
                    const adviceChipY = position_y + _cardTopPad;
                    const adviceFill = this.hyundai_colors_rgb.blue;
                    const adviceBorder = adviceFill.map(c => Math.round(c + (255 - c) * 0.35));
                    doc.setFillColor(...adviceFill);
                    doc.setDrawColor(...adviceBorder);
                    doc.setLineWidth(2);
                    doc.roundedRect(adviceChipX, adviceChipY, adviceChipW, chipH, 3.5, 3.5, 'FD');
                    doc.setTextColor(255, 255, 255);
                    doc.text(adviceChipText, adviceChipX + chipPadX, adviceChipY + 10);

                    this.setFont(doc, null, null, 'medium');
                    doc.setFontSize(11);
                    doc.setTextColor(55, 55, 55);
                    doc.setLineHeightFactor(1.3);
                    doc.text(adviceLines, rightCardX + _cardInnerPadX, adviceChipY + chipH + _cardChipDescGap, { baseline: 'top' });
                    doc.setLineHeightFactor(1.15);
                }

                position_y += cardRowH;
            }

            return position_y;
        },
        // ────────────────────────────────────────────────────────────────
        // P8 — Core 지표 충족 여부 (Excel CORE 11문항)
        //   - 상단 설명 텍스트 + 1개 autoTable (11 rows × 6 cols)
        //   - 동일 category3_code (예: A-1-2 의 Q1.13 / Q1.14) 는 Code 셀 rowSpan
        //   - 충족: ● 회색 텍스트 / 미충족: ● 빨강 + 비고 셀에 risk 문구 + 좌측 stripe
        //   - 데이터 없음(satisfied=null): "-" 표기
        // ────────────────────────────────────────────────────────────────
        // 충족 여부 셀 — ● dot + label 을 셀 '중앙'에 그룹으로 정렬해 직접 렌더한다. (언어 무관)
        //   autoTable 기본 텍스트는 willDrawCell 에서 비우고(data.cell.text=[]) 여기서 그림.
        //   방식: 그룹폭(dot 지름 + gap + label폭)을 셀 가운데 정렬 → dot 먼저, 그 우측 gap 뒤에 label.
        //         (= label 을 중앙에서 dot폭/2+gap/2 만큼 우측으로 민 뒤, 그 좌측에 dot → 둘이 중앙)
        //   Core 표 / 문항별 답변 표 공통.
        _drawCoreDotLabel(doc, cell) {
            const raw = cell.raw;
            if (!raw || typeof raw !== 'object' || !raw._coreShowDot) return;
            const label = String(raw.content ?? '');
            const dotColor = raw._coreStatusColor || [120, 120, 120];
            // 폰트(CJK fallback) / 크기 적용 — getTextWidth 측정·렌더 직전 일치시킴
            const fontStyle = cell.styles.fontStyle;
            if (this.isChineseOrHanzi(label)) {
                this.setFont(doc, fontStyle, this.jsPDFOpt.fontFamily_ca);
            } else {
                this.setFont(doc, fontStyle, this.jsPDFOpt.fontFamily);
            }
            const fs = cell.styles.fontSize;
            if (fs != null) doc.setFontSize(fs);
            const _dotR = 1.7;
            const _gap = 3.5;                                   // dot ↔ label 간격
            const _labelW = label ? doc.getTextWidth(label) : 0;
            const _groupW = _dotR * 2 + _gap + _labelW;
            const _groupLeft = cell.x + (cell.width - _groupW) / 2;
            const _cy = cell.y + cell.height / 2;
            doc.setFillColor(dotColor[0], dotColor[1], dotColor[2]);
            doc.circle(_groupLeft + _dotR, _cy, _dotR, 'F');
            const tc = cell.styles.textColor;
            if (Array.isArray(tc)) doc.setTextColor(tc[0], tc[1], tc[2]);
            else if (tc != null) doc.setTextColor(tc);
            if (label) doc.text(label, _groupLeft + _dotR * 2 + _gap, _cy, { baseline: 'middle' });
        },
        createCorePage_v2(doc, position_y) {
            doc.addPage();
            this.addHeader(doc);
            position_y = this.firstElementSpacing;
            position_y = this.addTitle(doc, position_y, 1, this.$t('Core 지표 충족 여부', this.report_lang));

            const indicators = this.coreRiskIndicators || [];
            if (!indicators.length) return position_y;

            const statusMap = this.coreIndicatorStatusByDbCode;
            const lang = this.report_lang;
            const _t = (bundle) => (bundle && (bundle[lang] || bundle.ko)) || '';

            // ── 상단 설명 박스 (light sand bg, 짧은 paragraph) ──
            const leftX = this.jsPDFOpt.margin.left;
            const fullW = this.pageContentWidth;
            const introText = this.$t(
                'Core 지표는 조직 운영의 근간이 되는 핵심 척도로서, 미충족 시 잠재적 고위험(High Risk) 요인이 상존하는 것으로 간주되므로 전사적인 차원에서 엄격하고 체계적인 관리를 이행하여야 합니다.',
                lang
            );
            this.setFont(doc, null, null, 'medium');
            doc.setFontSize(10.5);
            doc.setTextColor(64, 64, 64);
            doc.setLineHeightFactor(1.45);
            const introLines = doc.splitTextToSize(introText, fullW - 24);
            const introPadY = 10;
            const introBoxH = introPadY * 2 + introLines.length * 12;
            doc.setFillColor(245, 242, 242);
            doc.rect(leftX, position_y, fullW, introBoxH, 'F');
            doc.text(introLines, leftX + 12, position_y + introPadY + 9);
            doc.setLineHeightFactor(1.15);
            position_y += introBoxH + 14;

            // ── 표 ──
            // ESG 평가 표 표준 페어 — 영문은 'Met'/'Unmet' (audit·평가 도메인 통용, wrap 회피)
            const labels = {
                ko: { satisfied: '충족', not_satisfied: '미충족', na: '해당 없음', none: '-' },
                en: { satisfied: 'Met',  not_satisfied: 'Unmet',  na: 'N/A',     none: '-' },
                ca: { satisfied: '合规',  not_satisfied: '不合规',  na: '不适用',  none: '-' }
            };
            const lbl = labels[lang] || labels.ko;

            const COLOR_SATISFIED = [108, 130, 150]; // 차분한 푸른 회색 (conformance 톤)
            const COLOR_FAIL      = [254, 240, 240]; // #fef0f0 — 매우 옅은 핑크 배경
            const COLOR_NA        = [180, 180, 180];
            const FAIL_TEXT       = [40, 40, 40];    // 옅은 배경 위 검정 텍스트 (가독성)
            const FAIL_DOT        = [188, 80, 70];   // ● 도트는 진한 톤 — indicator 역할

            const _header_fill = '#f5f2f2';
            const _header_text = '#2a2a2a';
            const _line_color  = '#d8d8d8';

            const thStyle = {
                ...this.tableStyles.th,
                // EN/CA 헤더 9 — 'Compliance'(10자) 가 좁아진 컬럼에 한 줄로 들어가도록(아래 _statusW 참고)
                fontSize: lang === 'ko' ? 10.5 : 9,
                fillColor: _header_fill,
                textColor: _header_text,
                lineColor: _line_color,
                fontStyle: 'bold',
                halign: 'center',
                valign: 'middle'
            };

            const tableHead = [[
                { content: this.$t('No.', lang) || 'No.', styles: thStyle },
                { content: this.$t('Code', lang),         styles: thStyle },
                { content: this.$t('문항', lang) || '문항', styles: thStyle },
                { content: this.$t('지표명', lang) || '지표명', styles: thStyle },
                { content: this.$t('충족 여부', lang) || '충족 여부', styles: thStyle },
                { content: this.$t('비고', lang) || '비고',   styles: thStyle }
            ]];

            // 그룹(= 동일 category3_code) 내 '미충족' 지표가 하나라도 있으면 Code 셀에 fail color 적용.
            //   (rowSpan 으로 묶인 Code 셀이 흰색으로 남아, 미충족 row 와 분리돼 보이던 현상 개선.
            //    예: A-1-2 의 Q1.13 만 미충족이어도 Code 셀을 칠해 row 와 연결되게 함)
            const _isGroupAnyFail = (code) => {
                const group = indicators.filter(i => i.category3_code === code);
                return group.some(i => {
                    const s = statusMap[i.db_sub_code] || {};
                    return s.not_applicable !== true && s.satisfied !== null && s.satisfied !== true;
                });
            };

            // body — 동일 category3_code 는 Code 셀 rowSpan, 미충족 row 전체에 fail 배경
            const body = [];
            let lastCode = null;
            indicators.forEach((ind) => {
                const st = statusMap[ind.db_sub_code] || { satisfied: null, not_applicable: false };
                const isNA = st.not_applicable === true;
                const isUnknown = st.satisfied === null && !isNA;
                const isOK = st.satisfied === true && !isNA;
                const isFail = !isOK && !isNA && !isUnknown;

                let statusText, statusColor;
                if (isOK) { statusText = lbl.satisfied; statusColor = COLOR_SATISFIED; }
                else if (isNA) { statusText = lbl.na; statusColor = COLOR_NA; }
                else if (isUnknown) { statusText = lbl.none; statusColor = COLOR_NA; }
                else { statusText = lbl.not_satisfied; statusColor = COLOR_FAIL; }

                const rowFill = isFail ? COLOR_FAIL : '#ffffff';
                const rowText = isFail ? FAIL_TEXT : null;

                // No 셀 — 항상 컬러 칩 톤 (미충족도 row와 같이 fail 컬러)
                const noCell = {
                    content: String(ind.no),
                    styles: {
                        halign: 'center',
                        valign: 'middle',
                        fontStyle: 'bold',
                        textColor: isFail ? FAIL_TEXT : [80, 80, 80],
                        fillColor: rowFill
                    }
                };

                const row = [noCell];
                // Code 컬럼: 같은 category3_code 연속 시 rowSpan — 그룹 전체 fail 일 때만 fail color
                if (ind.category3_code !== lastCode) {
                    const groupCount = indicators.filter(x => x.category3_code === ind.category3_code).length;
                    const groupFail = _isGroupAnyFail(ind.category3_code);
                    row.push({
                        content: `${ind.category3_code}\n${_t(ind.category3_name)}`,
                        rowSpan: groupCount,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            fontStyle: 'bold',
                            textColor: groupFail ? FAIL_TEXT : [40, 40, 40],
                            fillColor: groupFail ? COLOR_FAIL : '#ffffff'
                        }
                    });
                    lastCode = ind.category3_code;
                }
                // 문항 (Q-no)
                row.push({
                    content: ind.question_no,
                    styles: {
                        halign: 'center',
                        valign: 'middle',
                        textColor: rowText || [70, 70, 70],
                        fillColor: rowFill,
                        fontStyle: 'bold'
                    }
                });
                // 지표명
                row.push({
                    content: _t(ind.indicator),
                    styles: {
                        halign: 'left',
                        valign: 'middle',
                        textColor: rowText || [55, 55, 55],
                        fillColor: rowFill
                    }
                });
                // 충족 여부 (텍스트만 — ● 도트는 didDrawCell 에서 그림.
                //   fail 행: 배경 #fef0f0 + 텍스트 검정 + 도트 진한 빨강 (FAIL_DOT) 으로 indicator 강조)
                //   cellPadding.left 를 키워서 도트(cell.x + 5 위치)와 텍스트 간 여백 확보
                row.push({
                    content: statusText,
                    _coreStatusColor: isFail ? FAIL_DOT : statusColor,
                    _coreShowDot: true,
                    styles: {
                        halign: 'center',
                        valign: 'middle',
                        fontStyle: 'bold',
                        textColor: isFail ? FAIL_TEXT : statusColor,
                        fillColor: rowFill,
                        cellPadding: { top: 2, right: 4, bottom: 2, left: 8 }
                    }
                });
                // 비고 — 미충족 시 risk 문구, 그 외는 비움
                row.push({
                    content: isFail ? _t(ind.risk) : '',
                    styles: {
                        halign: 'left',
                        valign: 'middle',
                        fontSize: lang === 'ko' ? 8 : 7.5,
                        textColor: isFail ? FAIL_TEXT : [90, 70, 70],
                        fillColor: rowFill,
                        cellPadding: { top: 2, right: 4, bottom: 2, left: 4 }
                    }
                });
                body.push(row);
            });

            // 충족 여부 컬럼 — KO '해당 없음'(+● 도트) / EN 헤더 'Compliance'(헤더 9pt) 가 한 줄에 들어가는 최소 폭.
            //   (KO 48 / EN 64). 본문은 dot+label 그룹(짧음)이라 64 로 줄여도 충분 → 줄인 만큼 비고(auto)로.
            const _statusW = lang === 'en' ? 64 : 48;
            //   문항 컬럼 — EN 헤더 'Question'(8자)도 한 줄에 들어가도록 EN 만 넓힘 (본문은 Q-no 라 짧음).
            const _questionW = lang === 'en' ? 54 : 38;
            const _codeW   = lang === 'en' ? 84 : 78;
            //   지표명 — EN 120→104 로 축소(2~3줄 wrap 허용), 줄인 폭은 비고(Remarks, auto)가 흡수.
            const _indW    = lang === 'en' ? 104 : 116;
            const _columnStyles = {
                0: { cellWidth: 22 },               // No
                1: { cellWidth: _codeW },           // Code
                2: { cellWidth: _questionW },       // 문항
                3: { cellWidth: _indW },            // 지표명
                4: { cellWidth: _statusW },         // 충족 여부
                5: { cellWidth: 'auto' }            // 비고
            };

            doc.autoTable({
                head: tableHead,
                body,
                startY: position_y,
                margin: { left: leftX, right: this.jsPDFOpt.margin.right },
                showHead: 'everyPage',
                pageBreak: 'auto',
                rowPageBreak: 'avoid',
                styles: {
                    ...this.tableStyles.global,
                    ...this.tableStyles.td,
                    fontSize: lang === 'ko' ? 9 : 8.5,
                    lineColor: _line_color,
                    lineWidth: 0.4,
                    cellPadding: { top: 2, right: 4, bottom: 2, left: 4 },
                    minCellHeight: 24,
                    valign: 'middle'
                },
                columnStyles: _columnStyles,
                // CJK 폰트 fallback — cell content 의 글자 종류에 따라 setFont 로 가용 폰트 적용.
                // 주의: report.core.mixin.js 의 setFont() 가 마지막에 setFontSize/setTextColor 를
                //       default 로 reset 하므로 cell.styles 의 두 값을 직후에 다시 명시 적용.
                willDrawCell: (data) => {
                    const fontStyle = data.cell.styles.fontStyle;
                    if (this.isChineseOrHanzi(data.cell.text?.join?.('') || '')) {
                        this.setFont(doc, fontStyle, this.jsPDFOpt.fontFamily_ca);
                    } else {
                        this.setFont(doc, fontStyle, this.jsPDFOpt.fontFamily);
                    }
                    const fs = data.cell.styles.fontSize;
                    if (fs != null) doc.setFontSize(fs);
                    const tc = data.cell.styles.textColor;
                    if (tc != null) {
                        if (Array.isArray(tc)) doc.setTextColor(tc[0], tc[1], tc[2]);
                        else doc.setTextColor(tc);
                    }
                    // 충족 여부 셀: dot+label 을 didDrawCell 에서 그룹 중앙정렬로 직접 그림 → 기본 텍스트 비움
                    if (data.section === 'body' && data.cell.raw && data.cell.raw._coreShowDot) {
                        data.cell.text = [];
                    }
                },
                // 충족 여부 셀 — ● dot + label 을 셀 중앙에 그룹으로 정렬해 렌더 (_drawCoreDotLabel)
                didDrawCell: (data) => {
                    if (data.section !== 'body') return;
                    this._drawCoreDotLabel(doc, data.cell);
                }
            });

            position_y = doc.lastAutoTable.finalY + 12;
            return position_y;
        },
        // ────────────────────────────────────────────────────────────────
        // P9 (= PDF P10/P11) — 문항별 답변
        //   sub_code (= category3 code, A-1-1 등) 별로 mini autoTable 4컬럼:
        //     [문항 (Q-no + 주제), 충족 여부 (●), 답변 최종, 평가 근거]
        //   한 sub_code 안에 여러 question (Q-no). 한 question 한 row.
        //   다음 sub_code 표가 남은 공간에 안 들어가면 새 페이지.
        // ────────────────────────────────────────────────────────────────
        createQAPage_v2(doc, position_y) {
            const lang = this.report_lang;
            // scoring_pd 만 사용하면 Core(risk=true) 문항이 누락됨 — risk_pd 와 합쳐서 전체 question 확보
            const rows = [
                ...(this.scoring?.scoring_pd ?? []),
                ...(this.reportData?.risk?.risk_pd ?? [])
            ];
            if (!rows.length) return position_y;

            // category3_question_name 'A-1-1. ~~~' → A-1-1
            const _codeOf = (name) => {
                const m = String(name || '').match(/^([A-Z]-\d+-\d+)/);
                return m ? m[1] : null;
            };

            // sub_code (A-X-Y) 별 → label(Q-no) 별 → row[] 묶기
            const byCode = {};
            for (const r of rows) {
                const code = _codeOf(r.category3_question_name);
                if (!code) continue;
                const label = String(r.label || '').trim();
                if (!label) continue;
                if (!byCode[code]) byCode[code] = {};
                if (!byCode[code][label]) byCode[code][label] = [];
                byCode[code][label].push(r);
            }

            const _sortCode = (a, b) => {
                const ma = a.match(/^([A-Z])-(\d+)-(\d+)/);
                const mb = b.match(/^([A-Z])-(\d+)-(\d+)/);
                if (!ma || !mb) return 0;
                return (ma[1].charCodeAt(0) * 1e6 + Number(ma[2]) * 1000 + Number(ma[3]))
                     - (mb[1].charCodeAt(0) * 1e6 + Number(mb[2]) * 1000 + Number(mb[3]));
            };
            const _sortQ = (a, b) => {
                const ma = a.match(/Q(\d+)\.(\d+)/);
                const mb = b.match(/Q(\d+)\.(\d+)/);
                if (!ma || !mb) return 0;
                return (Number(ma[1]) * 1000 + Number(ma[2])) - (Number(mb[1]) * 1000 + Number(mb[2]));
            };

            const allCodes = Object.keys(byCode).sort(_sortCode);
            if (!allCodes.length) return position_y;

            // 색상/라벨
            const COLOR_SATISFIED = [108, 130, 150];
            const COLOR_FAIL_BG   = [254, 240, 240]; // #fef0f0
            const COLOR_FAIL_TEXT = [40, 40, 40];
            const COLOR_FAIL_DOT  = [188, 80, 70];
            const COLOR_NA        = [180, 180, 180];

            const labels = {
                ko: { satisfied: '충족', not_satisfied: '미충족', na: '해당 없음' },
                en: { satisfied: 'Met', not_satisfied: 'Unmet', na: 'N/A' },
                ca: { satisfied: '合规', not_satisfied: '不合规', na: '不适用' }
            };
            const lbl = labels[lang] || labels.ko;

            const _header_fill = '#f5f2f2';
            const _header_text = '#2a2a2a';
            const _line_color = '#d8d8d8';

            const thStyle = {
                ...this.tableStyles.th,
                fontSize: lang === 'ko' ? 10 : 9,
                fillColor: _header_fill,
                textColor: _header_text,
                lineColor: _line_color,
                fontStyle: 'bold',
                halign: 'center',
                valign: 'middle'
            };

            const _pageH = doc.internal.pageSize.height;

            // 첫 페이지 시작
            doc.addPage();
            this.addHeader(doc);
            position_y = this.firstElementSpacing;
            position_y = this.addTitle(doc, position_y, 1, this.$t('문항별 답변', lang));

            const _isKo = lang === 'ko';

            const getAnswer  = this.getQuestionAnswerFromRows;
            const pickAudit  = this.pickAuditCategoryFromRows;
            const getSubject = this.getLocalizedQuestionSubject;
            const getAuditTx = this.getLocalizedAuditCategory;
            const getCodeNm  = this.getLocalizedCodeName;

            // 컬럼: [Q-no, subject, 충족 여부, 답변 최종, 평가 근거] — 헤더에서 처음 2 컬럼 merge
            const _columnStyles = {
                0: { cellWidth: _isKo ? 32 : 34 },    // Q-no
                1: { cellWidth: _isKo ? 82 : 94 },    // subject (문항명)
                // 충족 여부 — EN 헤더 'Compliance'(10자) 줄바꿈 방지로 EN 만 넓힘 (본문 Met/Unmet 은 짧음). 답변(auto)이 흡수.
                2: { cellWidth: _isKo ? 38 : 68 },    // 충족 여부
                3: { cellWidth: 'auto' },              // 답변 최종
                4: { cellWidth: _isKo ? 80 : 90 }      // 평가 근거
            };

            const tableHead = [[
                { content: this.$t('문항', lang) || '문항', colSpan: 2, styles: thStyle },
                { content: this.$t('충족 여부', lang) || '충족 여부', styles: thStyle },
                { content: this.$t('답변 최종', lang) || '답변 최종', styles: thStyle },
                { content: this.$t('평가 근거', lang) || '평가 근거', styles: thStyle }
            ]];

            // section title (sub_code + name) — 박스 없이 텍스트만, medium weight + 작은 fontSize
            const _drawOneSection = (code) => {
                const subTitle = `${code}.  ${getCodeNm(code)}`;
                const leftX = this.jsPDFOpt.margin.left;
                this.setFont(doc, null, null, 'medium');
                doc.setFontSize(lang === 'ko' ? 10.5 : 10);
                doc.setTextColor(45, 45, 45);
                doc.text(subTitle, leftX, position_y + 8);
                position_y += 14;

                // body
                const labelsOfCode = Object.keys(byCode[code]).sort(_sortQ);
                const body = [];
                for (const label of labelsOfCode) {
                    const rowsForQ = byCode[code][label];
                    const ans = getAnswer(rowsForQ, label);
                    const subj = getSubject(label);

                    const isNA = ans.category === 'na';
                    const isOK = ans.category === 'satisfied';
                    const isFail = !isOK && !isNA;

                    // 평가 근거: 참여자가 '예'로 답변했는데 평가자가 미충족으로 처리한 경우(충족→미충족)만 표출.
                    //   참여자 raw 답변 = answerResultBySubCode[sub_code] (df_subs.result, loadAnswerResults).
                    //   충족→충족 / 미충족→미충족 / 미충족→충족 은 비워둠 (컴플 소지 축소).
                    const _subCode = (rowsForQ.find(_r => _r && _r.sub_code) || {}).sub_code;
                    const _participantResult = this.answerResultBySubCode?.[_subCode];
                    const _participantYes = typeof _participantResult === 'string' && _participantResult.trim().startsWith('예');
                    const _showAudit = isFail && _participantYes;
                    const auditCode = _showAudit ? pickAudit(rowsForQ) : '';
                    const auditText = _showAudit ? getAuditTx(auditCode) : '';

                    let statusText, statusColor;
                    if (isOK) { statusText = lbl.satisfied; statusColor = COLOR_SATISFIED; }
                    else if (isNA) { statusText = lbl.na; statusColor = COLOR_NA; }
                    else { statusText = lbl.not_satisfied; statusColor = COLOR_FAIL_DOT; }

                    const rowFill = isFail ? COLOR_FAIL_BG : '#ffffff';
                    const rowText = isFail ? COLOR_FAIL_TEXT : null;

                    // Q-no 와 subject 를 별도 컬럼으로 분리
                    body.push([
                        {
                            content: label,
                            styles: {
                                halign: 'center', valign: 'middle',
                                fontStyle: 'bold',
                                textColor: rowText || [70, 70, 70],
                                fillColor: rowFill
                            }
                        },
                        {
                            content: subj || '',
                            styles: {
                                halign: 'left', valign: 'middle',
                                textColor: rowText || [55, 55, 55],
                                fillColor: rowFill,
                                fontStyle: 'normal'
                            }
                        },
                        {
                            content: statusText,
                            _coreStatusColor: isFail ? COLOR_FAIL_DOT : statusColor,
                            _coreShowDot: true,
                            styles: {
                                halign: 'center', valign: 'middle',
                                fontStyle: 'bold',
                                textColor: isFail ? COLOR_FAIL_TEXT : statusColor,
                                fillColor: rowFill,
                                cellPadding: { top: 2, right: 4, bottom: 2, left: 10 }
                            }
                        },
                        {
                            content: ans.text || '-',
                            styles: {
                                halign: 'left', valign: 'middle',
                                textColor: rowText || [55, 55, 55],
                                fillColor: rowFill
                            }
                        },
                        {
                            content: _showAudit ? (auditText || '-') : '-',
                            styles: {
                                halign: 'center', valign: 'middle',
                                fontSize: lang === 'ko' ? 8.5 : 8,
                                textColor: rowText || [90, 70, 70],
                                fillColor: rowFill
                            }
                        }
                    ]);
                }

                doc.autoTable({
                    head: tableHead,
                    body,
                    startY: position_y,
                    margin: { left: this.jsPDFOpt.margin.left, right: this.jsPDFOpt.margin.right },
                    showHead: 'everyPage',
                    pageBreak: 'auto',
                    // row 가 페이지 사이에 잘리지 않도록 'avoid' — 잘리는 row 는 통째로 다음 페이지로
                    rowPageBreak: 'avoid',
                    styles: {
                        ...this.tableStyles.global,
                        ...this.tableStyles.td,
                        fontSize: lang === 'ko' ? 9 : 8.5,
                        lineColor: _line_color,
                        lineWidth: 0.4,
                        cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
                        minCellHeight: 22,
                        valign: 'middle'
                    },
                    columnStyles: _columnStyles,
                    willDrawCell: (data) => {
                        const fontStyle = data.cell.styles.fontStyle;
                        if (this.isChineseOrHanzi(data.cell.text?.join?.('') || '')) {
                            this.setFont(doc, fontStyle, this.jsPDFOpt.fontFamily_ca);
                        } else {
                            this.setFont(doc, fontStyle, this.jsPDFOpt.fontFamily);
                        }
                        const fs = data.cell.styles.fontSize;
                        if (fs != null) doc.setFontSize(fs);
                        const tc = data.cell.styles.textColor;
                        if (tc != null) {
                            if (Array.isArray(tc)) doc.setTextColor(tc[0], tc[1], tc[2]);
                            else doc.setTextColor(tc);
                        }
                        // 충족 여부 셀: dot+label 그룹 중앙정렬을 위해 기본 텍스트 비움 (didDrawCell 에서 그림)
                        if (data.section === 'body' && data.cell.raw && data.cell.raw._coreShowDot) {
                            data.cell.text = [];
                        }
                    },
                    didDrawCell: (data) => {
                        if (data.section !== 'body') return;
                        this._drawCoreDotLabel(doc, data.cell);
                    }
                });

                position_y = doc.lastAutoTable.finalY + 14;
            };

            // 각 sub_code 별 그리기 — 남은 공간 부족하면 새 페이지
            for (let i = 0; i < allCodes.length; i++) {
                const code = allCodes[i];
                const labelsCount = Object.keys(byCode[code]).length;
                // 섹션 헤더(22) + 표 헤더(~16) + row 별 ~24 + 여유 = 대략 estimate
                // jsPDF px unit 이라 정확한 estimate 어려움 → 비율 사용
                // 한 section 의 ratio 추정: section_h ≈ (22 + 16 + labelsCount*24 + 20) px (px mode 기준 일관)
                // 페이지 안에 들어갈 비율 — position_y 가 페이지 hgt 의 80% 넘으면 새 페이지
                if (i > 0 && position_y > _pageH * 0.78) {
                    doc.addPage();
                    this.addHeader(doc);
                    position_y = this.firstElementSpacing;
                }
                _drawOneSection(code);
            }

            // 마지막 sub_code 표 바로 아래에 평가 근거 인라인 주석 추가
            position_y = this.appendEvidenceLegend_v2(doc, position_y);

            return position_y;
        },
        // 한 codeId 에 속한 sub_code 별 대표 ev_name 수집 (locale 반영). 증빙 누락 메타는 제외.
        collectEvNamesForCodeDetail_v2(codeId) {
            const rows = [
                ...(this.scoring?.scoring_pd ?? []),
                ...(this.reportData?.risk?.risk_pd ?? [])
            ];
            const matched = rows.filter(r => {
                const m = String(r.category3_question_name || '').match(/^([A-Z]-\d+-\d+)/);
                return m && m[1] === codeId;
            });
            const byGroup = {};
            for (const r of matched) {
                if (r.not_applicable === true) continue;
                const sc = r.sub_code || 'default';
                if (!byGroup[sc]) byGroup[sc] = [];
                byGroup[sc].push(r);
            }
            const _name = (r) => {
                if (this.report_lang === 'en') return r.ev_name_e || r.ev_name;
                if (this.report_lang === 'ca') return r.ev_name_c || r.ev_name;
                return r.ev_name;
            };
            const _reverse = (r) => {
                if (this.report_lang === 'en') return r.ev_reverse_name_e || r.ev_reverse_name;
                if (this.report_lang === 'ca') return r.ev_reverse_name_c || r.ev_reverse_name;
                return r.ev_reverse_name;
            };
            // MLT 와 동일한 레벨 기반 문구 선택. (codeId 레벨 = 카드와 동일 codeLevelByCode)
            const _level = this.codeLevelByCode?.[codeId] || 'na';
            const _isBad = _level === 'major' || _level === 'minor';
            const result = [];
            for (const sc of Object.keys(byGroup)) {
                const subRows = byGroup[sc];
                // 매우미흡/미흡(bad)일 때만 부정 지표(아니오/모름) 노출. 보통/우수(good)는 충족 항목만 표시
                //   (MLT buildGoodLevelItem 미러: score 0 부정답변 = 미표시).
                if (_isBad) {
                    // 1) 음성답변(아니오/모름) → 그 분기 ev_name (MLT morueum_ev_name). df_subs.result 기준.
                    const _neg = this.negativeAnswerRow(sc, subRows);
                    if (_neg) { const n = _name(_neg); if (n) result.push(n); continue; }
                    // 2) 예 답변 + 매우미흡/미흡 → 아니오 행 reverse_name (MLT positiveReverseName).
                    const _aniyo = subRows.find(r => r.formula && r.formula.includes('result==\'아니오'));
                    const _rev = _aniyo ? _reverse(_aniyo) : '';
                    if (_rev) { result.push(_rev); continue; }
                }
                // 3) 충족(긍정) 항목 표시. good 레벨에서 충족이 없으면 미표시(부정 지표 노출 안 함).
                //    score===0 은 제외 (MLT buildGoodLevelItem line 299 `if (item.score === 0) return null` 미러).
                //    모름/아니오 음성답변 ev 는 BE 가 satisfied=true 로 표기하되 score=0 이므로
                //    이 필터에서 걸러져 보통/우수 등급에 부정 문구가 새지 않는다.
                const _sat = subRows.filter(r => r.satisfied === true && Number(r.score) > 0);
                if (_sat.length) {
                    // checkbox: "공통문구 (내역 : X)" 형태로 내역만 다른 만족 항목들은
                    //   공통 prefix 1줄 + 내역 하위리스트로 병합 (MLT detailMerge 미러).
                    const _satNames = _sat.map(r => _name(r)).filter(Boolean);
                    // ko '내역' / en 'details' / cn '明细' + 반각·전각 괄호/콜론 모두 대응.
                    const _detailRe = /^(.+?)\s*[(（]\s*(?:내역|details|明细)\s*[:：]\s*(.+?)\s*[)）]$/i;
                    const _ms = _satNames.map(n => n.match(_detailRe));
                    const _mergeable = _satNames.length > 1 && _ms.every(m => m)
                        && _ms.every(m => m[1] === _ms[0][1]);
                    if (_mergeable) {
                        result.push(`${_ms[0][1]}\n- ${_ms.map(m => m[2].trim()).join('\n- ')}`);
                    } else {
                        _satNames.forEach(n => result.push(n));
                    }
                } else if (_isBad) {
                    // 매우미흡/미흡에서 충족 없음 → 음성 분기 ev_name fallback. 보통/우수는 미표시.
                    const _neg2 = subRows.find(r => r.score === 0 && r.formula
                        && (r.formula.includes('result==\'모름\'') || r.formula.includes('result==\'아니오')));
                    if (_neg2) { const n = _name(_neg2); if (n) result.push(n); }
                }
            }
            return result;
        },
        generatePartialSummaryPage(doc, position_y) {
            doc.addPage();
            this.addHeader(doc);
            position_y = this.firstElementSpacing;
            
            position_y = this.addTitle(doc, position_y, 1, this.$t('영역별 평가결과 요약', this.report_lang));
            
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(12);
            let _partial_score_guide_padding = 10;
            let _partial_score_guide_text = this.report_lang === 'ko' ? `${this.companyInfo['회사명']}의 영역별 충족률과 세부 항목별 충족률은 다음과 같습니다.` : this.report_lang === 'en' ? `The compliance rate by area and the detailed compliance rate for ${this.companyInfo['회사명']} are as follows.` : `${this.companyInfo['회사명']} 各地区各细项的满意率如下。`;
            _partial_score_guide_text = doc.splitTextToSize(_partial_score_guide_text, this.pageContentWidth - (_partial_score_guide_padding * 2));
            
            let _partial_score_guide_text_dim = doc.getTextDimensions(_partial_score_guide_text);
            let _partial_score_guide_card_height = _partial_score_guide_text_dim.h + (_partial_score_guide_padding * 2);
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.rect(this.jsPDFOpt.margin.left, position_y, this.pageContentWidth, _partial_score_guide_card_height, 'F');
            
            let _partial_score_guide_text_position_y = position_y + (_partial_score_guide_card_height - _partial_score_guide_text_dim.h) - 1;
            doc.text(_partial_score_guide_text, this.jsPDFOpt.margin.left + _partial_score_guide_padding, _partial_score_guide_text_position_y);
            
            position_y += _partial_score_guide_card_height + this.jsPDFOpt.spacing.section_xs;
            
            // 테이블 범주
            const _fill_color = {below: '#F9D7D7', na: '#E0E0E0'};
            let legends_ko = ['기준미달 (50% 미만)', '해당없음, 평가제외'];
            let _category3_table_legend_ele = this.report_lang === 'en' ? (this.pageContentWidth / 2) : (this.pageContentWidth / 3);
            let _longest_legend_position_x = this.report_lang === 'en' ? this.jsPDFOpt.margin.left + 50 : this.jsPDFOpt.margin.left + (this.pageContentWidth / 2) + 20;
            let _legend_mark_width = 20;
            
            Object.values(_fill_color).forEach((_fill_color_ele, _fill_color_idx) => {
                this.setFont(doc, 'table_td', null, 'normal');
                doc.setFontSize(9.5);
                let _legend_name = 1 > _fill_color_idx ? `: ${this.$t(legends_ko[_fill_color_idx], this.report_lang)},` : `: ${this.$t(legends_ko[_fill_color_idx], this.report_lang)}`;
                _legend_name = doc.splitTextToSize(_legend_name, _category3_table_legend_ele);
                let _legend_name_dim = doc.getTextDimensions(_legend_name);
                
                doc.setFillColor(Object.values(_fill_color)[_fill_color_idx]);
                doc.rect(_longest_legend_position_x, position_y - 6.6, _legend_mark_width, _legend_name_dim.h, 'F');
                
                let _legend_name_position_x = _longest_legend_position_x + _legend_mark_width + 2;
                doc.text(_legend_name, _legend_name_position_x, position_y);
                _longest_legend_position_x += _legend_mark_width + _legend_name_dim.w + 5 + 8;
            });
            
            position_y += this.jsPDFOpt.spacing.section_xs;
            
            const mapQCodeToQName = {};
            this.scoringInfo.category3.forEach(category1 => {
                category1.category3.forEach(category3 => {
                    mapQCodeToQName[category3.category3_question_code] = category3;
                });
            });
            const _category3_group_left = JSON.parse(JSON.stringify(this.scoringInfo.category3.filter(({category1_question_name_ko}) => ['A. 인권'].includes(category1_question_name_ko))));
            
            _category3_group_left[0].category3 = this.reportData.template_questions.reduce((acc, cur) => {
                if (!['A. 인권'].includes(cur.category1_question_name))
                    return acc;
                acc.push({
                    ...cur,
                    category3_question_name: this.report_lang !== 'ko' ? cur.category3_question_name_ea ?? cur.category3_question_name : cur.category3_question_name,
                    ...mapQCodeToQName[cur.category3_question_code]
                });
                return acc;
            }, []);
            
            const _category3_group_right = JSON.parse(JSON.stringify(this.scoringInfo.category3.filter(({category1_question_name_ko}) => ['B. 윤리'].includes(category1_question_name_ko))));
            _category3_group_right[0].category3 = this.reportData.template_questions.reduce((acc, cur) => {
                if (!['B. 윤리'].includes(cur.category1_question_name))
                    return acc;
                acc.push({
                    ...cur,
                    category3_question_name: this.report_lang !== 'ko' ? cur.category3_question_name_ea ?? cur.category3_question_name : cur.category3_question_name,
                    ...mapQCodeToQName[cur.category3_question_code]
                });
                return acc;
            }, []);
            
            let _category3_group_left_postion_y = position_y;
            let _category3_group_right_postion_y = position_y;
            
            const returnCellFillColor = (below, na) => {
                const style = {};
                if (na) {
                    style.fillColor = _fill_color.na;
                } else if (below) {
                    style.fillColor = _fill_color.below;
                }
                return style;
            };
            
            const _category3_score_table_common_header = [{
                content: this.$t('세부 분류', this.report_lang), styles: {
                    ...this.tableStyles.th,
                    fontSize: this.report_lang === 'ko' ? 10 : 9.5,
                    cellPadding: {top: 5.5, right: 5, bottom: 1, left: 5}
                }
            }, {
                content: this.$t('충족률', this.report_lang), styles: {
                    ...this.tableStyles.th,
                    fontSize: this.report_lang === 'ko' ? 10 : 9.5,
                    cellPadding: {top: 5.5, right: 5, bottom: 1, left: 5}
                }
            },];
            
            const _category3_score_table_common_bodyStyle = {
                ...this.tableStyles.td,
                fontSize: this.report_lang === 'ko' ? 8.5 : 8,
                minCellHeight: 11,
                cellPadding: {top: 5, right: 5, bottom: 1.5, left: 5},
            };
            
            for (const _category3 of _category3_group_left) {
                const _category3_table_left = this.jsPDFOpt.margin.left;
                
                const {category1_question_name, category1_score} = _category3;
                _category3_group_left_postion_y = this.addTitle(doc, _category3_group_left_postion_y, 3, `${category1_question_name}:  ${category1_score}% `, {
                    skip_divider: true, no_margin: true
                });
                
                const _category3_score_table_body = _category3.category3.map(({
                    category3_question_name,
                    score,
                    below,
                    not_applicable
                }) => ([{
                    content: category3_question_name,
                    styles: {
                        ...returnCellFillColor(below, not_applicable), halign: 'left',
                        // fontSize: this.report_lang === 'ko' ? 8.5 : 7,
                        // cellWidth: this.pageContentWidth * 0.4
                    }
                }, {
                    content: not_applicable ? 'N/A' : (score != null ? score + '%' : 'Data'),
                    styles: returnCellFillColor(below, not_applicable),
                    // cellWidth: this.pageContentWidth * 0.1
                }]));
                
                _category3_score_table_body.unshift(_category3_score_table_common_header);
                
                autoTable(doc, {
                    theme: 'grid',
                    startY: _category3_group_left_postion_y,
                    tableWidth: (this.pageContentWidth / 2) - 5,
                    showHead: 'everyPage',
                    rowPageBreak: 'avoid',
                    margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing, left: _category3_table_left},
                    styles: {...this.tableStyles.global, halign: 'center', valign: 'middle'},
                    bodyStyles: _category3_score_table_common_bodyStyle,
                    body: _category3_score_table_body,
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    }
                });
                
                
                _category3_group_left_postion_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            }
            
            const _category3_table_right = (this.pageContentWidth / 2) + 5 + this.jsPDFOpt.margin.left;
            for (const _category3 of _category3_group_right) {
                const {category1_question_name, category1_score} = _category3;
                let category1_question_name_score = `${category1_question_name}: ${category1_score}%`;
                _category3_group_right_postion_y = this.addTitle(doc, _category3_group_right_postion_y, 3, category1_question_name_score, {
                    skip_divider: true, no_margin: true, position_x: _category3_table_right
                });
                
                const _category3_score_table_body = _category3.category3.map(({
                    category3_question_name,
                    score,
                    below,
                    not_applicable
                }) => ([{
                    content: category3_question_name,
                    styles: {...returnCellFillColor(below, not_applicable), halign: 'left'}
                }, {
                    content: not_applicable ? 'N/A' : (score != null ? score + '%' : 'Data'),
                    styles: returnCellFillColor(below, not_applicable)
                }]));
                
                _category3_score_table_body.unshift(_category3_score_table_common_header);
                
                autoTable(doc, {
                    theme: 'grid',
                    startY: _category3_group_right_postion_y,
                    tableWidth: (this.pageContentWidth / 2) - 5,
                    showHead: 'everyPage',
                    rowPageBreak: 'avoid',
                    margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing, left: _category3_table_right},
                    styles: {...this.tableStyles.global, halign: 'center', valign: 'middle'},
                    bodyStyles: _category3_score_table_common_bodyStyle,
                    body: _category3_score_table_body,
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    }
                });
                
                _category3_group_right_postion_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            }
            
            position_y = _category3_group_left_postion_y - 5;
            
            const guide_text_position_x = this.jsPDFOpt.margin.left;
            const guide_text_position_y = position_y;
            const guide_text = doc.splitTextToSize(`* Data: ${this.$t('법인 현황을 파악하기 위한 지표로 구성된 영역으로, 전체 충족률 산정에는 반영되지 않음', this.report_lang)}`, this.pageContentWidth + 100);
            
            this.setFont(doc, 'table_td', null, 'normal');
            doc.setTextColor(this.hyundai_colors.muted);
            doc.text(guide_text, guide_text_position_x, guide_text_position_y);
            
            return position_y;
        }, async generateCategory3AnalysisPage(doc, position_y) {
            doc.addPage();
            this.addHeader(doc);
            position_y = this.firstElementSpacing;
            
            // 세부 영역별 평가 결과
            position_y = this.addTitle(doc, position_y, 1, '세부 영역별 평가 결과');
            
            // 세부 영역별 점수
            for (const [category3_idx, _category3] of this.scoringInfo.category3.entries()) {
                if (category3_idx > 0) {
                    doc.addPage();
                    this.addHeader(doc, 2);
                    position_y = this.firstElementSpacing;
                }
                
                const {category1_question_name, category1_question_name_ko} = _category3;
                
                position_y = this.addTitle(doc, position_y, 2, category1_question_name, {
                    skip_divider: true, no_padding: true
                }) - 5;
                position_y = this.addTitle(doc, position_y, 3, '영역별 평가 결과', {
                    skip_divider: true, no_margin: true, no_padding: true
                });
                
                
                // this.pageContentWidth: 386.46
                // this.jsPDFOpt.margin.left: 30
                // position_y: 96.25
                const _category3_score_box_padding = 5;
                const _category1_score_box_width = this.pageContentWidth; // 386.46 
                const _category1_score_box_half_position_y = this.jsPDFOpt.margin.left + ((this.pageContentWidth / 2) - _category3_score_box_padding);
                const _category1_score_box_half_width = ((this.pageContentWidth / 2) - _category3_score_box_padding);
                const _category1_score_box_height = 85; // 90 // 100
                const _category1_score_box_inner_position_y_begin = position_y + _category3_score_box_padding;
                const _category1_score_box_inner_position_y_end = _category1_score_box_inner_position_y_begin + _category1_score_box_height - (_category3_score_box_padding * 2);
                
                doc.setFillColor(...this.jsPDFOpt.card.fillColor);
                doc.setLineWidth(this.jsPDFOpt.card.lineWidth * 2);
                doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                doc.rect(this.jsPDFOpt.margin.left, position_y, _category1_score_box_width, _category1_score_box_height, 'FD');
                
                
                // 좌우측 영역 구분 라인
                doc.setLineWidth(0.75);
                doc.setDrawColor('#E0E0E0');
                doc.line(_category1_score_box_half_position_y, _category1_score_box_inner_position_y_begin, _category1_score_box_half_position_y, _category1_score_box_inner_position_y_end);
                
                // Category1 타이틀
                const _category3_category1_question_name = doc.splitTextToSize(`${_category3.category1_question_name} ${this.$t('충족률', this.report_lang)}`, _category1_score_box_half_width - 10);
                const _category3_category1_question_name_dim = doc.getTextDimensions(_category3_category1_question_name);
                const _category3_category1_question_name_position_x = this.jsPDFOpt.margin.left + 10;
                const _category3_category1_question_name_position_y = _category1_score_box_inner_position_y_begin + (_category3_score_box_padding * 2);
                doc.text(_category3_category1_question_name, _category3_category1_question_name_position_x, _category3_category1_question_name_position_y);
                const _category3_category1_question_name_position_y_end = _category3_category1_question_name_position_y + _category3_category1_question_name_dim.h;
                
                // 좌측 영역
                doc.setFontSize(30);
                let _category3_category1_question_score = `${_category3.category1_score}%`;
                _category3_category1_question_score = doc.splitTextToSize(_category3_category1_question_score, _category1_score_box_half_width);
                const _category3_category1_question_score_dim = doc.getTextDimensions(_category3_category1_question_score);
                const _category3_category1_question_score_position_x = this.jsPDFOpt.margin.left + ((_category1_score_box_half_width - _category3_category1_question_score_dim.w) / 2);
                const _category3_category1_question_score_position_y = _category3_category1_question_name_position_y_end + ((_category1_score_box_inner_position_y_end - _category3_category1_question_name_position_y_end) / 2);
                doc.text(_category3_category1_question_score, _category3_category1_question_score_position_x, _category3_category1_question_score_position_y);
                
                // 우측 영역
                this.setFont(doc, 'table_td', null);
                doc.setFontSize(this.report_lang === 'en' ? 9 : 10.5);
                
                // 1. 우측 영역의 경계와 크기를 정의합니다.
                const _right_box_start_x = _category1_score_box_half_position_y;
                const _right_box_width = this.pageContentWidth / 2; // 혹은 _category1_score_box_width / 2
                const _right_box_height = _category1_score_box_height;
                const _right_box_padding_x = 10;
                
                // 2. 텍스트를 최대 너비에 맞게 나눕니다.
                const _category1_description_text = doc.splitTextToSize(
                    this.category1_description[category1_question_name_ko],
                    _right_box_width - (_right_box_padding_x * 2)
                );
                
                // 3. 나뉜 텍스트 블록의 전체 너비(w)와 높이(h)를 계산합니다.
                const text_block_dim = doc.getTextDimensions(_category1_description_text, {lineHeightFactor: 1.3});
                
                // 4. 텍스트를 상하좌우 정중앙에 배치하기 위한 X, Y 좌표를 계산합니다.
                // X 좌표: (박스 시작 x) + (박스 너비 - 텍스트 블록 너비) / 2
                const new_text_x = _right_box_start_x + ((_right_box_width - text_block_dim.w) / 2);
                
                // Y 좌표: (박스 시작 y) + (박스 높이 - 텍스트 블록 높이) / 2  (이 부분은 이전과 동일)
                const new_text_y = position_y + ((_right_box_height - text_block_dim.h) / 2) + 2;
                
                // 5. 계산된 위치에 align: 'left' 옵션으로 텍스트를 출력합니다.
                doc.text(
                    _category1_description_text,
                    new_text_x,
                    new_text_y,
                    {
                        align: 'left', // 텍스트 줄들은 왼쪽 정렬
                        lineHeightFactor: 1.3
                    }
                );
                
                position_y += _category1_score_box_height + this.jsPDFOpt.spacing.section_xs - 5;
                
                if (this.checkNeedNewPage(doc, position_y, 100)) {
                    position_y = this.firstElementSpacing;
                }
                
                
                position_y = this.addTitle(doc, position_y, 3, this.$t('세부 영역별 충족률', this.report_lang), {
                    skip_divider: true,
                    no_margin: true,
                });
                const y_axis_line_padding = 4.5;
                const _filtered_category3_score = _category3.category3.filter(({not_applicable}) => not_applicable === false);
                let _category3_score_chart_area_position_x_begin = this.jsPDFOpt.margin.left;
                let _category3_score_chart_area_position_x_end = this.jsPDFOpt.margin.left + this.pageContentWidth;
                let _category3_score_chart_width = _category3_score_chart_area_position_x_end - _category3_score_chart_area_position_x_begin;
                let _category3_score_chart_inner_width = _category3_score_chart_width - (y_axis_line_padding * 4);
                let _category3_score_chart_label_width = _category3_score_chart_inner_width * (this.report_lang === 'en' ? 0.5 : 0.35);
                let _category3_score_chart_label_position_x_begin = _category3_score_chart_area_position_x_begin + (y_axis_line_padding * 2);
                let _category3_score_chart_label_position_x_end = this.report_lang === 'en' ?
                    _category3_score_chart_area_position_x_begin + _category1_score_box_half_width - y_axis_line_padding :
                    _category3_score_chart_label_position_x_begin + _category3_score_chart_label_width - y_axis_line_padding;
                
                let _category3_score_chart_y_axis_position_x = _category3_score_chart_label_position_x_end + y_axis_line_padding;
                let _category3_score_chart_bar_area_width = (_category3_score_chart_inner_width * (this.report_lang === 'en' ? 0.5 : 0.65)) - (y_axis_line_padding * 4);
                let _category3_score_chart_bar_position_x_begin = _category3_score_chart_label_position_x_end + y_axis_line_padding;
                let _category3_score_chart_bar_thickness = 6;
                
                let _category3_score_label_total_height = _filtered_category3_score.reduce((_labels_total_height, {category3_question_name}) => {
                    this.setFont(doc, 'table_td', null, 'medium');
                    doc.setFontSize(9.5);
                    const _label = doc.splitTextToSize(category3_question_name, _category3_score_chart_label_width);
                    _labels_total_height += doc.getTextDimensions(_label).h / _label.length;
                    return _labels_total_height;
                }, 0);
                
                _category3_score_label_total_height += (_filtered_category3_score.length - 1) * (y_axis_line_padding * 2);
                
                let _category3_score_chart_area_height = _category3_score_label_total_height + (y_axis_line_padding * 2); // bar 시리즈에 위, 아래 패딩 추가
                let _category3_score_chart_height = _category3_score_chart_area_height + (y_axis_line_padding * 3); // 차트 내부 영역 - 높이에 위, 아래 패딩 추가
                let _category3_score_chart_position_y_begin = position_y;
                let _category3_score_chart_area_position_y_begin = _category3_score_chart_position_y_begin + y_axis_line_padding;
                let _category3_score_chart_area_position_y_end = _category3_score_chart_area_position_y_begin + _category3_score_chart_area_height + y_axis_line_padding;
                
                let _category3_bar_position_y = _category3_score_chart_area_position_y_begin + (y_axis_line_padding * 2.5);
                _filtered_category3_score.forEach(({category3_question_name, score, not_applicable}) => {
                    // label 추가
                    const _category3_barChart_label_dim = doc.getTextDimensions([category3_question_name]);
                    this.setFont(doc, 'table_td', null, 'medium');
                    doc.setFontSize(this.report_lang === 'en' ? 8 : 9.5);
                    
                    
                    doc.text(category3_question_name, _category3_score_chart_label_position_x_end, _category3_bar_position_y, {align: 'right'});
                    
                    
                    // score bar 추가
                    doc.setFillColor(...this.hyundai_colors_rgb.blue);
                    const _category3_barChart_bar_width = not_applicable ? 0 : (score / 100) * _category3_score_chart_bar_area_width;
                    const _category3_barChart_label_height = _category3_barChart_label_dim.h;
                    const _category3_barChart_bar_position_y = _category3_bar_position_y + (_category3_barChart_label_height - _category3_score_chart_bar_thickness) / 2 - y_axis_line_padding - 0.75;
                    const _category3_barChart_bar_position_x_end = _category3_score_chart_bar_position_x_begin + _category3_barChart_bar_width;
                    doc.rect(_category3_score_chart_bar_position_x_begin, _category3_barChart_bar_position_y, _category3_barChart_bar_width, _category3_score_chart_bar_thickness, 'F');
                    
                    // score 추가
                    this.setFont(doc, 'table_td', null, 'medium');
                    doc.setFontSize(9.5);
                    const _category3_barChart_score = doc.splitTextToSize(not_applicable ? 'N/A' : `${score}%`, 33);
                    const _category3_barChart_score_dim = doc.getTextDimensions(_category3_barChart_score);
                    doc.text(_category3_barChart_score, _category3_barChart_bar_position_x_end + y_axis_line_padding, _category3_barChart_bar_position_y + ((_category3_score_chart_bar_thickness + _category3_barChart_score_dim.h) / 2) - 1);
                    _category3_bar_position_y += (y_axis_line_padding * 2) + _category3_barChart_label_height;
                });
                
                // 라벨과 바의 분기 line
                doc.setLineWidth(0.35);
                doc.setDrawColor('#c8c8c8');
                doc.line(_category3_score_chart_y_axis_position_x, _category3_score_chart_area_position_y_begin, _category3_score_chart_y_axis_position_x, _category3_score_chart_area_position_y_end);
                
                // category3 bar 차트 Area
                doc.setLineWidth(this.jsPDFOpt.card.lineWidth);
                doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                doc.rect(_category3_score_chart_area_position_x_begin, _category3_score_chart_position_y_begin, _category3_score_chart_width, _category3_score_chart_height, 'D');
                
                
                position_y += _category3_score_chart_height + this.jsPDFOpt.spacing.section_md;
                if (this.checkNeedNewPage(doc, position_y, 40)) {
                    position_y = this.firstElementSpacing;
                }
                
                // 핵심지표 결과 테이블 범주
                let _longest_legend_position_x_weight = {ko: 28, en: 0, ca: 40};
                let legends_ko = ['충족', '미충족', '해당없음'];
                const _fill_color = {'충족': this.hyundai_colors.blue, '미충족': '#dc353c', '해당없음': '#BDBDBD'};
                let _category3_table_legend_ele = (this.pageContentWidth / 3);
                
                let _longest_legend_position_x = this.jsPDFOpt.margin.left + (this.pageContentWidth / 1.6) + _longest_legend_position_x_weight[this.report_lang];
                let _legend_mark_width = 7;
                let _legend_mark_position_y = position_y + 1;
                Object.values(_fill_color).forEach((_fill_color_ele, _fill_color_idx) => {
                    this.setFont(doc, 'table_td', null, 'normal');
                    doc.setFontSize(9);
                    let _legend_name = 2 > _fill_color_idx ? `${this.$t(legends_ko[_fill_color_idx], this.report_lang)}, ` : this.$t(legends_ko[_fill_color_idx], this.report_lang);
                    _legend_name = doc.splitTextToSize(_legend_name, _category3_table_legend_ele);
                    let _legend_name_dim = doc.getTextDimensions(_legend_name);
                    
                    doc.setFillColor(Object.values(_fill_color)[_fill_color_idx]);
                    doc.circle(_longest_legend_position_x, _legend_mark_position_y, 3, 'F');
                    
                    let _legend_name_position_x = _longest_legend_position_x + _legend_mark_width - 1;
                    doc.text(`: ${_legend_name}`, _legend_name_position_x, _legend_mark_position_y + 2);
                    _longest_legend_position_x += _legend_mark_width + _legend_name_dim.w + 5 + 5;
                });
                
                position_y = this.addTitle(doc, position_y - 5, 3, this.$t('Core 지표 충족 여부', this.report_lang), {
                    skip_divider: true, no_margin: true
                });
                let _category1_key_indicator = this.scoring?.category1_importance_pd
                    .filter(({category1_question_code}) => category1_question_code === _category3.category1_question_code);
                _category1_key_indicator = _category1_key_indicator.reduce((_key_indicators, _key_indicator) => {
                    let {
                        category3_question_code,
                        category3_question_name,
                        category3_question_name_ca,
                        category3_question_name_ea,
                        sub_code,
                        level
                    } = _key_indicator;
                    
                    const target_sub_score = {
                        label: '',
                        score_msg: ''
                    };
                    // hyundai-v2: 음성답변(아니오/모름)이면 선택 분기 ev_name 만 표시(비선택 reverse_name 차단).
                    const _negRow = this.negativeAnswerRow(sub_code, this.mapSubCodeToScoringPd[sub_code]);
                    this.mapSubCodeToScoringPd[sub_code].forEach(elem => {
                        const {
                            ev_name,
                            ev_name_e,
                            ev_name_c,
                            ev_reverse_name,
                            ev_reverse_name_e,
                            ev_reverse_name_c,
                            label,
                            satisfied
                        } = elem;
                        if (!_negRow) {
                            if (satisfied && ev_name) {
                                target_sub_score.score_msg = this.report_lang === 'ko' ? ev_name : this.report_lang === 'en' ? ev_name_e : ev_name_c;
                            } else if (!satisfied && ev_reverse_name) {
                                target_sub_score.score_msg = this.report_lang === 'ko' ? ev_reverse_name : this.report_lang === 'en' ? ev_reverse_name_e : ev_reverse_name_c;
                            }
                        }
                        target_sub_score.label = label;
                    });
                    if (_negRow) {
                        target_sub_score.score_msg = this.report_lang === 'ko' ? _negRow.ev_name : this.report_lang === 'en' ? _negRow.ev_name_e : _negRow.ev_name_c;
                    }

                    let _category3_question_name = this.report_lang === 'ko' ? category3_question_name : this.report_lang === 'ca' ? category3_question_name_ca : category3_question_name_ea;
                    
                    
                    if (!_key_indicators[category3_question_code]) {
                        _key_indicators[category3_question_code] = {
                            category3_question_name: _category3_question_name, count: 0, indicators: []
                        };
                    }
                    
                    _key_indicators[category3_question_code].count++;
                    _key_indicators[category3_question_code].indicators.push({
                        sub_label: target_sub_score.label,
                        score_msg: !level ? '-' : target_sub_score.score_msg,
                        level: !level ? 'N/A' : level
                    });
                    
                    return _key_indicators;
                }, {});
                _category1_key_indicator = Object.values(_category1_key_indicator);
                
                // 핵심지표 결과 테이블
                const category1_key_indicator_header = [[
                    {
                        content: this.$t('번호', this.report_lang),
                        styles: {cellWidth: this.pageContentWidth * 0.1}
                    },
                    {content: this.$t('현황', this.report_lang), styles: {cellWidth: this.pageContentWidth * 0.1}},
                    {
                        content: this.$t('내용', this.report_lang),
                        styles: {cellWidth: this.pageContentWidth * 0.8}
                    },
                ]];
                let category1_key_indicators = _category1_key_indicator.reduce((_key_indicator_rows, _key_indicator_ele) => {
                    const {indicators} = _key_indicator_ele;
                    indicators.forEach(({sub_label, score_msg, level}) => {
                        const _indicator_style = {...this.tableStyles.td};
                        if (level === null || level === 'N/A') {
                            _indicator_style.textColor = _fill_color['해당없음'];
                        } else if (level === 'full') {
                            _indicator_style.textColor = _fill_color['충족'];
                        } else {
                            _indicator_style.textColor = _fill_color['미충족'];
                        }
                        
                        let _indicator_row = [
                            {content: sub_label, styles: this.tableStyles.td},
                            {
                                content: level === null ? 'N/A' : '●', styles: _indicator_style
                            },
                            {
                                content: level === null ? '-' : score_msg,
                                styles: {...this.tableStyles.td, halign: 'left'}
                            },
                        
                        ];
                        
                        _key_indicator_rows.push(_indicator_row);
                    });
                    
                    return _key_indicator_rows;
                }, []);
                
                if (!category1_key_indicators.length) {
                    category1_key_indicators = [[{
                        content: this.$t('해당사항 없음', this.report_lang), colSpan: 4, styles: this.tableStyles.td
                    }]];
                }
                
                autoTable(doc, {
                    theme: 'grid',
                    startY: position_y,
                    tableWidth: this.pageContentWidth,
                    showHead: 'everyPage',
                    rowPageBreak: 'avoid',
                    margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing, left: this.jsPDFOpt.margin.left},
                    styles: {...this.tableStyles.global, halign: 'center'},
                    headStyles: this.tableStyles.th,
                    head: category1_key_indicator_header,
                    body: category1_key_indicators,
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    }
                });
                
                position_y = doc.lastAutoTable.finalY + 10;
                
                const guide_text_position_x = this.jsPDFOpt.margin.left;
                const guide_text = doc.splitTextToSize(`* ${this.$t('Core 지표: 다른 지표에 비해 중요도가 높은 항목으로, 미충족 시 즉시 시정조치가 요구되며, 정기적인 평가를 통해 지속적으로 모니터링 대상이 됨', this.report_lang)}`, this.pageContentWidth + 120);
                this.setFont(doc, 'table_td', null, 'normal');
                doc.setTextColor(this.hyundai_colors.muted);
                doc.text(guide_text, guide_text_position_x, position_y);
                position_y += this.jsPDFOpt.spacing.section_xs;
            }
            
            
            if (this.checkNeedNewPage(doc, position_y, 100)) {
                position_y = this.firstElementSpacing;
            }
            
            return position_y;
        }, createFactorsPage(doc, position_y) {
            try {
                let _is_new_page = this.checkNeedNewPage(doc, position_y, this.pageContentHeight / 2); // 35
                if (_is_new_page) {
                    position_y = this.firstElementSpacing;
                }
                
                position_y = this.addTitle(doc, position_y, 1, this.$t('세부 평가결과', this.report_lang));
                
                this.factors.forEach((_cat1, _cat1_idx) => {
                    if (_cat1_idx > 0 && _cat1_idx < this.factors.length) {
                        position_y += this.jsPDFOpt.spacing.section_sm;
                    }
                    
                    
                    let _is_new_page = this.checkNeedNewPage(doc, position_y, 40); // 35
                    if (_is_new_page) {
                        position_y = this.firstElementSpacing;
                    }
                    
                    position_y = this.addTitle(doc, position_y, 2, `${this.$t(_cat1.category1_question_name, this.report_lang)}`, {
                        skip_divider: true, no_padding: true, no_margin: true
                    });
                    
                    _cat1.category3.forEach((_cat3) => {
                        // 문항별 Factors 테이블
                        _cat3.subs.forEach((_sub) => {
                            let _is_new_page = this.checkNeedNewPage(doc, position_y, 40); // 80 // 50 // 35
                            if (_is_new_page) {
                                position_y = this.firstElementSpacing;
                            }
                            
                            let _factor_table_title = `[${_cat3.category3_question_name} - ${_sub.sub_code}]`;
                            if (_sub.sub_name) {
                                _factor_table_title += ` ${_sub.sub_name}`;
                            }
                            position_y = this.addTitle(doc, position_y, 4, _factor_table_title, {
                                skip_divider: true, no_margin: true, no_padding: true
                            });
                            position_y -= 3;
                            const _flatten_sub_evaluations = [..._sub.evaluations].reduce((_flatten, _sub_ev) => {
                                _flatten.push(..._sub_ev);
                                return _flatten;
                            }, []);
                            
                            const _has_importance_evaluation_or_assessment = !!_flatten_sub_evaluations.find(({contentType}) => ['importance', 'assessment'].includes(contentType));
                            const _columnStyles = {
                                0: {
                                    cellWidth: this.report_lang === 'ko' ? this.pageContentWidth * 0.1 : this.pageContentWidth * 0.15,
                                    halign: 'center',
                                }
                            };
                            
                            if (_has_importance_evaluation_or_assessment) {
                                _columnStyles[1] = {
                                    ...this.tableStyles.td,
                                    cellWidth: this.report_lang !== 'ko' ? this.pageContentWidth * 0.17 : this.pageContentWidth * 0.12,
                                    valign: 'middle',
                                    halign: 'left'
                                };
                            }
                            
                            autoTable(doc, {
                                theme: 'grid',
                                startY: position_y,
                                useCss: true,
                                margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5},
                                body: _sub.evaluations,
                                rowPageBreak: 'avoid',
                                head: [[{content: this.$t('충족 여부', this.report_lang), dataKey: 'FactorState'}, {
                                    content: this.$t('주요 사항', this.report_lang),
                                    dataKey: 'FactorContent',
                                    colSpan: (_has_importance_evaluation_or_assessment) ? 2 : 1
                                }]],
                                styles: this.factorstables.global,
                                headStyles: this.factorstables.th,
                                bodyStyles: this.factorstables.td,
                                columnStyles: _columnStyles,
                                willDrawCell: (data) => {
                                    if (data.section === 'body') {
                                        const {cell} = data;
                                        if (data.column.dataKey === 0) {
                                            cell.text = [];
                                        } else if (data.column.dataKey === 1) {
                                            if (cell.raw.contentType === 'importance' && cell.raw.content) {
                                                cell.text = [];
                                            } else {
                                                cell.styles.cellPadding.right = 6.5;
                                                cell.styles.cellPadding.left = 6.5;
                                            }
                                        }
                                    }
                                },
                                didDrawCell: (data) => {
                                    if (data.section === 'body') {
                                        const {column, cell, doc} = data;
                                        if (column.dataKey === 0) {
                                            const textColor = '#ffffff';
                                            const _text = cell.raw.content;
                                            const bgColor = cell.raw.status ? '#2196f3' : '#ff5722';
                                            if (this.report_lang === 'ca') {
                                                cell.styles.font = this.jsPDFOpt.fontFamily_ca;
                                            } else {
                                                cell.styles.font = this.jsPDFOpt.fontFamily;
                                            }
                                            
                                            // Set text color
                                            doc.setTextColor(textColor);
                                            doc.setFontSize(cell.styles.fontSize - 1.2);
                                            doc.setFillColor(bgColor);
                                            
                                            const [textWidth, textHeight] = [doc.getTextDimensions(_text).w, doc.getTextDimensions(_text).h];
                                            let [badgeWidth, badgeHeight] = [textWidth + 10, textHeight + 4.5];
                                            if (badgeWidth >= cell.width) {
                                                badgeWidth += textWidth + 3;
                                            }
                                            
                                            if (this.pageContentHeight >= cell.y + 1) {
                                                doc.roundedRect(cell.x + ((cell.width - badgeWidth) / 2), cell.y + ((cell.height - badgeHeight) / 2), badgeWidth, badgeHeight, 3, 3, 'F');
                                                
                                                // Draw text in the center of the cell
                                                doc.text(_text, cell.x + cell.width / 2, cell.y + (cell.height / 2) + 0.15, {
                                                    baseline: 'middle', align: 'center'
                                                });
                                            }
                                        } else if (column.dataKey === 1) {
                                            if (cell.raw.contentType === 'importance' && cell.raw.content) {
                                                // Set text color
                                                doc.setTextColor('#ffffff');
                                                doc.setFontSize(this.report_lang === 'ko' ? cell.styles.fontSize - 1.5 : cell.styles.fontSize - 2);
                                                doc.setFillColor('#1565C0');
                                                
                                                const _text = cell.raw.content;
                                                const [textWidth, textHeight] = [doc.getTextDimensions(_text).w, doc.getTextDimensions(_text).h];
                                                let [badgeWidth, badgeHeight] = [textWidth + 10, textHeight + 4.5];
                                                if (badgeWidth >= cell.width) {
                                                    badgeWidth += textWidth + 3;
                                                }
                                                
                                                doc.roundedRect(cell.x + ((cell.width - badgeWidth) / 2), cell.y + ((cell.height - badgeHeight) / 2), badgeWidth, badgeHeight, 3, 3, 'F');
                                                
                                                // Draw text in the center of the cell
                                                doc.text(_text, cell.x + cell.width / 2, cell.y + (cell.height / 2) + 0.15, {
                                                    baseline: 'middle', align: 'center'
                                                });
                                                
                                            } else if (cell.raw.contentType === 'content' && cell.raw.content) {
                                                if (this.report_lang === 'ca') {
                                                    cell.styles.font = this.jsPDFOpt.fontFamily_ca;
                                                } else {
                                                    cell.styles.font = this.jsPDFOpt.fontFamily;
                                                }
                                            }
                                        }
                                    }
                                },
                                didDrawPage: (HookData) => {
                                    this.addHeader(HookData.doc);
                                }
                            });
                            
                            this.setFont(doc, null);
                            position_y = doc.lastAutoTable.finalY;
                            
                            // // 문항별 실사의견 테이블
                            // if (_sub.sub_review_comment && /^[\s\W]*$/.test(_sub.sub_review_comment) === false) {
                            //     autoTable(doc, {
                            //         theme: 'grid',
                            //         startY: position_y,
                            //         useCss: true,
                            //         margin: { ...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5 },
                            //         styles: { valign: 'middle' },
                            //         body: [
                            //             [
                            //                 {
                            //                     content: this.$t('평가자 종합 의견', this.report_lang),
                            //                     styles: {
                            //                         ...this.tableStyles.th,
                            //                         fontSize: this.report_lang === "ca" ? 7.8 : 8.5,
                            //                         cellWidth: this.report_lang === "ko" ? 38.7 : 58,
                            //                         halign: 'center',
                            //                         cellPadding: { top: 4, right: 5, bottom: 3.5, left: 5 },
                            //                     }
                            //                 },
                            //                 {
                            //                     content: _sub.sub_review_comment,
                            //                     styles: {
                            //                         ...this.tableStyles.td,
                            //                         cellPadding: { top: 4.5, right: 5, bottom: 3.5, left: 5 }
                            //                     }
                            //                 },
                            //             ]
                            //         ]
                            //     })
                            
                            //     position_y = doc.lastAutoTable.finalY
                            // }
                            
                            position_y += this.jsPDFOpt.spacing.section_xs;
                        });
                    });
                });
                
                return position_y;
            } catch (error) {
                console.log(error);
                
            }
            
        }, generateFactors() {
            if (!this.scoring?.scoring_pd || !this.scoring.scoring_pd.length) {
                this.factors = [];
            } else {
                let _scoring_pd = this.sortBySubCode([...this.scoring.scoring_pd.sort((a, b) => {
                    return a.satisfied && !b.satisfied ? -1 : !a.satisfied && b.satisfied ? 1 : 0;
                })]);
                _scoring_pd = _scoring_pd.sort((a, b) => {
                    return a.ev_id > b.ev_id;
                });
                
                const _lang = this.report_lang;
                
                const mapSubToScoring = {};
                const mapSubToSubScoring = {};
                
                this.scoring.sub_scoring_pd.forEach(elem => {
                    mapSubToSubScoring[elem.sub_code] = elem;
                });
                
                // hyundai-v2: 음성답변(아니오/모름) sub_code → 선택 분기 행 (df_subs result 기준).
                //   비선택 분기의 reverse_name 오노출 차단용. (answerResultBySubCode 비면 전부 null = 기존 동작)
                const _rowsBySub = {};
                _scoring_pd.forEach(_r => {
                    if (_r.sub_code) (_rowsBySub[_r.sub_code] = _rowsBySub[_r.sub_code] || []).push(_r);
                });
                const _negRowBySub = {};
                Object.keys(_rowsBySub).forEach(_sc => {
                    _negRowBySub[_sc] = this.negativeAnswerRow(_sc, _rowsBySub[_sc]);
                });

                let _factors_raw = _scoring_pd.reduce((_factors_v2, _factor) => {
                    if (mapSubToScoring[_factor.sub_code] == null) {
                        mapSubToScoring[_factor.sub_code] = [];
                    }
                    mapSubToScoring[_factor.sub_code].push(_factor);
                    if (!_factor.not_applicable && _factor.sub_code) {
                        let {
                            category1_question_code,
                            category1_question_name,
                            category1_question_name_ea,
                            category1_question_name_ca,
                            category3_question_code,
                            category3_question_name,
                            category3_question_name_ea,
                            category3_question_name_ca,
                            sub_code,
                            sub_name,
                            sub_name_ca,
                            sub_name_ea,
                            satisfied,
                            ev_name,
                            ev_name_e,
                            ev_name_c,
                            ev_reverse_name,
                            ev_reverse_name_e,
                            ev_reverse_name_c,
                            ev_importance,
                        } = _factor;
                        
                        let _category1_idx = _factors_v2.findIndex(_factors => _factors.category1_question_code === category1_question_code);
                        const _category1_question_name = _lang === 'ko' ? category1_question_name : _lang === 'en' ? category1_question_name_ea : category1_question_name_ca;
                        if (_category1_idx < 0) {
                            _factors_v2.push({
                                category1_question_code,
                                category1_question_name: _category1_question_name,
                                category3: []
                            });
                            
                            _category1_idx = _factors_v2.findIndex(_factors => _factors.category1_question_code === category1_question_code);
                        }
                        
                        let _category3_idex = _factors_v2[_category1_idx].category3.findIndex(_factors_category3 => _factors_category3.category3_question_code === category3_question_code);
                        if (_category3_idex < 0) {
                            const _category3_question_name = _lang === 'ko' ? category3_question_name : _lang === 'en' ? category3_question_name_ea : category3_question_name_ca;
                            _factors_v2[_category1_idx].category3.push({
                                category3_question_code, category3_question_name: _category3_question_name, subs: []
                            });
                            
                            _category3_idex = _factors_v2[_category1_idx].category3.findIndex(_factors_category3 => _factors_category3.category3_question_code === category3_question_code);
                        }
                        
                        let [_content_ev, , _sub_name, // _content_audit_comment, _content_review_comment
                        ] = new Array(4).fill(null);
                        const _negRow = _negRowBySub[sub_code];
                        if (_negRow) {
                            // 음성답변(아니오/모름): 선택 분기 행만 ev_name, 비선택 분기·미충족 양성은 표시 안 함.
                            _content_ev = (_factor.ev_id === _negRow.ev_id)
                                ? (_lang === 'ko' ? ev_name : _lang === 'en' ? ev_name_e : ev_name_c)
                                : null;
                        } else if (satisfied) {
                            _content_ev = _lang === 'ko' ? ev_name : _lang === 'en' ? ev_name_e : ev_name_c;
                        } else {
                            _content_ev = _lang === 'ko' ? ev_reverse_name : _lang === 'en' ? ev_reverse_name_e : ev_reverse_name_c;
                        }
                        // _content_audit_comment = _lang === "ko" ? audit_comment : _lang === "en" ? audit_comment_ea : audit_comment_ca
                        // _content_review_comment = _lang === "ko" ? review_comment : _lang === "en" ? review_comment_ea : review_comment_ca
                        
                        if (!_content_ev) _content_ev = null;
                        // if (!_content_audit_comment) _content_audit_comment = null
                        // if (!_content_review_comment) _content_review_comment = null
                        
                        _sub_name = _lang === 'ko' ? sub_name : _lang === 'en' ? sub_name_ea : sub_name_ca;
                        if (!_sub_name) _sub_name = null;
                        // if (_content_review_comment && /^[^\p{L}\p{N}]+$/u.test(_content_review_comment) === false) {
                        //     if (this.auditor_shortname_prefix_regexp.test(_content_review_comment)) {
                        //         _content_review_comment = _content_review_comment.replace(this.auditor_shortname_prefix_regexp, '').trim()
                        //     }
                        // }
                        // _content_review_comment = this.isFieldEmpty(_content_review_comment) ? null : _content_review_comment
                        
                        let _sub_index = _factors_v2[_category1_idx].category3[_category3_idex].subs.findIndex((sub) => sub.sub_code === sub_code);
                        if (_sub_index === -1) {
                            _factors_v2[_category1_idx].category3[_category3_idex].subs.push({
                                sub_code, sub_name: _sub_name, evaluations: [], sub_review_comment: null // _content_review_comment
                            });
                            
                            _sub_index = _factors_v2[_category1_idx].category3[_category3_idex].subs.findIndex(({sub_code: _sub_code}) => _sub_code === sub_code);
                        }
                        
                        // if (_content_audit_comment && /^[^\p{L}\p{N}]+$/u.test(_content_audit_comment) === false) {
                        //     if (this.auditor_shortname_prefix_regexp.test(_content_audit_comment)) {
                        //         _content_audit_comment = _content_audit_comment.replace(this.auditor_shortname_prefix_regexp, '').trim()
                        //     }
                        // }
                        // _content_audit_comment = this.isFieldEmpty(_content_audit_comment) ? null : _content_audit_comment
                        
                        if (_content_ev && /^[^\p{L}\p{N}]+$/u.test(_content_ev) === false) {
                            let _evaluation_content = [];
                            const _row_1st = [{
                                content: satisfied ? this.$t('충족', this.report_lang) : this.$t('미충족', this.report_lang),
                                contentType: 'satisfied', // rowSpan: !_content_audit_comment ? 1 : 2,
                                rowSpan: 1,
                                status: satisfied,
                                styles: {}
                            }];
                            if (ev_importance === 'HIGH') {
                                _row_1st.push({
                                    content: this.$t('중요사항', this.report_lang),
                                    contentType: 'importance',
                                    styles: {halign: 'center', valign: 'middle', fillColor: 248}
                                });
                            }
                            _row_1st.push({
                                content: _content_ev, contentType: 'content', styles: this.factorstables.td
                            });
                            _evaluation_content.push(_row_1st);
                            
                            // if (_content_audit_comment) {
                            //     _evaluation_content.push([
                            //         { content: this.$t('항목별 평가자 의견', this.report_lang), styles: { halign: "center", valign: "middle", fillColor: 248, fontSize: this.report_lang === "en" ? this.factorstables.td.fontSize - 0.3 : this.factorstables.td.fontSize } },
                            //         { content: _content_audit_comment, contentType: "assessment" }
                            //     ])
                            // }
                            
                            _factors_v2[_category1_idx].category3[_category3_idex].subs[_sub_index].evaluations.push(..._evaluation_content);
                        }
                    }
                    
                    return _factors_v2;
                }, []);
                
                for (let _factor_cat1 of _factors_raw) {
                    for (let _factor_cat3 of _factor_cat1.category3) {
                        for (let _factor_subs of _factor_cat3.subs) {
                            const _flatten_sub_evaluations = [..._factor_subs.evaluations].reduce((_flatten, _sub_ev) => {
                                _flatten.push(..._sub_ev);
                                return _flatten;
                            }, []);
                            
                            const _has_importance_evaluation_or_assessment = _flatten_sub_evaluations.some(({contentType}) => ['importance', 'assessment'].includes(contentType));
                            if (_has_importance_evaluation_or_assessment) {
                                for (let _subs_evs of _factor_subs.evaluations) {
                                    let _ev_importance_or_assessment_idx = _subs_evs.findIndex(({contentType}) => contentType && ['importance', 'assessment'].includes(contentType));
                                    if (_ev_importance_or_assessment_idx == -1) {
                                        const _subs_evs_idx = _subs_evs.findIndex(({contentType}) => contentType === 'content');
                                        if (_subs_evs_idx !== -1) {
                                            _subs_evs[_subs_evs_idx].colSpan = 2;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                
                this.factors = _factors_raw;
                this.mapSubCodeToScoringPd = mapSubToScoring;
                this.mapSubCodeToSubScoringPd = mapSubToSubScoring;
            }
        },
        orderCategory1(_category1_scoring, _category1_fieldname) {
            if (!Array.isArray(_category1_scoring)) {
                return this.ordered_cataory1.reduce((_ordered, _category1_name) => {
                    const _category1 = Object.values(_category1_scoring).find(_category1 => _category1[_category1_fieldname] === _category1_name);
                    if (_category1) _ordered.push(_category1);
                    return _ordered;
                }, []);
            } else {
                return this.ordered_cataory1.reduce((_ordered, _category1_name) => {
                    const _category1 = _category1_scoring.find(_category1 => _category1[_category1_fieldname] === _category1_name);
                    if (_category1) _ordered.push(_category1);
                    return _ordered;
                }, []);
            }
        },
        // ────────────────────────────────────────────────────────────────
        // 평가 근거 주석 (inline legend) — createQAPage_v2 마지막 표 아래에 작은 폰트 텍스트로
        // 우측 정렬 블록으로 인라인 배치 (별도 페이지 X, 테이블 X, [충족]/[미충족] 구분 X).
        // 사용자(조혜진) 요청 — Slack 1779347894.503899 (2026-05-21).
        // ────────────────────────────────────────────────────────────────
        appendEvidenceLegend_v2(doc, position_y) {
            const lang = this.report_lang;
            const _isKo = lang === 'ko';

            // 타이틀 i18n — 별표(*) 로 footnote 성격 표시
            const i18nTitle = {
                ko: '평가 근거*',
                en: 'Audit Basis*',
                ca: '评估依据*'
            };

            // [충족]/[미충족] 구분 없이 li 나열형으로 합쳐서 표시
            const items = [
                {
                    label: { ko: '근거 확인', en: 'Evidence Confirmed', ca: '依据确认' },
                    desc: {
                        ko: '문항별 필수 요건을 충족하는 증빙 자료가 확인됨',
                        en: 'Supporting documents confirming the mandatory requirements of the question are verified.',
                        ca: '已确认能满足各题项必备要求的证明材料。'
                    }
                },
                {
                    label: { ko: '별도 근거 인정', en: 'Alternative Basis Accepted', ca: '接受其他依据' },
                    desc: {
                        ko: '해당 문항에 첨부된 자료 외, 타 문항의 증빙 또는 기타 제출 자료를 통해 근거가 인정됨',
                        en: 'Basis recognized via evidence from other questions or submitted materials.',
                        ca: '除该题项所附材料外，通过其他题项的证明或其他提交材料认定其依据。'
                    }
                },
                {
                    label: { ko: '증빙 누락', en: 'Document Missing', ca: '材料缺失' },
                    desc: {
                        ko: '증빙 필수 문항이나 관련 증빙 자료가 제출되지 않음',
                        en: 'No supporting documents have been submitted for a question that requires evidence.',
                        ca: '必备证明题项或相关证明材料未提交。'
                    }
                },
                {
                    label: { ko: '증빙 불충분', en: 'Insufficient Evidence', ca: '证据不足' },
                    desc: {
                        ko: '제출된 증빙 자료가 평가 요건을 충족하지 못함',
                        en: 'The submitted supporting documents do not meet the evaluation requirements.',
                        ca: '所提交的证明材料未能满足评估要求。'
                    }
                },
                {
                    label: { ko: '범위 불일치', en: 'Scope Mismatch', ca: '范围不一致' },
                    desc: {
                        ko: '제출된 증빙 자료가 평가 요건의 기준 범위와 일치하지 않음',
                        en: 'The submitted supporting documents do not align with the criteria scope of the evaluation requirements.',
                        ca: '所提交的证明材料与评估要求的标准范围不一致。'
                    }
                },
                {
                    label: { ko: '기간 만료', en: 'Document Expired', ca: '文件过期' },
                    desc: {
                        ko: '제출된 증빙 자료의 유효 기간이 만료되어 효력이 상실됨',
                        en: 'The validity period of the submitted supporting documents has expired and is no longer in effect.',
                        ca: '所提交的证明材料有效期已过，已不再生效。'
                    }
                },
                {
                    label: { ko: '파일 오류', en: 'File Error', ca: '文件错误' },
                    desc: {
                        ko: '제출된 증빙 파일이 암호화, 포맷 파손 등으로 인해 열람이 불가능함',
                        en: 'The submitted supporting file cannot be opened due to encryption, format corruption, or similar issues.',
                        ca: '所提交的证明文件因加密、格式损坏等原因无法打开。'
                    }
                },
                {
                    label: { ko: '기타', en: 'Other', ca: '其他' },
                    desc: {
                        ko: '기타 사유로 인해 인정이 불가하며, 세부 내역은 평가 기관으로 문의 바람',
                        en: 'Recognition is not possible for other reasons. For details, please contact the evaluation organization.',
                        ca: '因其他原因无法认定，详细内容请咨询评估机构。'
                    }
                }
            ];

            const _pickLang = (bundle) => (bundle && (bundle[lang] || bundle.ko)) || '';

            const leftX = this.jsPDFOpt.margin.left;
            const fullW = this.pageContentWidth;
            const _pageH = doc.internal.pageSize.height;

            // 폰트 사이즈 — 타이틀은 약간 키워 위계감 부여
            const titleFs = _isKo ? 9.5 : 9;
            const itemFs = _isKo ? 8.5 : 8;
            const titleH = 14;
            const lineH = _isKo ? 11.5 : 11;

            // 라벨 컬럼 폭 — 모든 라벨의 최대 폭 기준
            this.setFont(doc, null, null, 'medium');
            doc.setFontSize(itemFs);
            let maxLabelW = 0;
            for (const it of items) {
                const w = doc.getTextDimensions(_pickLang(it.label)).w;
                if (w > maxLabelW) maxLabelW = w;
            }
            // 설명 컬럼 폭 — 모든 desc 의 자연 폭 (wrap 없음) 최대값
            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(itemFs);
            let maxDescW = 0;
            for (const it of items) {
                const w = doc.getTextDimensions(_pickLang(it.desc)).w;
                if (w > maxDescW) maxDescW = w;
            }

            // 좌측 정렬 — 페이지 좌측 마진부터 시작, desc 가 길면 wrap.
            const bulletGap = 6;
            const colonGapL = 2; // 라벨 → 콜론
            const colonGapR = 4; // 콜론 → 설명
            const colonW = 4;
            const naturalBlockW = bulletGap + maxLabelW + colonGapL + colonW + colonGapR + maxDescW;
            const blockW = Math.min(fullW, naturalBlockW);
            const blockX = leftX;

            const bulletX = blockX;
            const labelX = bulletX + bulletGap;
            const colonX = labelX + maxLabelW + colonGapL;
            const descX = colonX + colonGapR;
            const descW = blockX + blockW - descX;

            // 높이 추정 — wrap 1-2 줄 가정
            const estH = titleH + 4 + items.length * lineH * 1.6 + 6;
            if (position_y + estH > _pageH - 60) {
                doc.addPage();
                this.addHeader(doc);
                position_y = this.firstElementSpacing;
            }

            // ── 타이틀 ([] 없이, 굵기/사이즈로만 위계감 — 부차 정보 톤) ──
            this.setFont(doc, null, null, 'semibold');
            doc.setFontSize(titleFs);
            doc.setTextColor(110, 110, 110);
            doc.text(_pickLang(i18nTitle), blockX, position_y + titleH - 2);
            position_y += titleH + 4;

            // ── li 나열형 — 충족/미충족 구분 없이 8개. 전체 muted tone. ──
            for (const it of items) {
                const labelText = _pickLang(it.label);
                const descText = _pickLang(it.desc);

                // 설명 wrap (먼저 라인 수 계산용)
                this.setFont(doc, null, null, 'normal');
                doc.setFontSize(itemFs);
                const descLines = doc.splitTextToSize(descText, descW);

                // bullet
                this.setFont(doc, null, null, 'normal');
                doc.setFontSize(itemFs);
                doc.setTextColor(160, 160, 160);
                doc.text('·', bulletX, position_y + 8);

                // 라벨 (medium — bold 보다 살짝 죽임)
                this.setFont(doc, null, null, 'medium');
                doc.setFontSize(itemFs);
                doc.setTextColor(120, 120, 120);
                doc.text(labelText, labelX, position_y + 8);

                // 콜론
                doc.setTextColor(170, 170, 170);
                doc.text(':', colonX, position_y + 8);

                // 설명 (normal — 라벨보다 살짝 더 옅게)
                this.setFont(doc, null, null, 'normal');
                doc.setFontSize(itemFs);
                doc.setTextColor(135, 135, 135);
                doc.setLineHeightFactor(1.35);
                doc.text(descLines, descX, position_y + 8);
                doc.setLineHeightFactor(1.15);

                const rowH = Math.max(lineH, descLines.length * lineH);
                position_y += rowH;
            }

            return position_y;
        },
        // 전년도(2025) 점수 lookup —
        //   currentPrefix: 2026 cat name prefix (e.g. 'A', 'A-1', 'A-1-1')
        //   PRIOR_YEAR_CODE_MAP 으로 2025 prefix 로 변환 후 priorYearScoringInfo 인덱스에서 조회.
        //   매핑 없거나 매칭 안 되면 null.
        getPriorYearScore(level, currentPrefix) {
            if (!this.hasPriorYear || !currentPrefix) return null;
            const _priorPrefix = PRIOR_YEAR_CODE_MAP?.[level]?.[currentPrefix];
            if (!_priorPrefix) return null;
            const _byPrefix = this.priorYearScoringInfo?.[`${level}ScoreByPrefix`];
            const _v = _byPrefix?.[_priorPrefix];
            return _v != null ? _v : null;
        },
        // 전년도(2025) report data 를 1회 fetch. PDF 생성 진입 시 호출.
        //   매핑 없거나 신규 회사면 fetch 자체를 건너뛰고 priorYearReportData = null 유지.
        //   API 실패도 무해 — 모든 prior year 마커가 자동으로 숨겨짐.
        async loadPriorYearReportData() {
            const _perf_t0 = performance.now();
            try {
                this.priorYearReportData = null;

                // campaign_id 는 DFNiceServiceRequestSchema 가 exclude 하므로 reportData.info 엔 없음.
                //   부모 컴포넌트(ManagingResultReport_v2 / CampaignReport / ReportBulkGenerator) 의
                //   prop / data 인 this.campaign_id 를 1순위로 사용.
                const _currentCampaignId = Number(this.campaign_id ?? this.reportData?.info?.campaign?.id);
                const _companyId = Number(this.reportData?.info?.company?.id);
                const _projectCode = this.reportData?.info?.project_code;
                const _priorCampaignId = PRIOR_YEAR_CAMPAIGN_MAP[_currentCampaignId];

                if (!_priorCampaignId) return;                              // 550 등 매핑 없는 캠페인
                if (HYUNDAI_V2_NEW_COMPANY_IDS.has(_companyId)) return;     // 신규 회사 4종
                if (!_companyId || !_projectCode) return;                   // 식별자 누락

                // project_code 는 trailing `_NN` 년도 suffix 를 -1. 예: HD_DOM_PROD_26 → HD_DOM_PROD_25
                //   매칭 안 되는 패턴이면 그대로 둬 → 404 fallback 정상 작동.
                const _priorProjectCode = String(_projectCode).replace(/_(\d{2})$/, (_, yy) => {
                    return `_${String(Number(yy) - 1).padStart(2, '0')}`;
                });

                const _res = await this.$axios('/report/data', {
                    params: {
                        project_code: _priorProjectCode,
                        campaign_id: _priorCampaignId,
                        company_id: _companyId
                    }
                });
                if (_res?.data?.scoring) {
                    this.priorYearReportData = _res.data;
                }
            } catch (_err) {
                // 전년도 데이터 없음/네트워크 실패 — 무해 (priorYearReportData null 유지)
                this.priorYearReportData = null;
            } finally {
                perfLog(`[PERF] loadPriorYearReportData: ${(performance.now() - _perf_t0).toFixed(0)}ms (hasData=${!!this.priorYearReportData})`);
            }
        },
        // hyundai-v2: 답변 result(예/아니오/모름) 를 sub_code 별로 1회 fetch. (loadPriorYearReportData 와 병렬)
        //   결과보고서 scoring 행엔 result 가 없어, 음성답변/증빙누락 시 어느 분기를 골랐는지
        //   satisfied 만으론 구분 불가(모름·아니오·예+증빙누락 모두 satisfied=false) → df_subs.result 로 정확 타겟.
        //   실패/누락은 무해 — 빈 맵 유지 시 기존 satisfied 기반 로직으로 fallback.
        async loadAnswerResults() {
            const _perf_t0 = performance.now();
            try {
                this.answerResultBySubCode = {};
                const _campaignId = this.campaign_id ?? this.reportData?.info?.campaign?.id;
                const _companyId = this.reportData?.info?.company?.id;
                const _companyName = this.reportData?.info?.company?.name;
                const _subCodes = [...new Set((this.scoring?.scoring_pd ?? [])
                    .map(_r => _r.sub_code).filter(Boolean))];
                if (!_campaignId || !_companyId || !_subCodes.length) return;

                const _res = await this.$axios.get('/subs', {
                    params: {
                        company_id: _companyId,
                        company_name: _companyName,
                        campaign_id: _campaignId,
                        sub_codes: _subCodes.join(',')
                    }
                });
                const _map = {};
                (_res?.data ?? []).forEach(_sub => {
                    if (!_sub || !_sub.sub_code) return;
                    let _sdc = _sub.sub_data_contents;
                    if (typeof _sdc === 'string') {
                        try { _sdc = JSON.parse(_sdc); } catch (_e) { _sdc = null; }
                    }
                    _map[_sub.sub_code] = _sdc && _sdc.result !== undefined ? _sdc.result : null;
                });
                this.answerResultBySubCode = _map;
            } catch (_err) {
                this.answerResultBySubCode = {};
            } finally {
                perfLog(`[PERF] loadAnswerResults: ${(performance.now() - _perf_t0).toFixed(0)}ms (subCount=${Object.keys(this.answerResultBySubCode || {}).length})`);
            }
        },
        // hyundai-v2: 답변 result(df_subs) 기준 음성분기(아니오/모름) 행 반환. 음성답변 아니면 null.
        //   "result=='아니오" 부분일치로 "아니오, …" 변형 답변도 커버.
        negativeAnswerRow(sub_code, rows) {
            const _result = this.answerResultBySubCode?.[sub_code];
            if (_result == null) return null;
            let _needle = null;
            if (_result === '모름') _needle = 'result==\'모름\'';
            else if (typeof _result === 'string' && _result.startsWith('아니오')) _needle = 'result==\'아니오';
            if (!_needle) return null;   // 예 / 해당없음 / 미답변 → 기존 로직 유지
            const _found = (rows || []).find(_r => _r && _r.ev_name && _r.formula && _r.formula.includes(_needle)) || null;
            return _found;
        },
        async generatePDF_hyundai_v2(doc, position_top) {
            const _perf_total_t0 = performance.now();
            // 전년도 비교 + 답변 result(hyundai-v2) 병렬 로드 — generateFactors/Core 표시 전에 완료돼야 함.
            //   실패해도 PDF 생성 진행 (null/빈 맵 fallback).
            const _perf_prefetch_t0 = performance.now();
            await Promise.all([
                this.loadPriorYearReportData(),
                this.loadAnswerResults()
            ]);
            perfLog(`[PERF] generatePDF_hyundai_v2 - prefetch(Promise.all) wall time: ${(performance.now() - _perf_prefetch_t0).toFixed(0)}ms`);

            if (this.reportMode === 'full') {
                // Factors 페이지 데이터 생성
                const _t = performance.now();
                this.generateFactors();
                perfLog(`[PERF] generateFactors: ${(performance.now() - _t).toFixed(0)}ms`);
            }
            this.pageContentWidth = doc.internal.pageSize.width - (this.jsPDFOpt.margin.left + this.jsPDFOpt.margin.right);
            this.pageContentHeight = doc.internal.pageSize.height - (this.jsPDFOpt.margin.top + this.jsPDFOpt.margin.bottom);

            let _t = performance.now();
            this.generateCover_hyundai(doc);
            perfLog(`[PERF] generateCover_hyundai: ${(performance.now() - _t).toFixed(0)}ms`);

            doc.addPage();
            this.addHeader(doc, 2);

            _t = performance.now();
            let position_y = await this.createSummaryPage(doc, position_top);
            perfLog(`[PERF] createSummaryPage: ${(performance.now() - _t).toFixed(0)}ms`);
            _t = performance.now();
            position_y = await this.createCategory2ScorePage(doc, position_y);
            perfLog(`[PERF] createCategory2ScorePage: ${(performance.now() - _t).toFixed(0)}ms`);
            _t = performance.now();
            position_y = this.createCodeCompliancePage_v2(doc, position_y);
            perfLog(`[PERF] createCodeCompliancePage_v2: ${(performance.now() - _t).toFixed(0)}ms`);
            _t = performance.now();
            position_y = this.createCodeDetailPages_v2(doc, position_y);
            perfLog(`[PERF] createCodeDetailPages_v2: ${(performance.now() - _t).toFixed(0)}ms`);
            _t = performance.now();
            position_y = this.createCorePage_v2(doc, position_y);
            perfLog(`[PERF] createCorePage_v2: ${(performance.now() - _t).toFixed(0)}ms`);
            _t = performance.now();
            position_y = this.createQAPage_v2(doc, position_y);
            perfLog(`[PERF] createQAPage_v2: ${(performance.now() - _t).toFixed(0)}ms`);


            const end_of_document_x = doc.internal.pageSize.getWidth() / 2;
            const end_of_document_y = doc.internal.pageSize.getHeight() - 50;
            
            this.setFont(doc, 'title_sm', null, 'normal');
            doc.setTextColor(this.hyundai_colors.muted);
            doc.text('- End of Document -', end_of_document_x, end_of_document_y, {
                align: 'center'
            });
            
            
            const totalPages = doc.getNumberOfPages();
            
            for (let i = 2; i <= totalPages; i++) {
                doc.setPage(i);  // 현재 페이지로 이동
                this.addFooter(doc);
            }
            
            doc.setDocumentProperties({
                title: this.pdf_filename,
                subject: this.pdf_filename,
                filename: `${this.pdf_filename}.pdf`,
                author: 'QuantifiedESG Inc.',
                creator: 'QESG'
            });

            perfLog(`[PERF] generatePDF_hyundai_v2 TOTAL: ${(performance.now() - _perf_total_t0).toFixed(0)}ms`);
            return doc;
        },
        generateCover_hyundai(doc) {
            try {
                // 페이지 너비,높이
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                
                const maxWidth = pageWidth - 20; // 페이지의 여백을 고려한 너비
                const maxHeight = pageHeight - 20; // 페이지의 여백을 고려한 높이
                
                // 표지 색상 설정
                doc.setFillColor(this.hyundai_colors.blue); // RGB 값
                doc.rect(0, 0, pageWidth, pageHeight, 'F'); // 'F'는 fill
                
                const cover_hyundai_logo_position_x = 30;
                const cover_hyundai_logo_position_y = 40;
                
                // 현대 로고 추가
                doc.addImage(this.hyundai_logo_png, 'png', cover_hyundai_logo_position_x, cover_hyundai_logo_position_y, this.hyundai_logo_white.width * 1.4, this.hyundai_logo_white.height * 1.4);
                
                // 보고서 이름 추가
                this.setFont(doc, 'cover_name');
                doc.setFontSize(36);
                const [_report_title_width, _report_title_height] = [doc.getTextDimensions(this.cover_title).w, doc.getTextDimensions(this.cover_title).h];
                
                let _text_position_x = this.jsPDFOpt.margin.left;
                let _text_position_y = (maxHeight / 3) + (this.jsPDFOpt.margin.top * 1.25);
                doc.setTextColor('#FFFFFF');
                doc.text(this.cover_title, _text_position_x, _text_position_y, {});
                
                
                // sub_title — lang arg 전달해야 dropdown 변경 반영 (i18n.locale 은 KO 그대로일 수 있음)
                // 0618: 초안 토글 시 보고서명 "결과보고서(초안)"
                const sub_title = this.is_draft
                    ? this.$t('결과보고서(초안)', this.report_lang)
                    : this.$t('결과보고서', this.report_lang);
                const sub_title_dim = doc.getTextDimensions(sub_title);
                // cover_title 이 array 일 때 (en: 2줄, ko/ca: 1줄) 마지막 줄 baseline 위치까지 내려야
                // sub_title 이 overlap 되지 않음
                const _coverLines = Array.isArray(this.cover_title) ? this.cover_title.length : 1;
                const _lineH = doc.getLineHeight();
                _text_position_y += (_coverLines - 1) * _lineH + sub_title_dim.h + 14;
                doc.setTextColor('#93cddd');
                doc.text(sub_title, _text_position_x, _text_position_y, {});
                
                
                // 보고서 기업명 추가
                if (this.report_lang === 'ca' && this.isChineseOrHanzi(this.companyInfo['회사명'])) {
                    this.setFont(doc, 'cover_company_name', this.jsPDFOpt.fontFamily_ca);
                } else {
                    doc.setFont();
                    this.setFont(doc, 'cover_company_name', this.jsPDFOpt.fontFamily);
                }
                
                const _company_name = doc.splitTextToSize(this.companyInfo['회사명'], this.pageContentWidth);
                _text_position_y += _report_title_height + (this.jsPDFOpt.margin.top * 1.5);
                doc.setTextColor('#FFFFFF');
                doc.text(_company_name, _text_position_x, _text_position_y);
                
                // ── Disclaimer 블록 (PPTX 양식: 사업장명 → gap → 전체폭 구분선 → Disclaimer 라벨 → bullets) ──
                const leftMargin = this.jsPDFOpt.margin.left;
                const rightMargin = pageWidth - this.jsPDFOpt.margin.right;
                let y = _text_position_y + 50; // 사업장명 baseline 아래로 50px gap

                // 전체폭 구분선 (먼저)
                doc.setDrawColor('#FFFFFF');
                doc.setLineWidth(1);
                doc.line(leftMargin, y, rightMargin, y);
                y += 16;

                // Disclaimer 라벨 — ca 는 중국어 폰트 사용
                const _disclaimerLabel = this.$t('Disclaimer', this.report_lang) || 'Disclaimer';
                this.setFont(doc, 'title_sm',
                    this.report_lang === 'ca' ? this.jsPDFOpt.fontFamily_ca : this.jsPDFOpt.fontFamily);
                doc.setFontSize(12);
                doc.setTextColor('#FFFFFF');
                doc.text(_disclaimerLabel, leftMargin, y);
                y += 14;

                // bullets — 3개 항목, 각 항목은 2줄로 자동 분할 (text1+2, text3+4, text5)
                this.setFont(doc, 'footnote',
                    this.report_lang === 'ca' ? this.jsPDFOpt.fontFamily_ca : this.jsPDFOpt.fontFamily);
                doc.setFontSize(9);
                doc.setTextColor('#FFFFFF');
                const bulletLineH = 12;
                const indentLineH = 11;
                const bullets = [
                    [`- ${this.$t('result_report_disclaimer.text1', this.report_lang)}`,
                        `   ${this.$t('result_report_disclaimer.text2', this.report_lang)}`],
                    [`- ${this.$t('result_report_disclaimer.text3', this.report_lang)}`,
                        `   ${this.$t('result_report_disclaimer.text4', this.report_lang)}`],
                    [`- ${this.$t('result_report_disclaimer.text5', this.report_lang)}`]
                ];
                bullets.forEach(group => {
                    group.forEach((line, idx) => {
                        doc.text(line, leftMargin, y);
                        y += idx === 0 ? indentLineH : bulletLineH;
                    });
                    y += 4; // bullet 간 추가 여백
                });

                // 0618: 초안 안내 문구 — footer 바로 위(거의 붙도록), 골드 유지 + Disclaimer 와 같은 9pt (is_draft 시에만)
                if (this.is_draft) {
                    this.setFont(doc, 'title_sm',
                        this.report_lang === 'ca' ? this.jsPDFOpt.fontFamily_ca : this.jsPDFOpt.fontFamily, 'semibold');
                    doc.setFontSize(8);
                    doc.setTextColor('#ffd666');
                    // 문장 단위 개행: "…작성된 초안입니다." / "기재된 …변경될 수 있습니다." 를 줄로 나눔
                    const _draftFull = this.$t('본 보고서는 최종 확정 전 이해관계자의 의견 수렴 및 내용 검토를 위해 작성된 초안입니다. 기재된 수치 및 내용은 피드백 검토 결과에 따라 최종 보고서에서 변경될 수 있습니다.', this.report_lang);
                    const _draftLines = String(_draftFull)
                        .replace(/([.。])\s*/g, '$1\n').split('\n').map(s => s.trim()).filter(Boolean)
                        .flatMap(s => doc.splitTextToSize(s, rightMargin - leftMargin));
                    const _draftLineH = 13;
                    // footer(y = pageHeight-22) 바로 위에 붙도록 역산 배치
                    let _draftY = (pageHeight - 22) - (_draftLines.length * _draftLineH) - 12;
                    _draftLines.forEach(line => { doc.text(line, leftMargin, _draftY); _draftY += _draftLineH; });
                }

                // ── 하단 footer: 좌측 © QuantifiedESG, Inc. / 우측 QESG White 로고 (space-between) ──
                const footer_y = pageHeight - 22;
                const footer_pad = 30;
                const qesg_logo_h = 15;
                const qesg_logo_w = qesg_logo_h * (3747 / 942); // ≈ 59.65, 원본 비율 유지

                // 좌측: © text — 연한 회색 + italic
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(9);
                doc.setTextColor('#bfbfbf');
                doc.text('© QuantifiedESG, Inc.', footer_pad, footer_y);

                // 우측: QESG White 로고
                try {
                    doc.addImage(
                        QESGLogoWhitePNG, 'png',
                        pageWidth - footer_pad - qesg_logo_w,
                        footer_y - qesg_logo_h + 2,
                        qesg_logo_w, qesg_logo_h
                    );
                } catch (e) {
                    // 로고 못 그려도 텍스트는 표시
                }
            } catch (error) {
                console.log(error);
            }
        },
        addHeader(doc) {
            doc.setFillColor(this.hyundai_colors.blue);
            
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.headerStyles.fontSize);
            doc.setTextColor(...this.jsPDFOpt.headerStyles.textColor);
            const [headerBox_position_x, headerBox_position_y] = [this.jsPDFOpt.margin.left, this.jsPDFOpt.margin.top + 10];
            doc.rect(headerBox_position_x, headerBox_position_y, this.pageContentWidth, this.jsPDFOpt.headerHeight, 'F');
            
            const report_header_padding = 10;
            let _report_serial_number = this.formatDate(new Date().toUTCString(), 'YYMMDD');
            _report_serial_number += `-${this.reportData.info.company_id}`;
            _report_serial_number += `-${this.reportData.info.id}`;
            
            _report_serial_number = doc.splitTextToSize(`${this.$t('발급번호', this.report_lang)} : ${_report_serial_number}`, this.pageContentWidth / 2);
            const _report_serial_number_dim = doc.getTextDimensions(_report_serial_number);
            const _report_serial_number_position_y = headerBox_position_y + ((this.jsPDFOpt.headerHeight - _report_serial_number_dim.h)) - 1;
            doc.text(_report_serial_number, headerBox_position_x + report_header_padding, _report_serial_number_position_y);
            
            const _report_company_header = doc.splitTextToSize(this.companyInfo['회사명'], doc.getTextWidth(this.companyInfo['회사명'] ?? '') + 10);
            const _report_company_header_dim = doc.getTextDimensions(_report_company_header);
            const _report_company_header_position_x = this.jsPDFOpt.margin.left + this.pageContentWidth - doc.getTextWidth(this.companyInfo['회사명'] ?? '') - report_header_padding;
            const _report_company_header_position_y = headerBox_position_y + ((this.jsPDFOpt.headerHeight - _report_company_header_dim.h)) - 1;
            
            if (this.report_lang === 'ca' && this.isChineseOrHanzi(this.companyInfo['회사명'])) {
                this.setFont(doc, null, this.jsPDFOpt.fontFamily_ca);
            } else {
                this.setFont(doc, null, this.jsPDFOpt.fontFamily);
            }
            
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.headerStyles.fontSize);
            doc.setTextColor(...this.jsPDFOpt.headerStyles.textColor);
            doc.text(_report_company_header, _report_company_header_position_x, _report_company_header_position_y);
        }, addFooter(doc) {
            // this.setFont(doc)
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.footerStyles.fontSize);
            doc.setFillColor(this.hyundai_colors.sand);
            
            const pageHeight = doc.internal.pageSize.getHeight();
            const [footerBox_position_x, footerBox_position_y] = [0, pageHeight - this.jsPDFOpt.footerHeight];
            const footerBox_width = doc.internal.pageSize.getWidth();
            doc.rect(footerBox_position_x, footerBox_position_y, footerBox_width, this.jsPDFOpt.footerHeight, 'F');
            
            // const _page_total = doc.internal.getNumberOfPages();
            const _page_total = doc.getNumberOfPages() - 1;
            const _page_current = doc.getCurrentPageInfo().pageNumber;
            
            // (좌측) Hyundai 로고
            const logo_hyundai_position_y = pageHeight - (this.jsPDFOpt.footerHeight / 2) - 20;
            doc.addImage(this.hyundai_logo_blue_png, 'png', this.jsPDFOpt.margin.left, logo_hyundai_position_y, this.hyundai_logo_blue.width * 0.9, this.hyundai_logo_blue.height * 0.9);
            const desc_position_x = this.jsPDFOpt.margin.left + (this.hyundai_logo_blue.width * 0.9) + 5;
            const desc_position_y = logo_hyundai_position_y + 23;
            doc.setFontSize(9);
            doc.setTextColor(this.hyundai_colors.gray);
            doc.text(this.$t('QESG 평가 수행', this.report_lang), desc_position_x, desc_position_y);
            // (우측) 페이지 넘버
            doc.setTextColor('#333333');
            doc.setFontSize(10);
            const _footer_pages = doc.splitTextToSize(`${_page_current - 1} / ${_page_total}`, footerBox_width - this.jsPDFOpt.margin.right);
            const _footer_pages_height = doc.getTextDimensions(_footer_pages).h;
            const _footer_pages_width = doc.getTextDimensions(_footer_pages).w;
            const _footer_pages_position_x = footerBox_width - _footer_pages_width - (this.jsPDFOpt.margin.left);
            const _footer_pages_position_y = footerBox_position_y + (this.jsPDFOpt.footerHeight / 2) + (_footer_pages_height / 2);
            doc.text(_footer_pages, _footer_pages_position_x, _footer_pages_position_y);
        },
        
        drawScoreIndicator_hyundai(doc, x, y, score, opt = {}) {
            // 기본 옵션 설정
            if (typeof opt.label_position === 'undefined') {
                opt.label_position = 'top';
            }
            if (typeof opt.indicator_fill_color === 'undefined') {
                opt.indicator_fill_color = [0, 76, 153];
            }
            if (typeof opt.indicator_border_color === 'undefined') {
                opt.indicator_border_color = [0, 76, 153];
            }
            if (typeof opt.indicator_line_style === 'undefined') {
                opt.indicator_line_style = 'solid'; // 'solid', 'dashed_border_only'
            }
            if (typeof opt.label_font_size === 'undefined') {
                opt.label_font_size = 9.5;
            }
            if (typeof opt.size === 'undefined') {
                opt.size = 3; // 원의 반지름으로 사용될 크기
            }
            
            const radius = opt.size; // 원의 반지름
            
            doc.setLineWidth(1); // 원의 테두리 두께
            
            // 선 스타일 설정 (점선 또는 실선)
            if (opt.indicator_line_style === 'dashed_border_only') {
                doc.setLineDash([2, 1], 0); // 점선 설정
                doc.setFillColor(255, 255, 255, 0); // 투명 채움 색상
                doc.setDrawColor(...opt.indicator_border_color); // 테두리 색상
                // 채움 색상은 설정하지 않음 (투명)
                doc.circle(x, y, radius, 'S'); // 테두리만 있는 원 ('S' = Stroke)
            } else { // 'solid' 또는 기본값
                doc.setLineDash([], 0); // 실선 설정 (점선 해제)
                doc.setDrawColor(...opt.indicator_border_color); // 테두리 색상
                doc.setFillColor(...opt.indicator_fill_color); // 채움 색상
                doc.circle(x, y, radius, 'FD'); // 채우고 테두리 있는 원 ('FD' = Fill and Draw)
            }
            
            // 텍스트 (스코어) 출력
            const scoreStr = String(score); // score가 숫자일 수 있으므로 문자열로 변환
            const textDims = doc.getTextDimensions(scoreStr, {fontSize: opt.label_font_size});
            const textX = x - textDims.w / 2;
            let textY = y; // 기본 Y 위치
            
            
            if (opt.label_position === 'bottom') {
                textY = y + radius + 4 + (textDims.h / 2); // 원 아래에 위치, 텍스트 높이 고려
            } else if (opt.label_position === 'top') {
                textY = y - radius - 2; // 원 위에 위치
            }
            if (opt.label_y_position) {
                textY += opt.label_y_position;
            }
            doc.setFontSize(opt.label_font_size);
            doc.setTextColor(0); // 텍스트 색상 (검정)
            if(score === 'N/A'){
                doc.text(score, textX, textY);
            }
            else{
                doc.text(`${scoreStr}%`, textX, textY);
            }
            
            // 다음 도형에 영향을 주지 않도록 선 스타일 초기화
            doc.setLineDash([], 0);
            doc.setLineWidth(1); // 기본 선 두께로 복원 (필요하다면)
        },
    }
};
